import type {
    SpreadsheetCellValue,
    SpreadsheetChartArtifactType,
    SpreadsheetDerivedArtifact,
    SpreadsheetRangeAddress,
    SpreadsheetRangeCell,
} from '../protocol/spreadsheetEngineProtocol';
import { formatSelectionAddress, parseA1Selection } from './addressing';
import { colToLetter } from '../utils/columnUtils';

export const DERIVED_ARTIFACT_PREVIEW_MAX_ROWS = 64;
export const DERIVED_ARTIFACT_PREVIEW_MAX_COLS = 16;
export const DERIVED_ARTIFACT_PREVIEW_MAX_CHART_POINTS = 8;
export const DERIVED_ARTIFACT_PREVIEW_MAX_PIVOT_ROWS = 5;
export const DERIVED_ARTIFACT_PREVIEW_MAX_PIVOT_COLS = 4;
export const DERIVED_ARTIFACT_PREVIEW_MAX_SERIES_SUMMARIES = 3;
export const DERIVED_ARTIFACT_PREVIEW_MAX_PIE_LEGEND_ITEMS = 4;
export const DERIVED_ARTIFACT_PREVIEW_MAX_PIVOT_MEASURES = 3;
export const DERIVED_ARTIFACT_CHART_PREVIEW_COLORS = ['#107c41', '#2563eb', '#d97706', '#9333ea', '#dc2626', '#0891b2', '#65a30d', '#be185d'] as const;
export const DERIVED_ARTIFACT_CHART_PREVIEW_POSITIVE_COLOR = 'rgba(16, 124, 65, 0.90)';
export const DERIVED_ARTIFACT_CHART_PREVIEW_NEGATIVE_COLOR = 'rgba(220, 38, 38, 0.86)';
export const DERIVED_ARTIFACT_CHART_PREVIEW_LINE_COLOR = 'rgba(37, 99, 235, 0.82)';
export const DERIVED_ARTIFACT_CHART_PREVIEW_AREA_LINE_COLOR = 'rgba(16, 124, 65, 0.82)';
export const DERIVED_ARTIFACT_CHART_PREVIEW_AREA_FILL_COLOR = 'rgba(16, 124, 65, 0.16)';
export const DERIVED_ARTIFACT_PIE_REMAINDER_LEGEND_COLOR = 'rgba(148, 163, 184, 0.55)';
export const DERIVED_ARTIFACT_EMPTY_PIE_GRADIENT_STOP = 'rgba(148, 163, 184, 0.45) 0% 100%';
export const DERIVED_ARTIFACT_CHART_PREVIEW_SERIES_LINE_WIDTH = 2.4;
const DERIVED_ARTIFACT_CHART_STYLE_PALETTES = [
    ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47'],
    ['#5B9BD5', '#ED7D31', '#A5A5A5', '#FFC000', '#4472C4', '#70AD47'],
    ['#70AD47', '#4472C4', '#FFC000', '#ED7D31', '#A5A5A5', '#5B9BD5'],
    ['#264478', '#9E480E', '#636363', '#997300', '#255E91', '#43682B'],
    ['#A9D18E', '#9DC3E6', '#FFD966', '#F4B183', '#BFBFBF', '#C6E0B4'],
    ['#2F75B5', '#C55A11', '#7F7F7F', '#BF9000', '#548235', '#8064A2'],
] as const;

export type DerivedArtifactPreviewRange = {
    range: SpreadsheetRangeAddress;
    sourceAddress: string;
    previewAddress: string;
    sourceRowCount: number;
    sourceColCount: number;
    previewRowCount: number;
    previewColCount: number;
    truncatedRows: boolean;
    truncatedCols: boolean;
};

export type DerivedArtifactUiRange = {
    startCol: number;
    startRow: number;
    endCol: number;
    endRow: number;
};

export type DerivedArtifactMutationPlan =
    | {
        kind: 'upsert-derived-artifact';
        artifactKind: SpreadsheetDerivedArtifact['kind'];
        range: DerivedArtifactUiRange;
        options: {
            chartType?: SpreadsheetChartArtifactType;
            title: string;
            metadata?: Record<string, unknown>;
        };
    }
    | {
        kind: 'update-derived-artifact';
        artifact: SpreadsheetDerivedArtifact;
        options: { label: string };
    }
    | { kind: 'delete-derived-artifact'; artifactId: string }
    | { kind: 'noop' };

export type DerivedArtifactChartPreviewPoint = {
    label: string;
    value: number;
    xValue?: number;
};

export type DerivedArtifactChartPreviewSeries = {
    name: string;
    total: number;
    valueCount: number;
    componentChartType: SpreadsheetChartArtifactType;
    valueAxis: 'primary' | 'secondary';
    points: DerivedArtifactChartPreviewPoint[];
    plotPoints: DerivedArtifactChartPlotPoint[];
};

export type ExcelRangeChartMarkerShape = 'circle' | 'square' | 'diamond' | 'triangle' | 'x';
export type ExcelRangeChartLineDash = 'solid' | 'dash' | 'dot' | 'dashDot';
export type ExcelRangeChartFillPattern = 'percent' | 'horizontal' | 'vertical' | 'diagonalDown' | 'diagonalUp' | 'cross' | 'diagonalCross';
export type ExcelRangeChartGrouping = 'standard' | 'clustered' | 'stacked' | 'percentStacked';
export type ExcelRangeChartAxisCrossing = 'autoZero' | 'min' | 'max';
export type ExcelRangeChartAxisPosition = 'bottom' | 'top' | 'left' | 'right';
export type ExcelRangeChartTickLabelPosition = 'nextTo' | 'high' | 'low' | 'none';
export type ExcelRangeChartTickMark = 'none' | 'inside' | 'outside' | 'cross';
export type ExcelRangeChartDisplayUnit = 'hundreds' | 'thousands' | 'tenThousands' | 'hundredThousands' | 'millions' | 'tenMillions' | 'hundredMillions' | 'billions' | 'trillions' | 'custom';
export type ExcelRangeChartCategoryAxisKind = 'category' | 'date';
export type ExcelRangeChartDateAxisTimeUnit = 'days' | 'months' | 'years';
export type ExcelRangeChartDataLabelPosition = 'bestFit' | 'center' | 'insideBase' | 'insideEnd' | 'outsideEnd' | 'left' | 'right' | 'top' | 'bottom';
export type ExcelRangeChartTextAlign = 'left' | 'center' | 'right';
export type ExcelRangeChartTextVerticalAlign = 'top' | 'middle' | 'bottom';
export type ExcelRangeChartTextAutoFit = 'normal' | 'shape' | 'none';
export type ExcelRangeChartTextHorizontalOverflow = 'clip' | 'overflow';
export type ExcelRangeChartTextVerticalOverflow = 'clip' | 'ellipsis' | 'overflow';

export type DerivedArtifactChartShapeStyle = {
    background: string;
    borderColor: string;
    borderWidth: number;
    borderRadius: number;
};

export type DerivedArtifactChartTextStyle = {
    color: string;
    fontSize: number;
    fontWeight: number;
    fontStyle: 'normal' | 'italic';
    rotationDeg: number;
    textAlign: ExcelRangeChartTextAlign;
    textVerticalAlign: ExcelRangeChartTextVerticalAlign;
    textWrap: boolean;
    textAutoFit: ExcelRangeChartTextAutoFit | null;
    textAutoFitFontScale: number | null;
    textHorizontalOverflow: ExcelRangeChartTextHorizontalOverflow | null;
    textVerticalOverflow: ExcelRangeChartTextVerticalOverflow | null;
    textInsetTop: number;
    textInsetRight: number;
    textInsetBottom: number;
    textInsetLeft: number;
};

export type ExcelRangeChartSourceInference = {
    sourceKind: 'selected_range';
    rowCount: number;
    columnCount: number;
    seriesOrientation: 'columns' | 'rows';
    firstRowHeaders: boolean;
    firstColumnCategories: boolean;
};

export type ExcelRangeChartSettings = ExcelRangeChartSourceInference & {
    chartType: SpreadsheetChartArtifactType;
    chartStyleId: number | null;
    showLegend: boolean;
    legendPosition: 'top' | 'bottom' | 'left' | 'right';
    showDataLabels: boolean;
    showCategoryAxis: boolean;
    showValueAxis: boolean;
    chartTitleFontColor: string;
    chartTitleFontSize: number | null;
    chartTitleFontBold: boolean | null;
    chartTitleFontItalic: boolean | null;
    chartTitleTextRotation: number | null;
    chartTitleTextAlign: ExcelRangeChartTextAlign | null;
    chartTitleTextVerticalAlign: ExcelRangeChartTextVerticalAlign | null;
    chartTitleTextWrap: boolean | null;
    chartTitleTextAutoFit: ExcelRangeChartTextAutoFit | null;
    chartTitleTextAutoFitFontScale: number | null;
    chartTitleTextHorizontalOverflow: ExcelRangeChartTextHorizontalOverflow | null;
    chartTitleTextVerticalOverflow: ExcelRangeChartTextVerticalOverflow | null;
    chartTitleTextInsetTop: number | null;
    chartTitleTextInsetRight: number | null;
    chartTitleTextInsetBottom: number | null;
    chartTitleTextInsetLeft: number | null;
    dataLabelPosition: ExcelRangeChartDataLabelPosition | null;
    dataLabelNumberFormat: string | null;
    dataLabelSeparator: string;
    dataLabelFontColor: string;
    dataLabelFontSize: number | null;
    dataLabelFontBold: boolean | null;
    dataLabelFontItalic: boolean | null;
    dataLabelTextRotation: number | null;
    dataLabelTextAlign: ExcelRangeChartTextAlign | null;
    dataLabelTextVerticalAlign: ExcelRangeChartTextVerticalAlign | null;
    dataLabelTextWrap: boolean | null;
    dataLabelTextAutoFit: ExcelRangeChartTextAutoFit | null;
    dataLabelTextAutoFitFontScale: number | null;
    dataLabelTextHorizontalOverflow: ExcelRangeChartTextHorizontalOverflow | null;
    dataLabelTextVerticalOverflow: ExcelRangeChartTextVerticalOverflow | null;
    dataLabelTextInsetTop: number | null;
    dataLabelTextInsetRight: number | null;
    dataLabelTextInsetBottom: number | null;
    dataLabelTextInsetLeft: number | null;
    dataLabelShowValue: boolean | null;
    dataLabelShowCategoryName: boolean | null;
    dataLabelShowSeriesName: boolean | null;
    dataLabelShowPercent: boolean | null;
    dataLabelShowLeaderLines: boolean | null;
    seriesDataLabelPositions: (ExcelRangeChartDataLabelPosition | null)[];
    seriesDataLabelNumberFormats: string[];
    seriesDataLabelSeparators: string[];
    seriesDataLabelFontColors: string[];
    seriesDataLabelFontSizes: number[];
    seriesDataLabelFontBolds: (boolean | null)[];
    seriesDataLabelFontItalics: (boolean | null)[];
    seriesDataLabelTextRotations: number[];
    seriesDataLabelTextAligns: (ExcelRangeChartTextAlign | null)[];
    seriesDataLabelTextVerticalAligns: (ExcelRangeChartTextVerticalAlign | null)[];
    seriesDataLabelTextWraps: (boolean | null)[];
    seriesDataLabelTextAutoFits: (ExcelRangeChartTextAutoFit | null)[];
    seriesDataLabelTextAutoFitFontScales: number[];
    seriesDataLabelTextHorizontalOverflows: (ExcelRangeChartTextHorizontalOverflow | null)[];
    seriesDataLabelTextVerticalOverflows: (ExcelRangeChartTextVerticalOverflow | null)[];
    seriesDataLabelTextInsetTops: number[];
    seriesDataLabelTextInsetRights: number[];
    seriesDataLabelTextInsetBottoms: number[];
    seriesDataLabelTextInsetLefts: number[];
    seriesDataLabelShowValues: (boolean | null)[];
    seriesDataLabelShowCategoryNames: (boolean | null)[];
    seriesDataLabelShowSeriesNames: (boolean | null)[];
    seriesDataLabelShowPercents: (boolean | null)[];
    seriesDataLabelShowLeaderLines: (boolean | null)[];
    categoryAxisKind: ExcelRangeChartCategoryAxisKind | null;
    categoryAxisPosition: ExcelRangeChartAxisPosition | null;
    valueAxisPosition: ExcelRangeChartAxisPosition | null;
    categoryAxisTitle: string;
    valueAxisTitle: string;
    categoryAxisTitleFontColor: string;
    categoryAxisTitleFontSize: number | null;
    categoryAxisTitleFontBold: boolean | null;
    categoryAxisTitleFontItalic: boolean | null;
    categoryAxisTitleTextRotation: number | null;
    categoryAxisTitleTextAlign: ExcelRangeChartTextAlign | null;
    categoryAxisTitleTextVerticalAlign: ExcelRangeChartTextVerticalAlign | null;
    categoryAxisTitleTextWrap: boolean | null;
    categoryAxisTitleTextAutoFit: ExcelRangeChartTextAutoFit | null;
    categoryAxisTitleTextAutoFitFontScale: number | null;
    categoryAxisTitleTextHorizontalOverflow: ExcelRangeChartTextHorizontalOverflow | null;
    categoryAxisTitleTextVerticalOverflow: ExcelRangeChartTextVerticalOverflow | null;
    categoryAxisTitleTextInsetTop: number | null;
    categoryAxisTitleTextInsetRight: number | null;
    categoryAxisTitleTextInsetBottom: number | null;
    categoryAxisTitleTextInsetLeft: number | null;
    valueAxisTitleFontColor: string;
    valueAxisTitleFontSize: number | null;
    valueAxisTitleFontBold: boolean | null;
    valueAxisTitleFontItalic: boolean | null;
    valueAxisTitleTextRotation: number | null;
    valueAxisTitleTextAlign: ExcelRangeChartTextAlign | null;
    valueAxisTitleTextVerticalAlign: ExcelRangeChartTextVerticalAlign | null;
    valueAxisTitleTextWrap: boolean | null;
    valueAxisTitleTextAutoFit: ExcelRangeChartTextAutoFit | null;
    valueAxisTitleTextAutoFitFontScale: number | null;
    valueAxisTitleTextHorizontalOverflow: ExcelRangeChartTextHorizontalOverflow | null;
    valueAxisTitleTextVerticalOverflow: ExcelRangeChartTextVerticalOverflow | null;
    valueAxisTitleTextInsetTop: number | null;
    valueAxisTitleTextInsetRight: number | null;
    valueAxisTitleTextInsetBottom: number | null;
    valueAxisTitleTextInsetLeft: number | null;
    categoryAxisCrosses: ExcelRangeChartAxisCrossing | null;
    categoryAxisCrossesAt: number | null;
    categoryAxisTickLabelPosition: ExcelRangeChartTickLabelPosition | null;
    valueAxisTickLabelPosition: ExcelRangeChartTickLabelPosition | null;
    categoryAxisMajorTickMark: ExcelRangeChartTickMark | null;
    categoryAxisMinorTickMark: ExcelRangeChartTickMark | null;
    valueAxisMajorTickMark: ExcelRangeChartTickMark | null;
    valueAxisMinorTickMark: ExcelRangeChartTickMark | null;
    categoryAxisHasMajorGridlines: boolean;
    categoryAxisHasMinorGridlines: boolean;
    valueAxisHasMajorGridlines: boolean;
    valueAxisHasMinorGridlines: boolean;
    categoryAxisDeleted: boolean | null;
    valueAxisDeleted: boolean | null;
    categoryAxisBaseTimeUnit: ExcelRangeChartDateAxisTimeUnit | null;
    categoryAxisMajorTimeUnit: ExcelRangeChartDateAxisTimeUnit | null;
    categoryAxisMinorTimeUnit: ExcelRangeChartDateAxisTimeUnit | null;
    categoryAxisMajorUnit: number | null;
    categoryAxisMinorUnit: number | null;
    categoryAxisNumberFormat: string | null;
    categoryAxisNumberFormatLinkedToSource: boolean | null;
    valueAxisNumberFormat: string | null;
    valueAxisNumberFormatLinkedToSource: boolean | null;
    valueAxisCount: number | null;
    hasSecondaryValueAxis: boolean;
    secondaryValueAxisId: string;
    secondaryValueAxisPosition: ExcelRangeChartAxisPosition | null;
    secondaryValueAxisTitle: string;
    secondaryValueAxisTitleFontColor: string;
    secondaryValueAxisTitleFontSize: number | null;
    secondaryValueAxisTitleFontBold: boolean | null;
    secondaryValueAxisTitleFontItalic: boolean | null;
    secondaryValueAxisTitleTextRotation: number | null;
    secondaryValueAxisTitleTextAlign: ExcelRangeChartTextAlign | null;
    secondaryValueAxisTitleTextVerticalAlign: ExcelRangeChartTextVerticalAlign | null;
    secondaryValueAxisTitleTextWrap: boolean | null;
    secondaryValueAxisTitleTextAutoFit: ExcelRangeChartTextAutoFit | null;
    secondaryValueAxisTitleTextAutoFitFontScale: number | null;
    secondaryValueAxisTitleTextHorizontalOverflow: ExcelRangeChartTextHorizontalOverflow | null;
    secondaryValueAxisTitleTextVerticalOverflow: ExcelRangeChartTextVerticalOverflow | null;
    secondaryValueAxisTitleTextInsetTop: number | null;
    secondaryValueAxisTitleTextInsetRight: number | null;
    secondaryValueAxisTitleTextInsetBottom: number | null;
    secondaryValueAxisTitleTextInsetLeft: number | null;
    secondaryValueAxisMin: number | null;
    secondaryValueAxisMax: number | null;
    secondaryValueAxisLogBase: number | null;
    secondaryValueAxisDisplayUnit: ExcelRangeChartDisplayUnit | null;
    secondaryValueAxisDisplayUnitCustom: number | null;
    secondaryValueAxisCrosses: ExcelRangeChartAxisCrossing | null;
    secondaryValueAxisCrossesAt: number | null;
    secondaryValueAxisTickLabelPosition: ExcelRangeChartTickLabelPosition | null;
    secondaryValueAxisMajorTickMark: ExcelRangeChartTickMark | null;
    secondaryValueAxisMinorTickMark: ExcelRangeChartTickMark | null;
    secondaryValueAxisHasMajorGridlines: boolean;
    secondaryValueAxisHasMinorGridlines: boolean;
    secondaryValueAxisDeleted: boolean | null;
    secondaryValueAxisNumberFormat: string | null;
    secondaryValueAxisNumberFormatLinkedToSource: boolean | null;
    seriesValueAxisIds: string[];
    seriesChartTypes: SpreadsheetChartArtifactType[];
    seriesColors: string[];
    seriesLineColors: string[];
    seriesLineWidths: number[];
    seriesMarkerSizes: number[];
    seriesMarkerShapes: ExcelRangeChartMarkerShape[];
    seriesMarkerFillColors: string[];
    seriesMarkerLineColors: string[];
    seriesLineDashes: ExcelRangeChartLineDash[];
    seriesFillOpacities: number[];
    seriesFillPatterns: ExcelRangeChartFillPattern[];
    seriesFillForegroundColors: string[];
    seriesFillBackgroundColors: string[];
    seriesExplosions: number[];
    seriesPointIndexes: number[][];
    seriesPointColors: string[][];
    seriesPointLineColors: string[][];
    seriesPointFillOpacities: number[][];
    seriesPointFillPatterns: ExcelRangeChartFillPattern[][];
    seriesPointFillForegroundColors: string[][];
    seriesPointFillBackgroundColors: string[][];
    seriesPointExplosions: number[][];
    pieFirstSliceAngle: number | null;
    doughnutHoleSize: number | null;
    valueAxisMin: number | null;
    valueAxisMax: number | null;
    valueAxisLogBase: number | null;
    valueAxisDisplayUnit: ExcelRangeChartDisplayUnit | null;
    valueAxisDisplayUnitCustom: number | null;
    valueAxisCrosses: ExcelRangeChartAxisCrossing | null;
    valueAxisCrossesAt: number | null;
    chartGrouping: ExcelRangeChartGrouping | null;
    chartVaryColors: boolean | null;
    lineSmooth: boolean | null;
    barGapWidth: number | null;
    barOverlap: number | null;
    chartRoundedCorners: boolean | null;
    chartAreaFillColor: string;
    chartAreaFillOpacity: number | null;
    chartAreaLineColor: string;
    chartAreaLineWidth: number | null;
    chartAreaFillPattern: ExcelRangeChartFillPattern | null;
    chartAreaFillForegroundColor: string;
    chartAreaFillBackgroundColor: string;
    plotAreaFillColor: string;
    plotAreaFillOpacity: number | null;
    plotAreaLineColor: string;
    plotAreaLineWidth: number | null;
    plotAreaFillPattern: ExcelRangeChartFillPattern | null;
    plotAreaFillForegroundColor: string;
    plotAreaFillBackgroundColor: string;
};

export type ExcelRangeChartMetadataUpdate =
    | { key: 'showLegend' | 'showDataLabels' | 'showCategoryAxis' | 'showValueAxis'; value?: boolean }
    | { key: 'legendPosition'; value: 'top' | 'bottom' | 'left' | 'right' }
    | { key: 'seriesOrientation'; value: 'columns' | 'rows' }
    | { key: 'firstRowHeaders' | 'firstColumnCategories'; value?: boolean }
    | { key: 'categoryAxisTitle' | 'valueAxisTitle'; value?: string };

type ExcelRangeChartSeriesStyleSettings = Pick<ExcelRangeChartSettings,
    'seriesLineColors'
    | 'seriesLineWidths'
    | 'seriesMarkerSizes'
    | 'seriesMarkerShapes'
    | 'seriesMarkerFillColors'
    | 'seriesMarkerLineColors'
    | 'seriesLineDashes'
    | 'seriesFillOpacities'
    | 'seriesFillPatterns'
    | 'seriesFillForegroundColors'
    | 'seriesFillBackgroundColors'
    | 'seriesExplosions'
    | 'seriesPointIndexes'
    | 'seriesPointColors'
    | 'seriesPointLineColors'
    | 'seriesPointFillOpacities'
    | 'seriesPointFillPatterns'
    | 'seriesPointFillForegroundColors'
    | 'seriesPointFillBackgroundColors'
    | 'seriesPointExplosions'
    | 'pieFirstSliceAngle'
    | 'doughnutHoleSize'
>;

export const EXCEL_RANGE_CHART_TYPE_LABELS: Record<SpreadsheetChartArtifactType, string> = {
    column: 'Column',
    bar: 'Bar',
    line: 'Line',
    area: 'Area',
    pie: 'Pie',
    scatter: 'Scatter',
};

const chartTypeFromUnknown = (value: unknown): SpreadsheetChartArtifactType | null => {
    const chartType = String(value ?? '').trim();
    return chartType in EXCEL_RANGE_CHART_TYPE_LABELS ? chartType as SpreadsheetChartArtifactType : null;
};

export const inferExcelRangeChartSource = (
    range: DerivedArtifactUiRange | null | undefined,
): ExcelRangeChartSourceInference | null => {
    if (!range) return null;
    const startRow = Math.min(range.startRow, range.endRow);
    const endRow = Math.max(range.startRow, range.endRow);
    const startCol = Math.min(range.startCol, range.endCol);
    const endCol = Math.max(range.startCol, range.endCol);
    const rowCount = Math.max(1, endRow - startRow + 1);
    const columnCount = Math.max(1, endCol - startCol + 1);
    const canHaveHeaderRow = rowCount > 1;
    const canHaveCategoryColumn = columnCount > 1;
    return {
        sourceKind: 'selected_range',
        rowCount,
        columnCount,
        seriesOrientation: rowCount <= 2 && columnCount > rowCount ? 'rows' : 'columns',
        firstRowHeaders: canHaveHeaderRow,
        firstColumnCategories: canHaveHeaderRow && canHaveCategoryColumn,
    };
};

export const buildExcelRangeChartTitle = (
    chartType: SpreadsheetChartArtifactType | null | undefined,
): string => `${EXCEL_RANGE_CHART_TYPE_LABELS[chartType ?? 'column']} Chart`;

const isDerivedArtifactRecord = (value: unknown): value is Record<string, unknown> => (
    Boolean(value && typeof value === 'object' && !Array.isArray(value))
);

const rangeAddressToUiRange = (range: SpreadsheetRangeAddress): DerivedArtifactUiRange => ({
    startCol: range.start_col,
    startRow: range.start_row,
    endCol: range.end_col,
    endRow: range.end_row,
});

const boolFromRecord = (
    record: Record<string, unknown>,
    key: string,
    defaultValue: boolean,
): boolean => (
    typeof record[key] === 'boolean' ? Boolean(record[key]) : defaultValue
);

const boolMetadataFromRecord = (
    record: Record<string, unknown>,
    key: string,
): boolean => (
    record[key] === true
);

const nullableBoolFromRecord = (
    record: Record<string, unknown>,
    key: string,
): boolean | null => (
    typeof record[key] === 'boolean' ? Boolean(record[key]) : null
);

const stringFromRecord = (
    record: Record<string, unknown>,
    key: string,
): string => (
    typeof record[key] === 'string' ? record[key].trim() : ''
);

const chartColorFromUnknown = (value: unknown): string | null => {
    if (typeof value !== 'string') return null;
    const color = value.trim();
    if (!/^#[0-9A-Fa-f]{6}$/.test(color)) return null;
    return color.toUpperCase();
};

const stringArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): string[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map(chartColorFromUnknown)
        .map((color) => color ?? '')
        .filter((color, index, colors) => color || colors.slice(index + 1).some(Boolean));
};

const trimmedStringArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): string[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
        .filter((entry, index, entries) => entry || entries.slice(index + 1).some(Boolean));
};

const nullableBoolArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): (boolean | null)[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map((entry) => (typeof entry === 'boolean' ? Boolean(entry) : null))
        .filter((entry, index, entries) => entry != null || entries.slice(index + 1).some((nextEntry) => nextEntry != null));
};

const nullableStringEnumArrayFromRecord = <T extends string>(
    record: Record<string, unknown>,
    key: string,
    normalize: (value: unknown) => T | null,
): (T | null)[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map(normalize)
        .filter((entry, index, entries) => entry != null || entries.slice(index + 1).some((nextEntry) => nextEntry != null));
};

const chartNumberFromUnknown = (value: unknown): number | null => {
    const number = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(number) || number <= 0) return null;
    return Math.min(16, Math.max(0.5, number));
};

const numberArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map(chartNumberFromUnknown)
        .map((number) => number ?? 0)
        .filter((number, index, numbers) => number > 0 || numbers.slice(index + 1).some(Boolean));
};

const chartExplosionFromUnknown = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const number = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(number) || number < 0) return null;
    return Math.min(400, Math.max(0, number));
};

const explosionArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map(chartExplosionFromUnknown)
        .map((number) => number ?? -1)
        .filter((number, index, numbers) => number >= 0 || numbers.slice(index + 1).some((entry) => entry >= 0));
};

const nestedNumberArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number[][] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value.map((entry) => (
        Array.isArray(entry)
            ? entry.map((item) => {
                const number = Number(item);
                return Number.isInteger(number) && number >= 0 ? number : -1;
            })
            : []
    ));
};

const nestedExplosionArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number[][] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value.map((entry) => (
        Array.isArray(entry)
            ? entry.map(chartExplosionFromUnknown).map((number) => number ?? -1)
            : []
    ));
};

const chartOpacityFromUnknown = (value: unknown): number | null => {
    if (value == null || value === '') return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const number = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(number) || number < 0) return null;
    return Math.min(1, Math.max(0, number));
};

const finiteNumberFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number | null => {
    const value = record[key];
    if (value == null || value === '') return null;
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(number) ? number : null;
};

const chartStyleIdFromRecord = (
    record: Record<string, unknown>,
): number | null => {
    const value = finiteNumberFromRecord(record, 'chartStyleId');
    if (value == null) return null;
    const styleId = Math.floor(value);
    return styleId > 0 ? Math.min(48, styleId) : null;
};

const chartLogBaseFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number | null => {
    const number = finiteNumberFromRecord(record, key);
    if (number == null || number <= 1) return null;
    return Math.min(1000, Math.max(2, number));
};

const opacityArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map(chartOpacityFromUnknown)
        .map((number) => number ?? -1)
        .filter((number, index, numbers) => number >= 0 || numbers.slice(index + 1).some((entry) => entry >= 0));
};

const nestedOpacityArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): number[][] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value.map((entry) => (
        Array.isArray(entry)
            ? entry.map(chartOpacityFromUnknown).map((number) => number ?? -1)
            : []
    ));
};

const nestedStringArrayFromRecord = (
    record: Record<string, unknown>,
    key: string,
): string[][] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value.map((entry) => (
        Array.isArray(entry)
            ? entry.map(chartColorFromUnknown).map((color) => color ?? '')
            : []
    ));
};

const nestedStringEnumArrayFromRecord = <T extends string>(
    record: Record<string, unknown>,
    key: string,
    normalize: (value: unknown) => T | null,
): T[][] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value.map((entry) => (
        Array.isArray(entry)
            ? entry.map(normalize).map((item) => item ?? '').filter((item, index, items) => item || items.slice(index + 1).some(Boolean)) as T[]
            : []
    ));
};

const chartMarkerShapeFromUnknown = (value: unknown): ExcelRangeChartMarkerShape | null => {
    const shape = String(value ?? '').trim();
    return shape === 'square' || shape === 'diamond' || shape === 'triangle' || shape === 'x' ? shape : shape === 'circle' ? 'circle' : null;
};

const chartLineDashFromUnknown = (value: unknown): ExcelRangeChartLineDash | null => {
    const dash = String(value ?? '').trim();
    return dash === 'dash' || dash === 'dot' || dash === 'dashDot' ? dash : dash === 'solid' ? 'solid' : null;
};

const chartFillPatternFromUnknown = (value: unknown): ExcelRangeChartFillPattern | null => {
    const pattern = String(value ?? '').trim();
    switch (pattern) {
        case 'percent':
        case 'horizontal':
        case 'vertical':
        case 'diagonalDown':
        case 'diagonalUp':
        case 'cross':
        case 'diagonalCross':
            return pattern;
        default:
            return null;
    }
};

const chartAxisCrossingFromUnknown = (value: unknown): ExcelRangeChartAxisCrossing | null => {
    const crossing = String(value ?? '').trim();
    switch (crossing) {
        case 'autoZero':
        case 'min':
        case 'max':
            return crossing;
        default:
            return null;
    }
};

const chartAxisPositionFromUnknown = (value: unknown): ExcelRangeChartAxisPosition | null => {
    const position = String(value ?? '').trim();
    switch (position) {
        case 'bottom':
        case 'top':
        case 'left':
        case 'right':
            return position;
        default:
            return null;
    }
};

const chartTickLabelPositionFromUnknown = (value: unknown): ExcelRangeChartTickLabelPosition | null => {
    const position = String(value ?? '').trim();
    switch (position) {
        case 'nextTo':
        case 'high':
        case 'low':
        case 'none':
            return position;
        default:
            return null;
    }
};

const chartTickMarkFromUnknown = (value: unknown): ExcelRangeChartTickMark | null => {
    const mark = String(value ?? '').trim();
    switch (mark) {
        case 'none':
        case 'inside':
        case 'outside':
        case 'cross':
            return mark;
        default:
            return null;
    }
};

const chartDisplayUnitFromUnknown = (value: unknown): ExcelRangeChartDisplayUnit | null => {
    const unit = String(value ?? '').trim();
    switch (unit) {
        case 'hundreds':
        case 'thousands':
        case 'tenThousands':
        case 'hundredThousands':
        case 'millions':
        case 'tenMillions':
        case 'hundredMillions':
        case 'billions':
        case 'trillions':
        case 'custom':
            return unit;
        default:
            return null;
    }
};

const chartCategoryAxisKindFromUnknown = (value: unknown): ExcelRangeChartCategoryAxisKind | null => {
    const kind = String(value ?? '').trim();
    return kind === 'category' || kind === 'date' ? kind : null;
};

const chartDateAxisTimeUnitFromUnknown = (value: unknown): ExcelRangeChartDateAxisTimeUnit | null => {
    const unit = String(value ?? '').trim();
    switch (unit) {
        case 'days':
        case 'months':
        case 'years':
            return unit;
        default:
            return null;
    }
};

const chartGroupingFromUnknown = (value: unknown): ExcelRangeChartGrouping | null => {
    const grouping = String(value ?? '').trim();
    switch (grouping) {
        case 'standard':
        case 'clustered':
        case 'stacked':
        case 'percentStacked':
            return grouping;
        default:
            return null;
    }
};

const chartDataLabelPositionFromUnknown = (value: unknown): ExcelRangeChartDataLabelPosition | null => {
    const position = String(value ?? '').trim();
    switch (position) {
        case 'bestFit':
        case 'center':
        case 'insideBase':
        case 'insideEnd':
        case 'outsideEnd':
        case 'left':
        case 'right':
        case 'top':
        case 'bottom':
            return position;
        default:
            return null;
    }
};

const chartTextAlignFromUnknown = (value: unknown): ExcelRangeChartTextAlign | null => {
    const align = String(value ?? '').trim();
    return align === 'left' || align === 'center' || align === 'right' ? align : null;
};

const chartTextVerticalAlignFromUnknown = (value: unknown): ExcelRangeChartTextVerticalAlign | null => {
    const align = String(value ?? '').trim();
    return align === 'top' || align === 'middle' || align === 'bottom' ? align : null;
};

const chartTextAutoFitFromUnknown = (value: unknown): ExcelRangeChartTextAutoFit | null => {
    const autoFit = String(value ?? '').trim();
    return autoFit === 'normal' || autoFit === 'shape' || autoFit === 'none' ? autoFit : null;
};

const chartTextHorizontalOverflowFromUnknown = (value: unknown): ExcelRangeChartTextHorizontalOverflow | null => {
    const overflow = String(value ?? '').trim();
    return overflow === 'clip' || overflow === 'overflow' ? overflow : null;
};

const chartTextVerticalOverflowFromUnknown = (value: unknown): ExcelRangeChartTextVerticalOverflow | null => {
    const overflow = String(value ?? '').trim();
    return overflow === 'clip' || overflow === 'ellipsis' || overflow === 'overflow' ? overflow : null;
};

const stringEnumArrayFromRecord = <T extends string>(
    record: Record<string, unknown>,
    key: string,
    normalize: (value: unknown) => T | null,
): T[] => {
    const value = record[key];
    if (!Array.isArray(value)) return [];
    return value
        .map(normalize)
        .map((entry) => entry ?? '')
        .filter((entry, index, entries) => entry || entries.slice(index + 1).some(Boolean)) as T[];
};

const legendPositionFromRecord = (
    record: Record<string, unknown>,
): ExcelRangeChartSettings['legendPosition'] => {
    const value = String(record.legendPosition ?? '').trim();
    return value === 'bottom' || value === 'left' || value === 'right' ? value : 'top';
};

const excelRangeChartRecordFromArtifact = (
    artifact: SpreadsheetDerivedArtifact | null | undefined,
): Record<string, unknown> => {
    const metadata = artifact?.metadata;
    if (!isDerivedArtifactRecord(metadata)) return {};
    const chartRecord = metadata.excel_range_chart;
    return isDerivedArtifactRecord(chartRecord) ? chartRecord : {};
};

export const buildExcelRangeChartSettings = (args: {
    artifact?: SpreadsheetDerivedArtifact | null;
    range?: DerivedArtifactUiRange | null;
    chartType?: SpreadsheetChartArtifactType | null;
    overrides?: ExcelRangeChartMetadataUpdate[] | null;
}): ExcelRangeChartSettings | null => {
    const artifactRecord = excelRangeChartRecordFromArtifact(args.artifact);
    const source = inferExcelRangeChartSource(args.range ?? (args.artifact?.source_range ? rangeAddressToUiRange(args.artifact.source_range) : null));
    if (!source) return null;
    const chartType = args.chartType ?? args.artifact?.chart_type ?? (
        typeof artifactRecord.chartType === 'string' && artifactRecord.chartType in EXCEL_RANGE_CHART_TYPE_LABELS
            ? artifactRecord.chartType as SpreadsheetChartArtifactType
            : 'column'
    );
    const categoryAxisDeleted = nullableBoolFromRecord(artifactRecord, 'categoryAxisDeleted');
    const valueAxisDeleted = nullableBoolFromRecord(artifactRecord, 'valueAxisDeleted');
    const base: ExcelRangeChartSettings = {
        ...source,
        seriesOrientation: artifactRecord.seriesOrientation === 'rows' || artifactRecord.seriesOrientation === 'columns'
            ? artifactRecord.seriesOrientation
            : source.seriesOrientation,
        firstRowHeaders: boolFromRecord(artifactRecord, 'firstRowHeaders', source.firstRowHeaders),
        firstColumnCategories: boolFromRecord(artifactRecord, 'firstColumnCategories', source.firstColumnCategories),
        chartType,
        chartStyleId: chartStyleIdFromRecord(artifactRecord),
        showLegend: boolFromRecord(artifactRecord, 'showLegend', true),
        legendPosition: legendPositionFromRecord(artifactRecord),
        showDataLabels: boolFromRecord(artifactRecord, 'showDataLabels', false),
        showCategoryAxis: boolFromRecord(artifactRecord, 'showCategoryAxis', categoryAxisDeleted !== true),
        showValueAxis: boolFromRecord(artifactRecord, 'showValueAxis', valueAxisDeleted !== true),
        chartTitleFontColor: stringFromRecord(artifactRecord, 'chartTitleFontColor'),
        chartTitleFontSize: finiteNumberFromRecord(artifactRecord, 'chartTitleFontSize'),
        chartTitleFontBold: nullableBoolFromRecord(artifactRecord, 'chartTitleFontBold'),
        chartTitleFontItalic: nullableBoolFromRecord(artifactRecord, 'chartTitleFontItalic'),
        chartTitleTextRotation: finiteNumberFromRecord(artifactRecord, 'chartTitleTextRotation'),
        chartTitleTextAlign: chartTextAlignFromUnknown(artifactRecord.chartTitleTextAlign),
        chartTitleTextVerticalAlign: chartTextVerticalAlignFromUnknown(artifactRecord.chartTitleTextVerticalAlign),
        chartTitleTextWrap: nullableBoolFromRecord(artifactRecord, 'chartTitleTextWrap'),
        chartTitleTextAutoFit: chartTextAutoFitFromUnknown(artifactRecord.chartTitleTextAutoFit),
        chartTitleTextAutoFitFontScale: finiteNumberFromRecord(artifactRecord, 'chartTitleTextAutoFitFontScale'),
        chartTitleTextHorizontalOverflow: chartTextHorizontalOverflowFromUnknown(artifactRecord.chartTitleTextHorizontalOverflow),
        chartTitleTextVerticalOverflow: chartTextVerticalOverflowFromUnknown(artifactRecord.chartTitleTextVerticalOverflow),
        chartTitleTextInsetTop: finiteNumberFromRecord(artifactRecord, 'chartTitleTextInsetTop'),
        chartTitleTextInsetRight: finiteNumberFromRecord(artifactRecord, 'chartTitleTextInsetRight'),
        chartTitleTextInsetBottom: finiteNumberFromRecord(artifactRecord, 'chartTitleTextInsetBottom'),
        chartTitleTextInsetLeft: finiteNumberFromRecord(artifactRecord, 'chartTitleTextInsetLeft'),
        dataLabelPosition: chartDataLabelPositionFromUnknown(artifactRecord.dataLabelPosition),
        dataLabelNumberFormat: stringFromRecord(artifactRecord, 'dataLabelNumberFormat') || null,
        dataLabelSeparator: stringFromRecord(artifactRecord, 'dataLabelSeparator') || ', ',
        dataLabelFontColor: stringFromRecord(artifactRecord, 'dataLabelFontColor'),
        dataLabelFontSize: finiteNumberFromRecord(artifactRecord, 'dataLabelFontSize'),
        dataLabelFontBold: nullableBoolFromRecord(artifactRecord, 'dataLabelFontBold'),
        dataLabelFontItalic: nullableBoolFromRecord(artifactRecord, 'dataLabelFontItalic'),
        dataLabelTextRotation: finiteNumberFromRecord(artifactRecord, 'dataLabelTextRotation'),
        dataLabelTextAlign: chartTextAlignFromUnknown(artifactRecord.dataLabelTextAlign),
        dataLabelTextVerticalAlign: chartTextVerticalAlignFromUnknown(artifactRecord.dataLabelTextVerticalAlign),
        dataLabelTextWrap: nullableBoolFromRecord(artifactRecord, 'dataLabelTextWrap'),
        dataLabelTextAutoFit: chartTextAutoFitFromUnknown(artifactRecord.dataLabelTextAutoFit),
        dataLabelTextAutoFitFontScale: finiteNumberFromRecord(artifactRecord, 'dataLabelTextAutoFitFontScale'),
        dataLabelTextHorizontalOverflow: chartTextHorizontalOverflowFromUnknown(artifactRecord.dataLabelTextHorizontalOverflow),
        dataLabelTextVerticalOverflow: chartTextVerticalOverflowFromUnknown(artifactRecord.dataLabelTextVerticalOverflow),
        dataLabelTextInsetTop: finiteNumberFromRecord(artifactRecord, 'dataLabelTextInsetTop'),
        dataLabelTextInsetRight: finiteNumberFromRecord(artifactRecord, 'dataLabelTextInsetRight'),
        dataLabelTextInsetBottom: finiteNumberFromRecord(artifactRecord, 'dataLabelTextInsetBottom'),
        dataLabelTextInsetLeft: finiteNumberFromRecord(artifactRecord, 'dataLabelTextInsetLeft'),
        dataLabelShowValue: nullableBoolFromRecord(artifactRecord, 'dataLabelShowValue'),
        dataLabelShowCategoryName: nullableBoolFromRecord(artifactRecord, 'dataLabelShowCategoryName'),
        dataLabelShowSeriesName: nullableBoolFromRecord(artifactRecord, 'dataLabelShowSeriesName'),
        dataLabelShowPercent: nullableBoolFromRecord(artifactRecord, 'dataLabelShowPercent'),
        dataLabelShowLeaderLines: nullableBoolFromRecord(artifactRecord, 'dataLabelShowLeaderLines'),
        seriesDataLabelPositions: nullableStringEnumArrayFromRecord(artifactRecord, 'seriesDataLabelPositions', chartDataLabelPositionFromUnknown),
        seriesDataLabelNumberFormats: trimmedStringArrayFromRecord(artifactRecord, 'seriesDataLabelNumberFormats'),
        seriesDataLabelSeparators: trimmedStringArrayFromRecord(artifactRecord, 'seriesDataLabelSeparators'),
        seriesDataLabelFontColors: stringArrayFromRecord(artifactRecord, 'seriesDataLabelFontColors'),
        seriesDataLabelFontSizes: numberArrayFromRecord(artifactRecord, 'seriesDataLabelFontSizes'),
        seriesDataLabelFontBolds: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelFontBolds'),
        seriesDataLabelFontItalics: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelFontItalics'),
        seriesDataLabelTextRotations: numberArrayFromRecord(artifactRecord, 'seriesDataLabelTextRotations'),
        seriesDataLabelTextAligns: nullableStringEnumArrayFromRecord(artifactRecord, 'seriesDataLabelTextAligns', chartTextAlignFromUnknown),
        seriesDataLabelTextVerticalAligns: nullableStringEnumArrayFromRecord(artifactRecord, 'seriesDataLabelTextVerticalAligns', chartTextVerticalAlignFromUnknown),
        seriesDataLabelTextWraps: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelTextWraps'),
        seriesDataLabelTextAutoFits: nullableStringEnumArrayFromRecord(artifactRecord, 'seriesDataLabelTextAutoFits', chartTextAutoFitFromUnknown),
        seriesDataLabelTextAutoFitFontScales: numberArrayFromRecord(artifactRecord, 'seriesDataLabelTextAutoFitFontScales'),
        seriesDataLabelTextHorizontalOverflows: nullableStringEnumArrayFromRecord(artifactRecord, 'seriesDataLabelTextHorizontalOverflows', chartTextHorizontalOverflowFromUnknown),
        seriesDataLabelTextVerticalOverflows: nullableStringEnumArrayFromRecord(artifactRecord, 'seriesDataLabelTextVerticalOverflows', chartTextVerticalOverflowFromUnknown),
        seriesDataLabelTextInsetTops: numberArrayFromRecord(artifactRecord, 'seriesDataLabelTextInsetTops'),
        seriesDataLabelTextInsetRights: numberArrayFromRecord(artifactRecord, 'seriesDataLabelTextInsetRights'),
        seriesDataLabelTextInsetBottoms: numberArrayFromRecord(artifactRecord, 'seriesDataLabelTextInsetBottoms'),
        seriesDataLabelTextInsetLefts: numberArrayFromRecord(artifactRecord, 'seriesDataLabelTextInsetLefts'),
        seriesDataLabelShowValues: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelShowValues'),
        seriesDataLabelShowCategoryNames: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelShowCategoryNames'),
        seriesDataLabelShowSeriesNames: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelShowSeriesNames'),
        seriesDataLabelShowPercents: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelShowPercents'),
        seriesDataLabelShowLeaderLines: nullableBoolArrayFromRecord(artifactRecord, 'seriesDataLabelShowLeaderLines'),
        categoryAxisKind: chartCategoryAxisKindFromUnknown(artifactRecord.categoryAxisKind),
        categoryAxisPosition: chartAxisPositionFromUnknown(artifactRecord.categoryAxisPosition),
        valueAxisPosition: chartAxisPositionFromUnknown(artifactRecord.valueAxisPosition),
        categoryAxisTitle: stringFromRecord(artifactRecord, 'categoryAxisTitle'),
        valueAxisTitle: stringFromRecord(artifactRecord, 'valueAxisTitle'),
        categoryAxisTitleFontColor: stringFromRecord(artifactRecord, 'categoryAxisTitleFontColor'),
        categoryAxisTitleFontSize: finiteNumberFromRecord(artifactRecord, 'categoryAxisTitleFontSize'),
        categoryAxisTitleFontBold: nullableBoolFromRecord(artifactRecord, 'categoryAxisTitleFontBold'),
        categoryAxisTitleFontItalic: nullableBoolFromRecord(artifactRecord, 'categoryAxisTitleFontItalic'),
        categoryAxisTitleTextRotation: finiteNumberFromRecord(artifactRecord, 'categoryAxisTitleTextRotation'),
        categoryAxisTitleTextAlign: chartTextAlignFromUnknown(artifactRecord.categoryAxisTitleTextAlign),
        categoryAxisTitleTextVerticalAlign: chartTextVerticalAlignFromUnknown(artifactRecord.categoryAxisTitleTextVerticalAlign),
        categoryAxisTitleTextWrap: nullableBoolFromRecord(artifactRecord, 'categoryAxisTitleTextWrap'),
        categoryAxisTitleTextAutoFit: chartTextAutoFitFromUnknown(artifactRecord.categoryAxisTitleTextAutoFit),
        categoryAxisTitleTextAutoFitFontScale: finiteNumberFromRecord(artifactRecord, 'categoryAxisTitleTextAutoFitFontScale'),
        categoryAxisTitleTextHorizontalOverflow: chartTextHorizontalOverflowFromUnknown(artifactRecord.categoryAxisTitleTextHorizontalOverflow),
        categoryAxisTitleTextVerticalOverflow: chartTextVerticalOverflowFromUnknown(artifactRecord.categoryAxisTitleTextVerticalOverflow),
        categoryAxisTitleTextInsetTop: finiteNumberFromRecord(artifactRecord, 'categoryAxisTitleTextInsetTop'),
        categoryAxisTitleTextInsetRight: finiteNumberFromRecord(artifactRecord, 'categoryAxisTitleTextInsetRight'),
        categoryAxisTitleTextInsetBottom: finiteNumberFromRecord(artifactRecord, 'categoryAxisTitleTextInsetBottom'),
        categoryAxisTitleTextInsetLeft: finiteNumberFromRecord(artifactRecord, 'categoryAxisTitleTextInsetLeft'),
        valueAxisTitleFontColor: stringFromRecord(artifactRecord, 'valueAxisTitleFontColor'),
        valueAxisTitleFontSize: finiteNumberFromRecord(artifactRecord, 'valueAxisTitleFontSize'),
        valueAxisTitleFontBold: nullableBoolFromRecord(artifactRecord, 'valueAxisTitleFontBold'),
        valueAxisTitleFontItalic: nullableBoolFromRecord(artifactRecord, 'valueAxisTitleFontItalic'),
        valueAxisTitleTextRotation: finiteNumberFromRecord(artifactRecord, 'valueAxisTitleTextRotation'),
        valueAxisTitleTextAlign: chartTextAlignFromUnknown(artifactRecord.valueAxisTitleTextAlign),
        valueAxisTitleTextVerticalAlign: chartTextVerticalAlignFromUnknown(artifactRecord.valueAxisTitleTextVerticalAlign),
        valueAxisTitleTextWrap: nullableBoolFromRecord(artifactRecord, 'valueAxisTitleTextWrap'),
        valueAxisTitleTextAutoFit: chartTextAutoFitFromUnknown(artifactRecord.valueAxisTitleTextAutoFit),
        valueAxisTitleTextAutoFitFontScale: finiteNumberFromRecord(artifactRecord, 'valueAxisTitleTextAutoFitFontScale'),
        valueAxisTitleTextHorizontalOverflow: chartTextHorizontalOverflowFromUnknown(artifactRecord.valueAxisTitleTextHorizontalOverflow),
        valueAxisTitleTextVerticalOverflow: chartTextVerticalOverflowFromUnknown(artifactRecord.valueAxisTitleTextVerticalOverflow),
        valueAxisTitleTextInsetTop: finiteNumberFromRecord(artifactRecord, 'valueAxisTitleTextInsetTop'),
        valueAxisTitleTextInsetRight: finiteNumberFromRecord(artifactRecord, 'valueAxisTitleTextInsetRight'),
        valueAxisTitleTextInsetBottom: finiteNumberFromRecord(artifactRecord, 'valueAxisTitleTextInsetBottom'),
        valueAxisTitleTextInsetLeft: finiteNumberFromRecord(artifactRecord, 'valueAxisTitleTextInsetLeft'),
        categoryAxisCrosses: chartAxisCrossingFromUnknown(artifactRecord.categoryAxisCrosses),
        categoryAxisCrossesAt: finiteNumberFromRecord(artifactRecord, 'categoryAxisCrossesAt'),
        categoryAxisTickLabelPosition: chartTickLabelPositionFromUnknown(artifactRecord.categoryAxisTickLabelPosition),
        valueAxisTickLabelPosition: chartTickLabelPositionFromUnknown(artifactRecord.valueAxisTickLabelPosition),
        categoryAxisMajorTickMark: chartTickMarkFromUnknown(artifactRecord.categoryAxisMajorTickMark),
        categoryAxisMinorTickMark: chartTickMarkFromUnknown(artifactRecord.categoryAxisMinorTickMark),
        valueAxisMajorTickMark: chartTickMarkFromUnknown(artifactRecord.valueAxisMajorTickMark),
        valueAxisMinorTickMark: chartTickMarkFromUnknown(artifactRecord.valueAxisMinorTickMark),
        categoryAxisHasMajorGridlines: boolMetadataFromRecord(artifactRecord, 'categoryAxisHasMajorGridlines'),
        categoryAxisHasMinorGridlines: boolMetadataFromRecord(artifactRecord, 'categoryAxisHasMinorGridlines'),
        valueAxisHasMajorGridlines: boolMetadataFromRecord(artifactRecord, 'valueAxisHasMajorGridlines'),
        valueAxisHasMinorGridlines: boolMetadataFromRecord(artifactRecord, 'valueAxisHasMinorGridlines'),
        categoryAxisDeleted,
        valueAxisDeleted,
        categoryAxisBaseTimeUnit: chartDateAxisTimeUnitFromUnknown(artifactRecord.categoryAxisBaseTimeUnit),
        categoryAxisMajorTimeUnit: chartDateAxisTimeUnitFromUnknown(artifactRecord.categoryAxisMajorTimeUnit),
        categoryAxisMinorTimeUnit: chartDateAxisTimeUnitFromUnknown(artifactRecord.categoryAxisMinorTimeUnit),
        categoryAxisMajorUnit: finiteNumberFromRecord(artifactRecord, 'categoryAxisMajorUnit'),
        categoryAxisMinorUnit: finiteNumberFromRecord(artifactRecord, 'categoryAxisMinorUnit'),
        categoryAxisNumberFormat: stringFromRecord(artifactRecord, 'categoryAxisNumberFormat') || null,
        categoryAxisNumberFormatLinkedToSource: nullableBoolFromRecord(artifactRecord, 'categoryAxisNumberFormatLinkedToSource'),
        valueAxisNumberFormat: stringFromRecord(artifactRecord, 'valueAxisNumberFormat') || null,
        valueAxisNumberFormatLinkedToSource: nullableBoolFromRecord(artifactRecord, 'valueAxisNumberFormatLinkedToSource'),
        valueAxisCount: finiteNumberFromRecord(artifactRecord, 'valueAxisCount'),
        hasSecondaryValueAxis: boolMetadataFromRecord(artifactRecord, 'hasSecondaryValueAxis'),
        secondaryValueAxisId: stringFromRecord(artifactRecord, 'secondaryValueAxisId'),
        secondaryValueAxisPosition: chartAxisPositionFromUnknown(artifactRecord.secondaryValueAxisPosition),
        secondaryValueAxisTitle: stringFromRecord(artifactRecord, 'secondaryValueAxisTitle'),
        secondaryValueAxisTitleFontColor: stringFromRecord(artifactRecord, 'secondaryValueAxisTitleFontColor'),
        secondaryValueAxisTitleFontSize: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisTitleFontSize'),
        secondaryValueAxisTitleFontBold: nullableBoolFromRecord(artifactRecord, 'secondaryValueAxisTitleFontBold'),
        secondaryValueAxisTitleFontItalic: nullableBoolFromRecord(artifactRecord, 'secondaryValueAxisTitleFontItalic'),
        secondaryValueAxisTitleTextRotation: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisTitleTextRotation'),
        secondaryValueAxisTitleTextAlign: chartTextAlignFromUnknown(artifactRecord.secondaryValueAxisTitleTextAlign),
        secondaryValueAxisTitleTextVerticalAlign: chartTextVerticalAlignFromUnknown(artifactRecord.secondaryValueAxisTitleTextVerticalAlign),
        secondaryValueAxisTitleTextWrap: nullableBoolFromRecord(artifactRecord, 'secondaryValueAxisTitleTextWrap'),
        secondaryValueAxisTitleTextAutoFit: chartTextAutoFitFromUnknown(artifactRecord.secondaryValueAxisTitleTextAutoFit),
        secondaryValueAxisTitleTextAutoFitFontScale: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisTitleTextAutoFitFontScale'),
        secondaryValueAxisTitleTextHorizontalOverflow: chartTextHorizontalOverflowFromUnknown(artifactRecord.secondaryValueAxisTitleTextHorizontalOverflow),
        secondaryValueAxisTitleTextVerticalOverflow: chartTextVerticalOverflowFromUnknown(artifactRecord.secondaryValueAxisTitleTextVerticalOverflow),
        secondaryValueAxisTitleTextInsetTop: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisTitleTextInsetTop'),
        secondaryValueAxisTitleTextInsetRight: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisTitleTextInsetRight'),
        secondaryValueAxisTitleTextInsetBottom: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisTitleTextInsetBottom'),
        secondaryValueAxisTitleTextInsetLeft: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisTitleTextInsetLeft'),
        secondaryValueAxisMin: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisMin'),
        secondaryValueAxisMax: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisMax'),
        secondaryValueAxisLogBase: chartLogBaseFromRecord(artifactRecord, 'secondaryValueAxisLogBase'),
        secondaryValueAxisDisplayUnit: chartDisplayUnitFromUnknown(artifactRecord.secondaryValueAxisDisplayUnit),
        secondaryValueAxisDisplayUnitCustom: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisDisplayUnitCustom'),
        secondaryValueAxisCrosses: chartAxisCrossingFromUnknown(artifactRecord.secondaryValueAxisCrosses),
        secondaryValueAxisCrossesAt: finiteNumberFromRecord(artifactRecord, 'secondaryValueAxisCrossesAt'),
        secondaryValueAxisTickLabelPosition: chartTickLabelPositionFromUnknown(artifactRecord.secondaryValueAxisTickLabelPosition),
        secondaryValueAxisMajorTickMark: chartTickMarkFromUnknown(artifactRecord.secondaryValueAxisMajorTickMark),
        secondaryValueAxisMinorTickMark: chartTickMarkFromUnknown(artifactRecord.secondaryValueAxisMinorTickMark),
        secondaryValueAxisHasMajorGridlines: boolMetadataFromRecord(artifactRecord, 'secondaryValueAxisHasMajorGridlines'),
        secondaryValueAxisHasMinorGridlines: boolMetadataFromRecord(artifactRecord, 'secondaryValueAxisHasMinorGridlines'),
        secondaryValueAxisDeleted: nullableBoolFromRecord(artifactRecord, 'secondaryValueAxisDeleted'),
        secondaryValueAxisNumberFormat: stringFromRecord(artifactRecord, 'secondaryValueAxisNumberFormat') || null,
        secondaryValueAxisNumberFormatLinkedToSource: nullableBoolFromRecord(artifactRecord, 'secondaryValueAxisNumberFormatLinkedToSource'),
        seriesValueAxisIds: trimmedStringArrayFromRecord(artifactRecord, 'seriesValueAxisIds'),
        seriesChartTypes: stringEnumArrayFromRecord(artifactRecord, 'seriesChartTypes', chartTypeFromUnknown),
        seriesColors: stringArrayFromRecord(artifactRecord, 'seriesColors'),
        seriesLineColors: stringArrayFromRecord(artifactRecord, 'seriesLineColors'),
        seriesLineWidths: numberArrayFromRecord(artifactRecord, 'seriesLineWidths'),
        seriesMarkerSizes: numberArrayFromRecord(artifactRecord, 'seriesMarkerSizes'),
        seriesMarkerShapes: stringEnumArrayFromRecord(artifactRecord, 'seriesMarkerShapes', chartMarkerShapeFromUnknown),
        seriesMarkerFillColors: stringArrayFromRecord(artifactRecord, 'seriesMarkerFillColors'),
        seriesMarkerLineColors: stringArrayFromRecord(artifactRecord, 'seriesMarkerLineColors'),
        seriesLineDashes: stringEnumArrayFromRecord(artifactRecord, 'seriesLineDashes', chartLineDashFromUnknown),
        seriesFillOpacities: opacityArrayFromRecord(artifactRecord, 'seriesFillOpacities'),
        seriesFillPatterns: stringEnumArrayFromRecord(artifactRecord, 'seriesFillPatterns', chartFillPatternFromUnknown),
        seriesFillForegroundColors: stringArrayFromRecord(artifactRecord, 'seriesFillForegroundColors'),
        seriesFillBackgroundColors: stringArrayFromRecord(artifactRecord, 'seriesFillBackgroundColors'),
        seriesExplosions: explosionArrayFromRecord(artifactRecord, 'seriesExplosions'),
        seriesPointIndexes: nestedNumberArrayFromRecord(artifactRecord, 'seriesPointIndexes'),
        seriesPointColors: nestedStringArrayFromRecord(artifactRecord, 'seriesPointColors'),
        seriesPointLineColors: nestedStringArrayFromRecord(artifactRecord, 'seriesPointLineColors'),
        seriesPointFillOpacities: nestedOpacityArrayFromRecord(artifactRecord, 'seriesPointFillOpacities'),
        seriesPointFillPatterns: nestedStringEnumArrayFromRecord(artifactRecord, 'seriesPointFillPatterns', chartFillPatternFromUnknown),
        seriesPointFillForegroundColors: nestedStringArrayFromRecord(artifactRecord, 'seriesPointFillForegroundColors'),
        seriesPointFillBackgroundColors: nestedStringArrayFromRecord(artifactRecord, 'seriesPointFillBackgroundColors'),
        seriesPointExplosions: nestedExplosionArrayFromRecord(artifactRecord, 'seriesPointExplosions'),
        pieFirstSliceAngle: finiteNumberFromRecord(artifactRecord, 'pieFirstSliceAngle'),
        doughnutHoleSize: finiteNumberFromRecord(artifactRecord, 'doughnutHoleSize'),
        valueAxisMin: finiteNumberFromRecord(artifactRecord, 'valueAxisMin'),
        valueAxisMax: finiteNumberFromRecord(artifactRecord, 'valueAxisMax'),
        valueAxisLogBase: chartLogBaseFromRecord(artifactRecord, 'valueAxisLogBase'),
        valueAxisDisplayUnit: chartDisplayUnitFromUnknown(artifactRecord.valueAxisDisplayUnit),
        valueAxisDisplayUnitCustom: finiteNumberFromRecord(artifactRecord, 'valueAxisDisplayUnitCustom'),
        valueAxisCrosses: chartAxisCrossingFromUnknown(artifactRecord.valueAxisCrosses),
        valueAxisCrossesAt: finiteNumberFromRecord(artifactRecord, 'valueAxisCrossesAt'),
        chartGrouping: chartGroupingFromUnknown(artifactRecord.chartGrouping),
        chartVaryColors: nullableBoolFromRecord(artifactRecord, 'chartVaryColors'),
        lineSmooth: nullableBoolFromRecord(artifactRecord, 'lineSmooth'),
        barGapWidth: finiteNumberFromRecord(artifactRecord, 'barGapWidth'),
        barOverlap: finiteNumberFromRecord(artifactRecord, 'barOverlap'),
        chartRoundedCorners: nullableBoolFromRecord(artifactRecord, 'chartRoundedCorners'),
        chartAreaFillColor: stringFromRecord(artifactRecord, 'chartAreaFillColor'),
        chartAreaFillOpacity: finiteNumberFromRecord(artifactRecord, 'chartAreaFillOpacity'),
        chartAreaLineColor: stringFromRecord(artifactRecord, 'chartAreaLineColor'),
        chartAreaLineWidth: finiteNumberFromRecord(artifactRecord, 'chartAreaLineWidth'),
        chartAreaFillPattern: chartFillPatternFromUnknown(artifactRecord.chartAreaFillPattern),
        chartAreaFillForegroundColor: stringFromRecord(artifactRecord, 'chartAreaFillForegroundColor'),
        chartAreaFillBackgroundColor: stringFromRecord(artifactRecord, 'chartAreaFillBackgroundColor'),
        plotAreaFillColor: stringFromRecord(artifactRecord, 'plotAreaFillColor'),
        plotAreaFillOpacity: finiteNumberFromRecord(artifactRecord, 'plotAreaFillOpacity'),
        plotAreaLineColor: stringFromRecord(artifactRecord, 'plotAreaLineColor'),
        plotAreaLineWidth: finiteNumberFromRecord(artifactRecord, 'plotAreaLineWidth'),
        plotAreaFillPattern: chartFillPatternFromUnknown(artifactRecord.plotAreaFillPattern),
        plotAreaFillForegroundColor: stringFromRecord(artifactRecord, 'plotAreaFillForegroundColor'),
        plotAreaFillBackgroundColor: stringFromRecord(artifactRecord, 'plotAreaFillBackgroundColor'),
    };
    for (const override of args.overrides ?? []) {
        if (!override) continue;
        if (override.key === 'seriesOrientation') {
            base.seriesOrientation = override.value;
        } else if (override.key === 'legendPosition') {
            base.legendPosition = override.value;
        } else if (override.key === 'categoryAxisTitle' || override.key === 'valueAxisTitle') {
            base[override.key] = String(override.value ?? '').trim();
        } else {
            base[override.key] = typeof override.value === 'boolean'
                ? override.value
                : !base[override.key];
        }
    }
    return base;
};

const mergeExcelRangeChartMetadata = (
    artifact: SpreadsheetDerivedArtifact,
    settings: ExcelRangeChartSettings,
): Record<string, unknown> => ({
    ...(isDerivedArtifactRecord(artifact.metadata) ? artifact.metadata : {}),
    excel_range_chart: settings,
});

export type DerivedArtifactChartPreviewVisual = 'bar' | 'line' | 'area' | 'pie' | 'scatter';

export type DerivedArtifactChartPlotPoint = {
    label: string;
    value: number;
    xValue?: number;
    xPct: number;
    yPct: number;
    magnitudePct: number;
    axisPct: number;
    barStartPct: number;
    barWidthPct: number;
    sharePct: number;
    shareStartPct: number;
    shareEndPct: number;
    markerFillColor?: string;
    markerLineColor?: string;
    markerFillOpacity?: number;
    markerFillPattern?: ExcelRangeChartFillPattern;
    markerFillForegroundColor?: string;
    markerFillBackgroundColor?: string;
};

export type DerivedArtifactPieLegendItem = Pick<DerivedArtifactChartPlotPoint, 'label' | 'sharePct' | 'shareStartPct' | 'shareEndPct'> & {
    color: string;
    sampledRemainder?: boolean;
    hiddenPointCount?: number;
};

export type DerivedArtifactChartSeriesSummaryItem = DerivedArtifactChartPreviewSeries & {
    color: string;
    displayTotal: string;
    title: string;
    lineColor?: string;
    lineWidth?: number;
    pointRadius?: number;
    markerShape?: ExcelRangeChartMarkerShape;
    markerFillColor?: string;
    markerLineColor?: string;
    lineDash?: ExcelRangeChartLineDash;
    fillOpacity?: number;
    fillPattern?: ExcelRangeChartFillPattern;
    fillForegroundColor?: string;
    fillBackgroundColor?: string;
};

export type DerivedArtifactChartSeriesPlotItem = DerivedArtifactChartSeriesSummaryItem & {
    polyline: string;
    smoothPath: string;
    areaPolygon: string;
    areaFillColor: string;
    renderAreaFill: boolean;
    renderBarFill: boolean;
    renderLineStroke: boolean;
};

export type DerivedArtifactChartCategoryBarItem = {
    key: string;
    seriesName: string;
    value: number;
    color: string;
    displayValue: string;
    axisPct: number;
    barStartPct: number;
    barWidthPct: number;
    fillPattern?: ExcelRangeChartFillPattern;
    fillForegroundColor?: string;
    fillBackgroundColor?: string;
};

export type DerivedArtifactChartCategoryBarGroup = {
    label: string;
    grouping: ExcelRangeChartGrouping;
    barGapPx: number;
    barHeightPx: number;
    minHeightPx: number;
    bars: DerivedArtifactChartCategoryBarItem[];
};

export type DerivedArtifactChartAggregatePointItem = DerivedArtifactChartPlotPoint & {
    color: string;
    displayLabel: string;
    displayValue: string;
};

export type DerivedArtifactChartAggregatePlotItem = {
    points: DerivedArtifactChartAggregatePointItem[];
    polyline: string;
    smoothPath: string;
    areaPolygon: string;
    lineColor: string;
    areaFillColor: string;
    startLabel: string;
    endLabel: string;
};

export type DerivedArtifactChartAxisGuideLine = {
    orientation: 'horizontal' | 'vertical';
    pct: number;
    weight: 'major' | 'minor';
};

export type DerivedArtifactChartAxisTickMarkItem = {
    axis: 'category' | 'value' | 'secondaryValue';
    pct: number;
    weight: 'major' | 'minor';
    mark: Exclude<ExcelRangeChartTickMark, 'none'>;
};

export type DerivedArtifactChartAxisTickLabelItem = {
    axis: 'category' | 'value' | 'secondaryValue';
    pct: number;
    label: string;
    placement: 'low' | 'high' | 'nextTo';
    textWrap?: boolean;
};

export type DerivedArtifactChartDataLabelItem = {
    label: string;
    title: string;
    xPct: number;
    yPct: number;
    anchor: 'center' | 'above' | 'below' | 'left' | 'right';
    color: string;
    fontSize: number;
    fontWeight: number;
    fontStyle: 'normal' | 'italic';
    rotationDeg: number;
    textAlign: ExcelRangeChartTextAlign;
    textVerticalAlign: ExcelRangeChartTextVerticalAlign;
    textWrap: boolean;
    textAutoFit: ExcelRangeChartTextAutoFit | null;
    textAutoFitFontScale: number | null;
    textHorizontalOverflow: ExcelRangeChartTextHorizontalOverflow | null;
    textVerticalOverflow: ExcelRangeChartTextVerticalOverflow | null;
    textInsetTop: number;
    textInsetRight: number;
    textInsetBottom: number;
    textInsetLeft: number;
    leaderLine?: {
        x1Pct: number;
        y1Pct: number;
        x2Pct: number;
        y2Pct: number;
    };
};

export type DerivedArtifactPieSliceItem = {
    label: string;
    color: string;
    path: string;
    translateX: number;
    translateY: number;
    exploded: boolean;
    fillPattern?: ExcelRangeChartFillPattern;
    fillForegroundColor?: string;
    fillBackgroundColor?: string;
};

export type DerivedArtifactPivotMeasureSummaryItem = DerivedArtifactPivotMeasureSummary & {
    displayTotal: string;
    title: string;
};

export type DerivedArtifactChartPreviewRenderKind = 'plot' | 'pie' | 'bar';

export type DerivedArtifactChartPreviewPresentation = {
    hasRenderableContent: boolean;
    renderKind: DerivedArtifactChartPreviewRenderKind;
    chartAreaStyle: DerivedArtifactChartShapeStyle;
    plotAreaStyle: DerivedArtifactChartShapeStyle;
    renderAreaFill: boolean;
    renderLineStroke: boolean;
    renderAggregateLine: boolean;
    chartTitleStyle: DerivedArtifactChartTextStyle;
    categoryAxisTitleStyle: DerivedArtifactChartTextStyle;
    valueAxisTitleStyle: DerivedArtifactChartTextStyle;
    secondaryValueAxisTitleStyle: DerivedArtifactChartTextStyle;
    seriesPointRadius: number;
    aggregatePointRadius: number;
    seriesSummaryItems: DerivedArtifactChartSeriesSummaryItem[];
    aggregatePlot: DerivedArtifactChartAggregatePlotItem;
    seriesPlotItems: DerivedArtifactChartSeriesPlotItem[];
    categoryBarGroups: DerivedArtifactChartCategoryBarGroup[];
    axisGuideLines: DerivedArtifactChartAxisGuideLine[];
    axisTickMarks: DerivedArtifactChartAxisTickMarkItem[];
    axisTickLabels: DerivedArtifactChartAxisTickLabelItem[];
    dataLabelItems: DerivedArtifactChartDataLabelItem[];
    pieGradient: string;
    pieHoleSizePct: number;
    pieSliceItems: DerivedArtifactPieSliceItem[];
    pieLegendItems: DerivedArtifactPieLegendItem[];
    emptyLabel: string;
};

export type DerivedArtifactPivotPreviewPresentation = {
    hasRenderableContent: boolean;
    measureSummaryItems: DerivedArtifactPivotMeasureSummaryItem[];
    emptyLabel: string;
};

export type DerivedArtifactPreviewStatusKind = 'loading' | 'error' | 'chart' | 'pivot' | 'empty' | 'placeholder';

export type DerivedArtifactPreviewStatusPresentation = {
    statusKind: DerivedArtifactPreviewStatusKind;
    label: string;
    title: string;
};

export type DerivedArtifactOverlayChromePresentation = {
    kindLabel: string;
    iconLabel: string;
    title: string;
    placementAddress: string;
    theme: DerivedArtifactOverlayThemePresentation;
    overlayTitle: string;
    overlayAriaLabel: string;
    sourceOutlineTitle: string;
    sourceOutlineAriaLabel: string;
    sourceMoveTitle: string;
    sourceMoveAriaLabel: string;
    sourceResizeTitle: string;
    sourceResizeAriaLabel: string;
    sourceChipLabel: string;
    placementResizeTitle: string;
    placementResizeAriaLabel: string;
};

export type DerivedArtifactOverlayThemePresentation = {
    sourceOutlineBorderColor: string;
    sourceOutlineBackground: string;
    sourceOutlineSelectedBackground: string;
    sourceOutlineShadowColor: string;
    sourceHandleBorderColor: string;
    sourceHandleTextColor: string;
    sourceResizeBorderColor: string;
    overlayBorderColor: string;
    overlayBackground: string;
    overlaySelectedShadowColor: string;
    headerBackground: string;
    headerBorderColor: string;
    headerTextColor: string;
    headerAddressTextColor: string;
    previewBorderColor: string;
};

export type DerivedArtifactContextMenuItemPresentation = {
    label: string;
    title: string;
    action: string;
    checked?: boolean;
};

export type DerivedArtifactContextMenuPresentation = {
    promptTitle: string;
    promptDefaultTitle: string;
    renameItem: DerivedArtifactContextMenuItemPresentation;
    resizeItem: DerivedArtifactContextMenuItemPresentation;
    setSourceItem: DerivedArtifactContextMenuItemPresentation;
    selectSourceItem: DerivedArtifactContextMenuItemPresentation;
    deleteItem: DerivedArtifactContextMenuItemPresentation;
    chartTypeMenu: {
        label: string;
        title: string;
        items: DerivedArtifactContextMenuItemPresentation[];
    } | null;
    chartSourceMenu: {
        label: string;
        title: string;
        items: DerivedArtifactContextMenuItemPresentation[];
    } | null;
    chartDisplayMenu: {
        label: string;
        title: string;
        items: DerivedArtifactContextMenuItemPresentation[];
    } | null;
};

export type DerivedArtifactContextMenuEntry =
    | { type: 'separator' }
    | {
        type?: 'item';
        label: string;
        title: string;
        action?: string;
        checked?: boolean;
        submenu?: DerivedArtifactContextMenuItemPresentation[];
    };

export type DerivedArtifactSelectionProjection = {
    col: number;
    row: number;
    endCol: number;
    endRow: number;
};

export type DerivedArtifactOverlayCellCoordinate = {
    col: number;
    row: number;
};

export type DerivedArtifactOverlayInteractionProjection = {
    placementSelection: DerivedArtifactSelectionProjection;
    sourceSelection: DerivedArtifactSelectionProjection;
    placementMoveDrag: {
        originCol: number;
        originRow: number;
        width: number;
        height: number;
    };
    placementResizeDrag: {
        anchorCol: number;
        anchorRow: number;
    };
    sourceMoveDrag: {
        artifactId: string;
        width: number;
        height: number;
    };
    sourceResizeDrag: {
        artifactId: string;
        anchorCol: number;
        anchorRow: number;
    };
};

export type DerivedArtifactOverlaySelectionTarget = 'placement' | 'source';

export type DerivedArtifactOverlaySelectionUpdate = {
    artifactId: string;
    placementCell: DerivedArtifactOverlayCellCoordinate;
    selection: DerivedArtifactSelectionProjection;
    selectedCell: DerivedArtifactOverlayCellCoordinate;
};

export type DerivedArtifactOverlayDragState =
    | {
        kind: 'placement-resize';
        drag: DerivedArtifactOverlayInteractionProjection['placementResizeDrag'];
    }
    | {
        kind: 'placement-move';
        drag: DerivedArtifactOverlayInteractionProjection['placementMoveDrag'];
    }
    | {
        kind: 'source-resize';
        drag: DerivedArtifactOverlayInteractionProjection['sourceResizeDrag'];
    }
    | {
        kind: 'source-move';
        drag: DerivedArtifactOverlayInteractionProjection['sourceMoveDrag'];
    };

export type DerivedArtifactOverlayDragCommit =
    | {
        kind: 'placement-resize';
        anchorCell: DerivedArtifactOverlayCellCoordinate;
        targetCell: DerivedArtifactOverlayCellCoordinate;
    }
    | {
        kind: 'placement-move';
        originCell: DerivedArtifactOverlayCellCoordinate;
        targetCell: DerivedArtifactOverlayCellCoordinate;
    }
    | {
        kind: 'source-resize';
        artifactId: string;
        targetCell: DerivedArtifactOverlayCellCoordinate;
    }
    | {
        kind: 'source-move';
        artifactId: string;
        targetCell: DerivedArtifactOverlayCellCoordinate;
    };

export type DerivedArtifactOverlayDragPreviewUpdate = {
    selection: DerivedArtifactSelectionProjection;
    selectedCell: DerivedArtifactOverlayCellCoordinate;
    commit: DerivedArtifactOverlayDragCommit;
};

export type DerivedArtifactOverlayDragStates = {
    placementResize: DerivedArtifactOverlayDragState;
    placementMove: DerivedArtifactOverlayDragState;
    sourceResize: DerivedArtifactOverlayDragState;
    sourceMove: DerivedArtifactOverlayDragState;
};

export type DerivedArtifactOverlaySelectionPresentation = {
    placementSelected: boolean;
    sourceSelected: boolean;
};

export type DerivedArtifactOverlayVisibleBounds = {
    startCol: number;
    endCol: number;
    startRow: number;
    endRow: number;
};

export type DerivedArtifactOverlayRangeProjection = {
    placementRange: SpreadsheetRangeAddress;
    sourceRange: SpreadsheetRangeAddress;
    placementVisible: boolean;
    sourceVisible: boolean;
};

export type DerivedArtifactOverlayScreenRect = {
    x: number;
    y: number;
    w: number;
    h: number;
};

export type DerivedArtifactOverlayViewportRect = {
    width: number;
    height: number;
};

export type DerivedArtifactOverlayScreenRectResolver = (
    range: SpreadsheetRangeAddress,
) => DerivedArtifactOverlayScreenRect | null | undefined;

export type DerivedArtifactOverlayPreviewCacheState = {
    sourceKey: string;
    loading: boolean;
    error: string | null;
    cells: SpreadsheetRangeCell[][] | null;
};

export type DerivedArtifactOverlayProjection = {
    id: string;
    kind: SpreadsheetDerivedArtifact['kind'];
    title: string;
    chartType?: SpreadsheetChartArtifactType;
    range: SpreadsheetRangeAddress;
    sourceRange: SpreadsheetRangeAddress;
    sourceAddress: string;
    placementSelected: boolean;
    sourceSelected: boolean;
    preview: DerivedArtifactPreviewModel | null;
    previewFootnote: string;
    chartSettings: ExcelRangeChartSettings | null;
    interaction: DerivedArtifactOverlayInteractionProjection;
    placementSelectionUpdate: DerivedArtifactOverlaySelectionUpdate;
    sourceSelectionUpdate: DerivedArtifactOverlaySelectionUpdate;
    dragStates: DerivedArtifactOverlayDragStates;
    previewLoading: boolean;
    previewError: string | null;
    sourceRect: DerivedArtifactOverlayScreenRect | null;
    rect: DerivedArtifactOverlayScreenRect;
};

export type DerivedArtifactChartPreviewModel = Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>;
export type DerivedArtifactPivotPreviewModel = Extract<DerivedArtifactPreviewModel, { kind: 'pivot' }>;

export type DerivedArtifactOverlayRenderPresentation = {
    chrome: DerivedArtifactOverlayChromePresentation;
    theme: DerivedArtifactOverlayThemePresentation;
    chartPreview: DerivedArtifactChartPreviewModel | null;
    pivotPreview: DerivedArtifactPivotPreviewModel | null;
    chartPresentation: DerivedArtifactChartPreviewPresentation | null;
    pivotPresentation: DerivedArtifactPivotPreviewPresentation | null;
    chartSeriesSummaryItems: DerivedArtifactChartSeriesSummaryItem[];
    chartAggregatePlot: DerivedArtifactChartAggregatePlotItem | null;
    chartSeriesPlotItems: DerivedArtifactChartSeriesPlotItem[];
    chartCategoryBarGroups: DerivedArtifactChartCategoryBarGroup[];
    chartAxisGuideLines: DerivedArtifactChartAxisGuideLine[];
    chartAxisTickMarks: DerivedArtifactChartAxisTickMarkItem[];
    chartAxisTickLabels: DerivedArtifactChartAxisTickLabelItem[];
    chartDataLabelItems: DerivedArtifactChartDataLabelItem[];
    chartPieGradient: string;
    chartPieHoleSizePct: number;
    chartPieSliceItems: DerivedArtifactPieSliceItem[];
    chartPieLegendItems: DerivedArtifactPieLegendItem[];
    chartSettings: ExcelRangeChartSettings | null;
    chartQuickToolbar: DerivedArtifactChartQuickToolbarPresentation | null;
    pivotMeasureSummaryItems: DerivedArtifactPivotMeasureSummaryItem[];
    chartRenderKind: DerivedArtifactChartPreviewRenderKind;
    previewStatus: DerivedArtifactPreviewStatusPresentation;
};

export type DerivedArtifactChartQuickToolbarItemPresentation = {
    label: string;
    title: string;
    action: string;
    active: boolean;
    promptTitle?: string;
    promptDefault?: string;
};

export type DerivedArtifactChartQuickToolbarGroupPresentation = {
    label: string;
    title: string;
    items: DerivedArtifactChartQuickToolbarItemPresentation[];
};

export type DerivedArtifactChartQuickToolbarPresentation = {
    title: string;
    editGroup: DerivedArtifactChartQuickToolbarGroupPresentation;
    chartTypeGroup: DerivedArtifactChartQuickToolbarGroupPresentation;
    sourceGroup: DerivedArtifactChartQuickToolbarGroupPresentation;
    displayGroup: DerivedArtifactChartQuickToolbarGroupPresentation;
};

export type DerivedArtifactOverlayCollectionProjectionRequest = {
    artifacts: SpreadsheetDerivedArtifact[];
    activeSheetId: string;
    visibleBounds: DerivedArtifactOverlayVisibleBounds;
    viewport: DerivedArtifactOverlayViewportRect;
    selectedRange: SpreadsheetRangeAddress | null | undefined;
    previewStates: Record<string, DerivedArtifactOverlayPreviewCacheState>;
    getRangeScreenRect: DerivedArtifactOverlayScreenRectResolver;
};

export type DerivedArtifactPreviewFetchRequest = {
    artifact: SpreadsheetDerivedArtifact;
    artifactId: string;
    previewRange: DerivedArtifactPreviewRange;
    sourceKey: string;
};

export type DerivedArtifactPreviewFetchRangeRead = {
    artifactId: string;
    sourceKey: string;
    sheetId: string;
    startCol: number;
    startRow: number;
    endCol: number;
    endRow: number;
    mode: 'computed';
};

export type DerivedArtifactPreviewFetchPlan = {
    requests: DerivedArtifactPreviewFetchRequest[];
    rangeReads: DerivedArtifactPreviewFetchRangeRead[];
    shouldClearCache: boolean;
};

const DERIVED_ARTIFACT_CHART_TYPE_MENU_ITEMS: Array<{
    chartType: SpreadsheetChartArtifactType;
    label: string;
    action: string;
}> = [
    { chartType: 'column', label: 'Column', action: 'derived-chart-type-column' },
    { chartType: 'bar', label: 'Bar', action: 'derived-chart-type-bar' },
    { chartType: 'line', label: 'Line', action: 'derived-chart-type-line' },
    { chartType: 'area', label: 'Area', action: 'derived-chart-type-area' },
    { chartType: 'pie', label: 'Pie', action: 'derived-chart-type-pie' },
    { chartType: 'scatter', label: 'Scatter', action: 'derived-chart-type-scatter' },
];

const DERIVED_ARTIFACT_CHART_METADATA_ACTIONS: Array<{
    action: string;
    label: string;
    shortLabel: string;
    update: ExcelRangeChartMetadataUpdate;
    group: 'source' | 'display';
}> = [
    { action: 'derived-chart-source-orientation-columns', label: 'Plot Series by Columns', shortLabel: 'Cols', update: { key: 'seriesOrientation', value: 'columns' }, group: 'source' },
    { action: 'derived-chart-source-orientation-rows', label: 'Plot Series by Rows', shortLabel: 'Rows', update: { key: 'seriesOrientation', value: 'rows' }, group: 'source' },
    { action: 'derived-chart-toggle-first-row-headers', label: 'Use First Row as Headers', shortLabel: 'Headers', update: { key: 'firstRowHeaders' }, group: 'source' },
    { action: 'derived-chart-toggle-first-column-categories', label: 'Use First Column as Categories', shortLabel: 'Categories', update: { key: 'firstColumnCategories' }, group: 'source' },
    { action: 'derived-chart-toggle-legend', label: 'Show Legend', shortLabel: 'Legend', update: { key: 'showLegend' }, group: 'display' },
    { action: 'derived-chart-legend-top', label: 'Legend Top', shortLabel: 'Top', update: { key: 'legendPosition', value: 'top' }, group: 'display' },
    { action: 'derived-chart-legend-bottom', label: 'Legend Bottom', shortLabel: 'Bottom', update: { key: 'legendPosition', value: 'bottom' }, group: 'display' },
    { action: 'derived-chart-legend-left', label: 'Legend Left', shortLabel: 'Left', update: { key: 'legendPosition', value: 'left' }, group: 'display' },
    { action: 'derived-chart-legend-right', label: 'Legend Right', shortLabel: 'Right', update: { key: 'legendPosition', value: 'right' }, group: 'display' },
    { action: 'derived-chart-toggle-data-labels', label: 'Show Data Labels', shortLabel: 'Labels', update: { key: 'showDataLabels' }, group: 'display' },
    { action: 'derived-chart-toggle-category-axis', label: 'Show Category Axis', shortLabel: 'Cat Axis', update: { key: 'showCategoryAxis' }, group: 'display' },
    { action: 'derived-chart-toggle-value-axis', label: 'Show Value Axis', shortLabel: 'Val Axis', update: { key: 'showValueAxis' }, group: 'display' },
];

const derivedArtifactMutationLabel = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'kind'>,
    action: 'rename' | 'resize' | 'move' | 'source-change' | 'source-resize' | 'source-move',
): string => {
    const subject = artifact.kind === 'pivot' ? 'pivot artifact' : 'chart artifact';
    switch (action) {
        case 'rename':
            return `Rename ${subject}`;
        case 'resize':
            return `Resize ${subject}`;
        case 'move':
            return `Move ${subject}`;
        case 'source-change':
            return `Change ${artifact.kind === 'pivot' ? 'pivot' : 'chart'} source range`;
        case 'source-resize':
            return `Resize ${artifact.kind === 'pivot' ? 'pivot' : 'chart'} source range`;
        case 'source-move':
            return `Move ${artifact.kind === 'pivot' ? 'pivot' : 'chart'} source range`;
    }
};

const clampDerivedArtifactIndex = (value: number, max: number): number => (
    Math.max(0, Math.min(Math.max(0, max), Math.floor(value)))
);

const rangeFromUiRange = (
    sheetId: string,
    range: DerivedArtifactUiRange,
): SpreadsheetRangeAddress => ({
    sheet_id: sheetId,
    start_row: Math.min(range.startRow, range.endRow),
    start_col: Math.min(range.startCol, range.endCol),
    end_row: Math.max(range.startRow, range.endRow),
    end_col: Math.max(range.startCol, range.endCol),
});

export const buildDerivedArtifactInsertPlan = (args: {
    artifactKind: SpreadsheetDerivedArtifact['kind'];
    range: DerivedArtifactUiRange | null | undefined;
    chartType?: SpreadsheetChartArtifactType | null;
    title?: string | null;
    metadata?: Record<string, unknown> | null;
}): DerivedArtifactMutationPlan => {
    if (!args.range) return { kind: 'noop' };
    const title = String(args.title ?? '').trim();
    return {
        kind: 'upsert-derived-artifact',
        artifactKind: args.artifactKind,
        range: args.range,
        options: args.artifactKind === 'chart'
            ? {
                chartType: args.chartType ?? 'column',
                title: title || 'Chart',
                ...(args.metadata ? { metadata: args.metadata } : {}),
            }
            : { title: 'PivotTable' },
    };
};

export const buildExcelRangeChartInsertPlan = (args: {
    range: DerivedArtifactUiRange | null | undefined;
    chartType?: SpreadsheetChartArtifactType | null;
}): DerivedArtifactMutationPlan => {
    if (!args.range) return { kind: 'noop' };
    const chartType = args.chartType ?? 'column';
    const settings = buildExcelRangeChartSettings({ range: args.range, chartType });
    return buildDerivedArtifactInsertPlan({
        artifactKind: 'chart',
        range: args.range,
        chartType,
        title: buildExcelRangeChartTitle(chartType),
        metadata: settings
            ? {
                excel_range_chart: settings,
            }
            : null,
    });
};

export const buildRenameDerivedArtifactPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    nextTitle: string;
}): DerivedArtifactMutationPlan => {
    const title = String(args.nextTitle ?? '').trim();
    const artifact = args.artifact;
    if (!artifact || !title || title === artifact.title) return { kind: 'noop' };
    return {
        kind: 'update-derived-artifact',
        artifact: { ...artifact, title },
        options: { label: derivedArtifactMutationLabel(artifact, 'rename') },
    };
};

export const buildResizeDerivedArtifactToSelectionPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    range: DerivedArtifactUiRange | null | undefined;
}): DerivedArtifactMutationPlan => {
    if (!args.artifact || !args.range) return { kind: 'noop' };
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...args.artifact,
            placement: rangeFromUiRange(args.artifact.sheet_id, args.range),
        },
        options: { label: derivedArtifactMutationLabel(args.artifact, 'resize') },
    };
};

export const buildResizeDerivedArtifactToCellPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    targetCol: number;
    targetRow: number;
    colCount: number;
    rowCount: number;
}): DerivedArtifactMutationPlan => {
    const artifact = args.artifact;
    if (!artifact) return { kind: 'noop' };
    const startCol = clampDerivedArtifactIndex(artifact.placement.start_col, args.colCount - 1);
    const startRow = clampDerivedArtifactIndex(artifact.placement.start_row, args.rowCount - 1);
    const endCol = Math.max(startCol, clampDerivedArtifactIndex(args.targetCol, args.colCount - 1));
    const endRow = Math.max(startRow, clampDerivedArtifactIndex(args.targetRow, args.rowCount - 1));
    if (artifact.placement.end_col === endCol && artifact.placement.end_row === endRow) return { kind: 'noop' };
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...artifact,
            placement: {
                sheet_id: artifact.sheet_id,
                start_row: startRow,
                start_col: startCol,
                end_row: endRow,
                end_col: endCol,
            },
        },
        options: { label: derivedArtifactMutationLabel(artifact, 'resize') },
    };
};

export const buildMoveDerivedArtifactToCellPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    targetCol: number;
    targetRow: number;
    colCount: number;
    rowCount: number;
}): DerivedArtifactMutationPlan => {
    const artifact = args.artifact;
    if (!artifact) return { kind: 'noop' };
    const width = Math.max(1, artifact.placement.end_col - artifact.placement.start_col + 1);
    const height = Math.max(1, artifact.placement.end_row - artifact.placement.start_row + 1);
    const maxStartCol = Math.max(0, args.colCount - width);
    const maxStartRow = Math.max(0, args.rowCount - height);
    const startCol = clampDerivedArtifactIndex(args.targetCol, maxStartCol);
    const startRow = clampDerivedArtifactIndex(args.targetRow, maxStartRow);
    const endCol = Math.min(args.colCount - 1, startCol + width - 1);
    const endRow = Math.min(args.rowCount - 1, startRow + height - 1);
    if (
        artifact.placement.start_col === startCol &&
        artifact.placement.start_row === startRow &&
        artifact.placement.end_col === endCol &&
        artifact.placement.end_row === endRow
    ) return { kind: 'noop' };
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...artifact,
            placement: {
                sheet_id: artifact.sheet_id,
                start_row: startRow,
                start_col: startCol,
                end_row: endRow,
                end_col: endCol,
            },
        },
        options: { label: derivedArtifactMutationLabel(artifact, 'move') },
    };
};

export const buildSetDerivedArtifactSourceToSelectionPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    range: DerivedArtifactUiRange | null | undefined;
}): DerivedArtifactMutationPlan => {
    if (!args.artifact || !args.range) return { kind: 'noop' };
    const nextSourceRange = rangeFromUiRange(args.artifact.sheet_id, args.range);
    const nextSettings = args.artifact.kind === 'chart'
        ? buildExcelRangeChartSettings({ artifact: args.artifact, range: args.range })
        : null;
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...args.artifact,
            source_range: nextSourceRange,
            ...(nextSettings ? { metadata: mergeExcelRangeChartMetadata(args.artifact, nextSettings) } : {}),
        },
        options: { label: derivedArtifactMutationLabel(args.artifact, 'source-change') },
    };
};

export const formatDerivedArtifactSourceRangeDraft = (
    artifact: SpreadsheetDerivedArtifact | null | undefined,
): string => (
    artifact?.source_range
        ? formatSelectionAddress({
            col: artifact.source_range.start_col,
            row: artifact.source_range.start_row,
            endCol: artifact.source_range.end_col,
            endRow: artifact.source_range.end_row,
        }, { colCount: 16_384, rowCount: 1_048_576 })
        : 'A1:B2'
);

export const buildSetDerivedArtifactSourceRangeDraftPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    sourceRangeDraft: string;
    colCount: number;
    rowCount: number;
}): DerivedArtifactMutationPlan => {
    const artifact = args.artifact;
    if (!artifact) return { kind: 'noop' };
    const selection = parseA1Selection(args.sourceRangeDraft, {
        colCount: args.colCount,
        rowCount: args.rowCount,
    });
    if (!selection) return { kind: 'noop' };
    const nextSourceRange = rangeFromUiRange(artifact.sheet_id, {
        startCol: selection.col,
        startRow: selection.row,
        endCol: selection.endCol,
        endRow: selection.endRow,
    });
    if (
        artifact.source_range.sheet_id === nextSourceRange.sheet_id &&
        artifact.source_range.start_col === nextSourceRange.start_col &&
        artifact.source_range.start_row === nextSourceRange.start_row &&
        artifact.source_range.end_col === nextSourceRange.end_col &&
        artifact.source_range.end_row === nextSourceRange.end_row
    ) return { kind: 'noop' };
    const nextSettings = artifact.kind === 'chart'
        ? buildExcelRangeChartSettings({ artifact, range: rangeAddressToUiRange(nextSourceRange) })
        : null;
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...artifact,
            source_range: nextSourceRange,
            ...(nextSettings ? { metadata: mergeExcelRangeChartMetadata(artifact, nextSettings) } : {}),
        },
        options: { label: derivedArtifactMutationLabel(artifact, 'source-change') },
    };
};

export const buildResizeDerivedArtifactSourceToCellPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    targetCol: number;
    targetRow: number;
    colCount: number;
    rowCount: number;
}): DerivedArtifactMutationPlan => {
    const artifact = args.artifact;
    if (!artifact || artifact.source_range.sheet_id !== artifact.sheet_id) return { kind: 'noop' };
    const startCol = clampDerivedArtifactIndex(artifact.source_range.start_col, args.colCount - 1);
    const startRow = clampDerivedArtifactIndex(artifact.source_range.start_row, args.rowCount - 1);
    const endCol = Math.max(startCol, clampDerivedArtifactIndex(args.targetCol, args.colCount - 1));
    const endRow = Math.max(startRow, clampDerivedArtifactIndex(args.targetRow, args.rowCount - 1));
    if (artifact.source_range.end_col === endCol && artifact.source_range.end_row === endRow) return { kind: 'noop' };
    const nextSourceRange = {
        sheet_id: artifact.source_range.sheet_id,
        start_row: startRow,
        start_col: startCol,
        end_row: endRow,
        end_col: endCol,
    };
    const nextSettings = artifact.kind === 'chart'
        ? buildExcelRangeChartSettings({ artifact, range: rangeAddressToUiRange(nextSourceRange) })
        : null;
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...artifact,
            source_range: nextSourceRange,
            ...(nextSettings ? { metadata: mergeExcelRangeChartMetadata(artifact, nextSettings) } : {}),
        },
        options: { label: derivedArtifactMutationLabel(artifact, 'source-resize') },
    };
};

export const buildMoveDerivedArtifactSourceToCellPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    targetCol: number;
    targetRow: number;
    colCount: number;
    rowCount: number;
}): DerivedArtifactMutationPlan => {
    const artifact = args.artifact;
    if (!artifact || artifact.source_range.sheet_id !== artifact.sheet_id) return { kind: 'noop' };
    const width = Math.max(1, artifact.source_range.end_col - artifact.source_range.start_col + 1);
    const height = Math.max(1, artifact.source_range.end_row - artifact.source_range.start_row + 1);
    const maxStartCol = Math.max(0, args.colCount - width);
    const maxStartRow = Math.max(0, args.rowCount - height);
    const startCol = clampDerivedArtifactIndex(args.targetCol, maxStartCol);
    const startRow = clampDerivedArtifactIndex(args.targetRow, maxStartRow);
    const endCol = Math.min(args.colCount - 1, startCol + width - 1);
    const endRow = Math.min(args.rowCount - 1, startRow + height - 1);
    if (
        artifact.source_range.start_col === startCol &&
        artifact.source_range.start_row === startRow &&
        artifact.source_range.end_col === endCol &&
        artifact.source_range.end_row === endRow
    ) return { kind: 'noop' };
    const nextSourceRange = {
        sheet_id: artifact.source_range.sheet_id,
        start_row: startRow,
        start_col: startCol,
        end_row: endRow,
        end_col: endCol,
    };
    const nextSettings = artifact.kind === 'chart'
        ? buildExcelRangeChartSettings({ artifact, range: rangeAddressToUiRange(nextSourceRange) })
        : null;
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...artifact,
            source_range: nextSourceRange,
            ...(nextSettings ? { metadata: mergeExcelRangeChartMetadata(artifact, nextSettings) } : {}),
        },
        options: { label: derivedArtifactMutationLabel(artifact, 'source-move') },
    };
};

export const buildSetDerivedArtifactChartTypePlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    chartType: SpreadsheetChartArtifactType;
}): DerivedArtifactMutationPlan => {
    const artifact = args.artifact;
    if (!artifact || artifact.kind !== 'chart' || artifact.chart_type === args.chartType) return { kind: 'noop' };
    const settings = buildExcelRangeChartSettings({ artifact, chartType: args.chartType });
    return {
        kind: 'update-derived-artifact',
        artifact: {
            ...artifact,
            chart_type: args.chartType,
            ...(settings ? { metadata: mergeExcelRangeChartMetadata(artifact, settings) } : {}),
        },
        options: { label: 'Change chart artifact type' },
    };
};

export const buildSetDerivedArtifactChartMetadataPlan = (args: {
    artifact: SpreadsheetDerivedArtifact | null | undefined;
    update: ExcelRangeChartMetadataUpdate;
}): DerivedArtifactMutationPlan => {
    const artifact = args.artifact;
    if (!artifact || artifact.kind !== 'chart') return { kind: 'noop' };
    const settings = buildExcelRangeChartSettings({ artifact, overrides: [args.update] });
    if (!settings) return { kind: 'noop' };
    const previousRecord = excelRangeChartRecordFromArtifact(artifact);
    if (JSON.stringify(settings) === JSON.stringify(previousRecord)) return { kind: 'noop' };
    return {
        kind: 'update-derived-artifact',
        artifact: { ...artifact, metadata: mergeExcelRangeChartMetadata(artifact, settings) },
        options: { label: 'Change chart artifact options' },
    };
};

export const buildDeleteDerivedArtifactPlan = (
    artifact: SpreadsheetDerivedArtifact | null | undefined,
): DerivedArtifactMutationPlan => (
    artifact ? { kind: 'delete-derived-artifact', artifactId: artifact.id } : { kind: 'noop' }
);

type DerivedArtifactChartPlotScale = {
    minValue: number;
    maxValue: number;
    maxAbsValue: number;
    totalAbsValue: number;
    valueCount: number;
    ordinalCount?: number;
    minXValue?: number;
    maxXValue?: number;
    valueAxisLogBase?: number;
    valueAxisCrosses?: ExcelRangeChartAxisCrossing;
    valueAxisCrossesAt?: number;
};

type DerivedArtifactValueAxisScale = Pick<ExcelRangeChartSettings,
    'categoryAxisKind'
    | 'valueAxisMin'
    | 'valueAxisMax'
    | 'valueAxisLogBase'
    | 'valueAxisCrosses'
    | 'valueAxisCrossesAt'
    | 'chartType'
    | 'hasSecondaryValueAxis'
    | 'secondaryValueAxisId'
    | 'seriesValueAxisIds'
    | 'seriesChartTypes'
>;

type DerivedArtifactSecondaryValueAxisScale = {
    categoryAxisKind: ExcelRangeChartSettings['categoryAxisKind'];
    valueAxisMin: ExcelRangeChartSettings['secondaryValueAxisMin'];
    valueAxisMax: ExcelRangeChartSettings['secondaryValueAxisMax'];
    valueAxisLogBase: ExcelRangeChartSettings['secondaryValueAxisLogBase'];
    valueAxisCrosses: ExcelRangeChartSettings['secondaryValueAxisCrosses'];
    valueAxisCrossesAt: ExcelRangeChartSettings['secondaryValueAxisCrossesAt'];
};

const derivedArtifactSeriesValueAxis = (
    settings: Partial<Pick<ExcelRangeChartSettings, 'hasSecondaryValueAxis' | 'secondaryValueAxisId' | 'seriesValueAxisIds'>> | null | undefined,
    seriesIndex: number,
    seriesCount: number,
): 'primary' | 'secondary' => {
    if (settings?.hasSecondaryValueAxis !== true) return 'primary';
    const secondaryAxisId = String(settings.secondaryValueAxisId ?? '').trim();
    const seriesAxisId = settings.seriesValueAxisIds?.[seriesIndex]?.trim() ?? '';
    if (secondaryAxisId && seriesAxisId) return seriesAxisId === secondaryAxisId ? 'secondary' : 'primary';
    return seriesCount > 1 && seriesIndex === seriesCount - 1 ? 'secondary' : 'primary';
};

const derivedArtifactSeriesComponentChartType = (
    settings: Partial<Pick<ExcelRangeChartSettings, 'seriesChartTypes' | 'chartType'>> | null | undefined,
    seriesIndex: number,
    fallbackChartType: SpreadsheetChartArtifactType,
): SpreadsheetChartArtifactType => (
    settings?.seriesChartTypes?.[seriesIndex] ?? settings?.chartType ?? fallbackChartType
);

const secondaryValueAxisScaleFromSettings = (
    settings: ExcelRangeChartSettings | null | undefined,
): DerivedArtifactSecondaryValueAxisScale | null => {
    if (settings?.hasSecondaryValueAxis !== true || settings.secondaryValueAxisDeleted === true) return null;
    return {
        categoryAxisKind: settings.categoryAxisKind,
        valueAxisMin: settings.secondaryValueAxisMin,
        valueAxisMax: settings.secondaryValueAxisMax,
        valueAxisLogBase: settings.secondaryValueAxisLogBase,
        valueAxisCrosses: settings.secondaryValueAxisCrosses,
        valueAxisCrossesAt: settings.secondaryValueAxisCrossesAt,
    };
};

const buildChartPlotScale = (
    values: number[],
    xValues: number[] = [],
    ordinalCount?: number,
    axisScale?: DerivedArtifactValueAxisScale | DerivedArtifactSecondaryValueAxisScale | null,
): DerivedArtifactChartPlotScale => {
    const finiteValues = values.filter((value) => Number.isFinite(value));
    const finiteXValues = xValues.filter((value) => Number.isFinite(value));
    const autoMinValue = finiteValues.length > 0 ? Math.min(...finiteValues) : 0;
    const autoMaxValue = finiteValues.length > 0 ? Math.max(...finiteValues) : 0;
    const explicitMinValue = typeof axisScale?.valueAxisMin === 'number' && Number.isFinite(axisScale.valueAxisMin)
        ? axisScale.valueAxisMin
        : null;
    const explicitMaxValue = typeof axisScale?.valueAxisMax === 'number' && Number.isFinite(axisScale.valueAxisMax)
        ? axisScale.valueAxisMax
        : null;
    const minValue = explicitMinValue ?? autoMinValue;
    const maxValue = explicitMaxValue ?? autoMaxValue;
    const hasValidValueRange = maxValue > minValue;
    const resolvedMinValue = hasValidValueRange ? minValue : autoMinValue;
    const resolvedMaxValue = hasValidValueRange ? maxValue : autoMaxValue;
    const logBase = typeof axisScale?.valueAxisLogBase === 'number' && Number.isFinite(axisScale.valueAxisLogBase) && axisScale.valueAxisLogBase > 1
        && resolvedMinValue > 0
        && resolvedMaxValue > resolvedMinValue
        ? axisScale.valueAxisLogBase
        : undefined;
    return {
        minValue: resolvedMinValue,
        maxValue: resolvedMaxValue,
        maxAbsValue: Math.max(1, Math.abs(resolvedMinValue), Math.abs(resolvedMaxValue), ...finiteValues.map((value) => Math.abs(value))),
        totalAbsValue: finiteValues.reduce((sum, value) => sum + Math.abs(value), 0),
        valueCount: finiteValues.length,
        ...(typeof ordinalCount === 'number' ? { ordinalCount } : {}),
        ...(finiteXValues.length > 0 ? { minXValue: Math.min(...finiteXValues), maxXValue: Math.max(...finiteXValues) } : {}),
        ...(logBase == null ? {} : { valueAxisLogBase: logBase }),
        ...(axisScale?.valueAxisCrosses ? { valueAxisCrosses: axisScale.valueAxisCrosses } : {}),
        ...(typeof axisScale?.valueAxisCrossesAt === 'number' && Number.isFinite(axisScale.valueAxisCrossesAt)
            ? { valueAxisCrossesAt: axisScale.valueAxisCrossesAt }
            : {}),
    };
};

const chartValuePct = (value: number, scale: DerivedArtifactChartPlotScale): number => {
    const valueRange = scale.maxValue - scale.minValue;
    if (valueRange === 0) return 0.5;
    return Math.max(0, Math.min(1, (value - scale.minValue) / valueRange));
};

const chartAxisCrossingValue = (scale: DerivedArtifactChartPlotScale): number | null => {
    if (typeof scale.valueAxisCrossesAt === 'number' && Number.isFinite(scale.valueAxisCrossesAt)) {
        return Math.max(scale.minValue, Math.min(scale.maxValue, scale.valueAxisCrossesAt));
    }
    if (scale.valueAxisCrosses === 'min') return scale.minValue;
    if (scale.valueAxisCrosses === 'max') return scale.maxValue;
    if (scale.valueAxisCrosses === 'autoZero') {
        if (scale.minValue <= 0 && scale.maxValue >= 0) return 0;
        return scale.maxValue <= 0 ? scale.maxValue : scale.minValue;
    }
    return null;
};

const buildChartBarGeometry = (
    value: number,
    scale: DerivedArtifactChartPlotScale,
): { axisPct: number; barStartPct: number; barWidthPct: number } => {
    const explicitCrossingValue = chartAxisCrossingValue(scale);
    if (explicitCrossingValue != null && scale.maxValue > scale.minValue) {
        const axisPct = Math.round(chartValuePct(explicitCrossingValue, scale) * 100);
        const valuePct = Math.round(chartValuePct(value, scale) * 100);
        return {
            axisPct,
            barStartPct: Math.min(axisPct, valuePct),
            barWidthPct: Math.max(4, Math.abs(valuePct - axisPct)),
        };
    }
    const magnitudePct = Math.max(4, Math.round((Math.abs(value) / scale.maxAbsValue) * 100));
    if (scale.minValue < 0 && scale.maxValue > 0) {
        const width = Math.max(4, Math.round((Math.abs(value) / scale.maxAbsValue) * 50));
        return {
            axisPct: 50,
            barStartPct: value < 0 ? 50 - width : 50,
            barWidthPct: width,
        };
    }
    if (scale.maxValue <= 0) {
        return {
            axisPct: 100,
            barStartPct: 100 - magnitudePct,
            barWidthPct: magnitudePct,
        };
    }
    return {
        axisPct: 0,
        barStartPct: 0,
        barWidthPct: magnitudePct,
    };
};

const chartPieSegmentBounds = (
    value: number,
    index: number,
    pointCount: number,
    totalAbs: number,
    shareOffsetPct: number,
    closeAtFullCircle: boolean,
): { sharePct: number; shareStartPct: number; shareEndPct: number; nextShareOffsetPct: number } => {
    if (totalAbs <= 0) {
        return { sharePct: 0, shareStartPct: 0, shareEndPct: 0, nextShareOffsetPct: 0 };
    }
    const share = (Math.abs(value) / totalAbs) * 100;
    const nextShareOffsetPct = shareOffsetPct + share;
    const shareStartPct = Math.round(shareOffsetPct);
    const shareEndPct = closeAtFullCircle && index === pointCount - 1 ? 100 : Math.round(nextShareOffsetPct);
    return {
        sharePct: Math.round(share),
        shareStartPct,
        shareEndPct,
        nextShareOffsetPct,
    };
};

export type DerivedArtifactPivotMeasureSummary = {
    header: string;
    total: number;
    count: number;
    average: number;
};

export type DerivedArtifactPreviewModel =
    | {
        kind: 'chart';
        chartType: SpreadsheetChartArtifactType;
        visualKind: DerivedArtifactChartPreviewVisual;
        sourceAddress: string;
        points: DerivedArtifactChartPreviewPoint[];
        totalPointCount: number;
        series: DerivedArtifactChartPreviewSeries[];
        plotPoints: DerivedArtifactChartPlotPoint[];
        maxAbsValue: number;
        minValue: number;
        maxValue: number;
        secondaryMinValue: number | null;
        secondaryMaxValue: number | null;
        totalNumericCells: number;
    }
    | {
        kind: 'pivot';
        sourceAddress: string;
        headers: string[];
        rows: string[][];
        dataRowCount: number;
        measureSummaries: DerivedArtifactPivotMeasureSummary[];
        totalMeasureCount: number;
        sourceRowCount: number;
        sourceColCount: number;
    };

export const describeDerivedArtifactPreviewFootnote = (
    previewRange: Pick<DerivedArtifactPreviewRange, 'truncatedRows' | 'truncatedCols' | 'previewAddress' | 'sourceAddress'>,
    model: DerivedArtifactPreviewModel,
    options: { maxSeriesSummaries?: number; maxPieLegendItems?: number } = {},
): string => {
    const parts: string[] = [];
    if (previewRange.truncatedRows || previewRange.truncatedCols) {
        parts.push(`Previewing ${previewRange.previewAddress} from ${previewRange.sourceAddress}.`);
    }
    if (model.kind === 'chart') {
        const maxSeriesSummaries = Math.max(1, Math.floor(options.maxSeriesSummaries ?? DERIVED_ARTIFACT_PREVIEW_MAX_SERIES_SUMMARIES));
        const maxPieLegendItems = Math.max(1, Math.floor(options.maxPieLegendItems ?? DERIVED_ARTIFACT_PREVIEW_MAX_PIE_LEGEND_ITEMS));
        const sampledPoints = model.points.length < model.totalPointCount;
        const sampledSeries = model.series.some((series) => series.points.length < series.valueCount);
        if (sampledPoints || sampledSeries) {
            parts.push(`Showing ${model.points.length} of ${model.totalPointCount} chart point${model.totalPointCount === 1 ? '' : 's'}; series totals include all bounded source values.`);
        }
        if (model.series.length > maxSeriesSummaries) {
            parts.push(`Showing ${maxSeriesSummaries} of ${model.series.length} series summaries.`);
        }
        if (model.visualKind === 'pie') {
            const hiddenPointCount = Math.max(0, model.totalPointCount - model.plotPoints.length);
            const hasVisibleSampledRemainder = hiddenPointCount > 0 && model.plotPoints.length < maxPieLegendItems;
            if (hasVisibleSampledRemainder) {
                parts.push(`Showing ${model.plotPoints.length} pie legend item${model.plotPoints.length === 1 ? '' : 's'} plus sampled remainder from ${hiddenPointCount} more.`);
            } else {
                const visibleLegendItems = Math.min(maxPieLegendItems, model.plotPoints.length);
                if (visibleLegendItems < model.totalPointCount) {
                    parts.push(`Showing ${visibleLegendItems} of ${model.totalPointCount} pie legend item${model.totalPointCount === 1 ? '' : 's'}.`);
                }
            }
        }
    } else {
        const sampledRows = model.rows.length < model.dataRowCount;
        const sampledColumns = model.headers.length < model.sourceColCount;
        const sampledMeasures = model.measureSummaries.length < model.totalMeasureCount;
        if (sampledRows || sampledColumns || sampledMeasures) {
            const sampledParts: string[] = [];
            if (sampledRows) {
                sampledParts.push(`${model.rows.length} of ${model.dataRowCount} row${model.dataRowCount === 1 ? '' : 's'}`);
            }
            if (sampledColumns) {
                sampledParts.push(`${model.headers.length} of ${model.sourceColCount} column${model.sourceColCount === 1 ? '' : 's'}`);
            }
            if (sampledMeasures) {
                sampledParts.push(`${model.measureSummaries.length} of ${model.totalMeasureCount} measure${model.totalMeasureCount === 1 ? '' : 's'}`);
            }
            parts.push(`Showing ${joinPreviewFootnoteParts(sampledParts)}.`);
        }
    }
    return parts.join(' ');
};

export const derivedArtifactAreaPolygonPoints = (
    plotPoints: Pick<DerivedArtifactChartPlotPoint, 'xPct' | 'yPct'>[],
): string => {
    if (plotPoints.length === 0) return '';
    const firstPoint = plotPoints[0];
    const lastPoint = plotPoints[plotPoints.length - 1];
    return `${firstPoint.xPct},100 ${plotPoints.map((point) => `${point.xPct},${point.yPct}`).join(' ')} ${lastPoint.xPct},100`;
};

const derivedArtifactChartSmoothPath = (
    plotPoints: Pick<DerivedArtifactChartPlotPoint, 'xPct' | 'yPct'>[],
): string => {
    if (plotPoints.length === 0) return '';
    const firstPoint = plotPoints[0]!;
    if (plotPoints.length < 3) {
        return `M ${plotPoints.map((point) => `${point.xPct} ${point.yPct}`).join(' L ')}`;
    }
    const parts = [`M ${firstPoint.xPct} ${firstPoint.yPct}`];
    for (let index = 1; index < plotPoints.length - 1; index += 1) {
        const point = plotPoints[index]!;
        const next = plotPoints[index + 1]!;
        const midX = Math.round(((point.xPct + next.xPct) / 2) * 10) / 10;
        const midY = Math.round(((point.yPct + next.yPct) / 2) * 10) / 10;
        parts.push(`Q ${point.xPct} ${point.yPct} ${midX} ${midY}`);
    }
    const lastPoint = plotPoints[plotPoints.length - 1]!;
    parts.push(`L ${lastPoint.xPct} ${lastPoint.yPct}`);
    return parts.join(' ');
};

export const formatDerivedArtifactPreviewNumber = (value: number): string => {
    if (!Number.isFinite(value)) return '';
    const abs = Math.abs(value);
    if (abs >= 1000) return Math.round(value).toLocaleString('en-US');
    if (Number.isInteger(value)) return String(value);
    return value.toFixed(abs >= 10 ? 1 : 2).replace(/\.?0+$/, '');
};

const excelChartNumberFormatSection = (value: number, formatCode: string): string => {
    const sections = formatCode.split(';');
    if (value < 0 && sections[1]) return sections[1]!;
    if (value === 0 && sections[2]) return sections[2]!;
    return sections[0] ?? formatCode;
};

const decimalPlacesForExcelChartNumberFormat = (formatCode: string): number | null => {
    const decimalPart = formatCode.split('.')[1] ?? '';
    const significantPart = decimalPart.split(/[^\d#0?]/)[0] ?? '';
    if (!formatCode.includes('.') || !significantPart) return null;
    return Math.min(6, Math.max(0, (significantPart.match(/[0#?]/g) ?? []).length));
};

const formatDerivedArtifactChartNumber = (
    value: number,
    formatCode?: string | null,
): string => {
    if (!Number.isFinite(value)) return '';
    const normalizedFormat = String(formatCode ?? '').trim();
    if (!normalizedFormat || /^general$/i.test(normalizedFormat)) return formatDerivedArtifactPreviewNumber(value);
    const formatSection = excelChartNumberFormatSection(value, normalizedFormat);
    const isPercent = formatSection.includes('%');
    const isCurrency = formatSection.includes('$');
    const useThousandsSeparator = /[#0?],[#0?]/.test(formatSection);
    const decimalPlaces = decimalPlacesForExcelChartNumberFormat(formatSection)
        ?? (isPercent ? 0 : null);
    const scaledValue = Math.abs(isPercent ? value * 100 : value);
    const formattedNumber = scaledValue.toLocaleString('en-US', {
        useGrouping: useThousandsSeparator,
        minimumFractionDigits: decimalPlaces ?? 0,
        maximumFractionDigits: decimalPlaces ?? (scaledValue >= 100 ? 0 : 2),
    });
    const wrapped = `${isCurrency ? '$' : ''}${formattedNumber}${isPercent ? '%' : ''}`;
    if (value < 0 && formatSection.includes('(') && formatSection.includes(')')) return `(${wrapped})`;
    return value < 0 ? `-${wrapped}` : wrapped;
};

const derivedArtifactChartValueAxisDisplayUnitDivisor = (
    settings?: Partial<Pick<ExcelRangeChartSettings, 'valueAxisDisplayUnit' | 'valueAxisDisplayUnitCustom'>> | null,
): number => {
    switch (settings?.valueAxisDisplayUnit) {
        case 'hundreds':
            return 100;
        case 'thousands':
            return 1_000;
        case 'tenThousands':
            return 10_000;
        case 'hundredThousands':
            return 100_000;
        case 'millions':
            return 1_000_000;
        case 'tenMillions':
            return 10_000_000;
        case 'hundredMillions':
            return 100_000_000;
        case 'billions':
            return 1_000_000_000;
        case 'trillions':
            return 1_000_000_000_000;
        case 'custom':
            return Number.isFinite(settings?.valueAxisDisplayUnitCustom) && Number(settings?.valueAxisDisplayUnitCustom) > 0
                ? Number(settings?.valueAxisDisplayUnitCustom)
                : 1;
        default:
            return 1;
    }
};

const formatDerivedArtifactChartValueNumber = (
    value: number,
    settings?: Partial<Pick<ExcelRangeChartSettings, 'valueAxisNumberFormat' | 'valueAxisDisplayUnit' | 'valueAxisDisplayUnitCustom'>> | null,
): string => {
    const divisor = derivedArtifactChartValueAxisDisplayUnitDivisor(settings);
    return formatDerivedArtifactChartNumber(value / divisor, settings?.valueAxisNumberFormat);
};

const formatDerivedArtifactChartSecondaryValueNumber = (
    value: number,
    settings?: Partial<Pick<ExcelRangeChartSettings, 'secondaryValueAxisNumberFormat' | 'secondaryValueAxisDisplayUnit' | 'secondaryValueAxisDisplayUnitCustom'>> | null,
): string => {
    const divisor = derivedArtifactChartValueAxisDisplayUnitDivisor({
        valueAxisDisplayUnit: settings?.secondaryValueAxisDisplayUnit,
        valueAxisDisplayUnitCustom: settings?.secondaryValueAxisDisplayUnitCustom,
    });
    return formatDerivedArtifactChartNumber(value / divisor, settings?.secondaryValueAxisNumberFormat);
};

const EXCEL_CHART_MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const EXCEL_CHART_MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatDerivedArtifactChartDateNumber = (
    value: number,
    formatCode?: string | null,
): string | null => {
    if (!Number.isFinite(value)) return null;
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return null;
    const formatSection = excelChartNumberFormatSection(value, String(formatCode ?? '').trim() || 'yyyy-mm-dd');
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    return formatSection.replace(/yyyy|yy|mmmm|mmm|mm|m|dd|d/gi, (token) => {
        switch (token.toLowerCase()) {
            case 'yyyy':
                return String(year).padStart(4, '0');
            case 'yy':
                return String(year % 100).padStart(2, '0');
            case 'mmmm':
                return EXCEL_CHART_MONTH_LONG[month - 1] ?? String(month);
            case 'mmm':
                return EXCEL_CHART_MONTH_SHORT[month - 1] ?? String(month);
            case 'mm':
                return String(month).padStart(2, '0');
            case 'm':
                return String(month);
            case 'dd':
                return String(day).padStart(2, '0');
            case 'd':
                return String(day);
            default:
                return token;
        }
    });
};

const formatDerivedArtifactChartCategoryLabel = (
    point: Pick<DerivedArtifactChartPlotPoint, 'label' | 'xValue'>,
    settings?: Partial<Pick<ExcelRangeChartSettings, 'categoryAxisKind' | 'categoryAxisNumberFormat' | 'categoryAxisNumberFormatLinkedToSource'>> | null,
): string => {
    const formatCode = String(settings?.categoryAxisNumberFormat ?? '').trim();
    if (!formatCode) return point.label;
    const xValue = typeof point.xValue === 'number' && Number.isFinite(point.xValue) ? point.xValue : null;
    if (xValue == null) return point.label;
    if (settings?.categoryAxisKind === 'date') {
        return formatDerivedArtifactChartDateNumber(xValue, formatCode) ?? point.label;
    }
    return formatDerivedArtifactChartNumber(xValue, formatCode);
};

const derivedArtifactChartColorWithOpacity = (color: string, opacity: number | null): string => {
    if (opacity == null) return `${color}22`;
    const match = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(color);
    if (!match) return `${color}22`;
    const alpha = Math.round(opacity * 1000) / 1000;
    return `rgba(${Number.parseInt(match[1]!, 16)}, ${Number.parseInt(match[2]!, 16)}, ${Number.parseInt(match[3]!, 16)}, ${alpha})`;
};

const derivedArtifactChartShapeFillBackground = (
    fillColor: string | null | undefined,
    fillOpacity: number | null | undefined,
    fillPattern: ExcelRangeChartFillPattern | null | undefined,
    fillForegroundColor: string | null | undefined,
    fillBackgroundColor: string | null | undefined,
    fallback: string,
): string => {
    const color = chartColorFromUnknown(fillColor);
    const foreground = chartColorFromUnknown(fillForegroundColor) ?? color ?? '#94a3b8';
    const background = chartColorFromUnknown(fillBackgroundColor) ?? color;
    const opacity = chartOpacityFromUnknown(fillOpacity);
    const base = background ? derivedArtifactChartColorWithOpacity(background, opacity ?? 1) : fallback;
    switch (fillPattern) {
        case 'horizontal':
            return `repeating-linear-gradient(0deg, ${base} 0 5px, ${foreground} 5px 6px)`;
        case 'vertical':
            return `repeating-linear-gradient(90deg, ${base} 0 5px, ${foreground} 5px 6px)`;
        case 'diagonalDown':
            return `repeating-linear-gradient(45deg, ${base} 0 5px, ${foreground} 5px 6px)`;
        case 'diagonalUp':
            return `repeating-linear-gradient(135deg, ${base} 0 5px, ${foreground} 5px 6px)`;
        case 'cross':
            return `repeating-linear-gradient(0deg, transparent 0 5px, ${foreground} 5px 6px), repeating-linear-gradient(90deg, ${base} 0 5px, ${foreground} 5px 6px)`;
        case 'diagonalCross':
            return `repeating-linear-gradient(45deg, transparent 0 5px, ${foreground} 5px 6px), repeating-linear-gradient(135deg, ${base} 0 5px, ${foreground} 5px 6px)`;
        case 'percent':
            return `radial-gradient(circle at 50% 50%, ${foreground} 0 1px, transparent 1.2px), ${base}`;
        default:
            return color ? derivedArtifactChartColorWithOpacity(color, opacity ?? 1) : fallback;
    }
};

const derivedArtifactChartShapeStyle = (
    settings: Partial<ExcelRangeChartPresentationSettings> | null | undefined,
    shape: 'chartArea' | 'plotArea',
): DerivedArtifactChartShapeStyle => {
    const chartArea = shape === 'chartArea';
    const fallbackBackground = chartArea ? 'transparent' : 'rgba(255, 255, 255, 0.58)';
    const background = derivedArtifactChartShapeFillBackground(
        chartArea ? settings?.chartAreaFillColor : settings?.plotAreaFillColor,
        chartArea ? settings?.chartAreaFillOpacity : settings?.plotAreaFillOpacity,
        chartArea ? settings?.chartAreaFillPattern : settings?.plotAreaFillPattern,
        chartArea ? settings?.chartAreaFillForegroundColor : settings?.plotAreaFillForegroundColor,
        chartArea ? settings?.chartAreaFillBackgroundColor : settings?.plotAreaFillBackgroundColor,
        fallbackBackground,
    );
    return {
        background,
        borderColor: chartColorFromUnknown(chartArea ? settings?.chartAreaLineColor : settings?.plotAreaLineColor) ?? 'transparent',
        borderWidth: Math.max(0, chartNumberFromUnknown(chartArea ? settings?.chartAreaLineWidth : settings?.plotAreaLineWidth) ?? 0),
        borderRadius: chartArea && settings?.chartRoundedCorners === true ? 8 : 4,
    };
};

const derivedArtifactChartTextStyle = (
    settings: Partial<ExcelRangeChartPresentationSettings> | null | undefined,
    prefix: 'chartTitle' | 'categoryAxisTitle' | 'valueAxisTitle' | 'secondaryValueAxisTitle',
    defaults: Pick<DerivedArtifactChartTextStyle, 'color' | 'fontSize' | 'fontWeight'>,
): DerivedArtifactChartTextStyle => {
    const color = chartColorFromUnknown(settings?.[`${prefix}FontColor`]) ?? defaults.color;
    const fontSize = finiteNumberFromRecord({ value: settings?.[`${prefix}FontSize`] }, 'value');
    const bold = settings?.[`${prefix}FontBold`];
    const italic = settings?.[`${prefix}FontItalic`];
    const textAutoFit = chartTextAutoFitFromUnknown(settings?.[`${prefix}TextAutoFit`]);
    const textAutoFitFontScale = textAutoFit === 'normal'
        ? Math.max(20, Math.min(100, finiteNumberFromRecord({ value: settings?.[`${prefix}TextAutoFitFontScale`] }, 'value') ?? 100))
        : null;
    const baseFontSize = fontSize == null ? defaults.fontSize : Math.max(6, Math.min(20, Math.round(fontSize * 10) / 10));
    return {
        color,
        fontSize: Math.max(6, Math.min(20, Math.round((baseFontSize * ((textAutoFitFontScale ?? 100) / 100)) * 10) / 10)),
        fontWeight: bold === false ? Math.min(defaults.fontWeight, 700) : defaults.fontWeight,
        fontStyle: italic === true ? 'italic' : 'normal',
        rotationDeg: Math.max(-180, Math.min(180, finiteNumberFromRecord({ value: settings?.[`${prefix}TextRotation`] }, 'value') ?? 0)),
        textAlign: chartTextAlignFromUnknown(settings?.[`${prefix}TextAlign`]) ?? 'center',
        textVerticalAlign: chartTextVerticalAlignFromUnknown(settings?.[`${prefix}TextVerticalAlign`]) ?? 'middle',
        textWrap: settings?.[`${prefix}TextWrap`] === true,
        textAutoFit,
        textAutoFitFontScale,
        textHorizontalOverflow: chartTextHorizontalOverflowFromUnknown(settings?.[`${prefix}TextHorizontalOverflow`]),
        textVerticalOverflow: chartTextVerticalOverflowFromUnknown(settings?.[`${prefix}TextVerticalOverflow`]),
        textInsetTop: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.[`${prefix}TextInsetTop`] }, 'value') ?? 0)),
        textInsetRight: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.[`${prefix}TextInsetRight`] }, 'value') ?? 0)),
        textInsetBottom: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.[`${prefix}TextInsetBottom`] }, 'value') ?? 0)),
        textInsetLeft: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.[`${prefix}TextInsetLeft`] }, 'value') ?? 0)),
    };
};

export const derivedArtifactChartSeriesSummaryItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    maxItems = DERIVED_ARTIFACT_PREVIEW_MAX_SERIES_SUMMARIES,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    styles?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartSeriesSummaryItem[] => {
    const itemLimit = Math.max(0, Math.floor(maxItems));
    if (itemLimit === 0 || colors.length === 0) return [];
    return model.series.slice(0, itemLimit).map((series, index) => {
        const lineColor = chartColorFromUnknown(styles?.seriesLineColors?.[index]);
        const lineWidth = chartNumberFromUnknown(styles?.seriesLineWidths?.[index]);
        const markerSize = chartNumberFromUnknown(styles?.seriesMarkerSizes?.[index]);
        const markerShape = chartMarkerShapeFromUnknown(styles?.seriesMarkerShapes?.[index]);
        const markerFillColor = chartColorFromUnknown(styles?.seriesMarkerFillColors?.[index]);
        const markerLineColor = chartColorFromUnknown(styles?.seriesMarkerLineColors?.[index]);
        const lineDash = chartLineDashFromUnknown(styles?.seriesLineDashes?.[index]);
        const fillOpacity = chartOpacityFromUnknown(styles?.seriesFillOpacities?.[index]);
        const fillPattern = chartFillPatternFromUnknown(styles?.seriesFillPatterns?.[index]);
        const fillForegroundColor = chartColorFromUnknown(styles?.seriesFillForegroundColors?.[index]);
        const fillBackgroundColor = chartColorFromUnknown(styles?.seriesFillBackgroundColors?.[index]);
        const displayTotal = series.valueAxis === 'secondary'
            ? formatDerivedArtifactChartSecondaryValueNumber(series.total, styles)
            : formatDerivedArtifactChartValueNumber(series.total, styles);
        return {
            ...series,
            color: colors[index % colors.length],
            displayTotal,
            title: `${series.name}: ${series.valueAxis === 'secondary' ? 'secondary axis ' : ''}total ${displayTotal} from ${series.valueCount} value${series.valueCount === 1 ? '' : 's'}${series.points.length < series.valueCount ? `, showing ${series.points.length}` : ''}.`,
            ...(lineColor == null ? {} : { lineColor }),
            ...(lineWidth == null ? {} : { lineWidth }),
            ...(markerSize == null ? {} : { pointRadius: markerSize / 2 }),
            ...(markerShape == null ? {} : { markerShape }),
            ...(markerFillColor == null ? {} : { markerFillColor }),
            ...(markerLineColor == null ? {} : { markerLineColor }),
            ...(lineDash == null ? {} : { lineDash }),
            ...(fillOpacity == null ? {} : { fillOpacity }),
            ...(fillPattern == null ? {} : { fillPattern }),
            ...(fillForegroundColor == null ? {} : { fillForegroundColor }),
            ...(fillBackgroundColor == null ? {} : { fillBackgroundColor }),
        };
    });
};

export const derivedArtifactChartSeriesPreviewItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    styles?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartSeriesSummaryItem[] => derivedArtifactChartSeriesSummaryItems(model, model.series.length, colors, styles);

const derivedArtifactChartPointMarkerStyle = (
    styles: ExcelRangeChartSeriesStyleSettings | null | undefined,
    seriesIndex: number,
    pointIndex: number,
): Pick<DerivedArtifactChartPlotPoint, 'markerFillColor' | 'markerLineColor' | 'markerFillOpacity' | 'markerFillPattern' | 'markerFillForegroundColor' | 'markerFillBackgroundColor'> => {
    const pointIndexes = styles?.seriesPointIndexes?.[seriesIndex] ?? [];
    const styleIndex = pointIndexes.findIndex((index) => index === pointIndex);
    if (styleIndex < 0) return {};
    const markerFillColor = chartColorFromUnknown(styles?.seriesPointColors?.[seriesIndex]?.[styleIndex])
        ?? chartColorFromUnknown(styles?.seriesPointFillForegroundColors?.[seriesIndex]?.[styleIndex]);
    const markerLineColor = chartColorFromUnknown(styles?.seriesPointLineColors?.[seriesIndex]?.[styleIndex]);
    const markerFillOpacity = chartOpacityFromUnknown(styles?.seriesPointFillOpacities?.[seriesIndex]?.[styleIndex]);
    const markerFillPattern = chartFillPatternFromUnknown(styles?.seriesPointFillPatterns?.[seriesIndex]?.[styleIndex]);
    const markerFillForegroundColor = chartColorFromUnknown(styles?.seriesPointFillForegroundColors?.[seriesIndex]?.[styleIndex]);
    const markerFillBackgroundColor = chartColorFromUnknown(styles?.seriesPointFillBackgroundColors?.[seriesIndex]?.[styleIndex]);
    return {
        ...(markerFillColor == null ? {} : { markerFillColor }),
        ...(markerLineColor == null ? {} : { markerLineColor }),
        ...(markerFillOpacity == null ? {} : { markerFillOpacity }),
        ...(markerFillPattern == null ? {} : { markerFillPattern }),
        ...(markerFillForegroundColor == null ? {} : { markerFillForegroundColor }),
        ...(markerFillBackgroundColor == null ? {} : { markerFillBackgroundColor }),
    };
};

type ExcelRangeChartDataLabelSettings = Pick<ExcelRangeChartSettings,
    'showDataLabels'
    | 'dataLabelPosition'
    | 'dataLabelNumberFormat'
    | 'dataLabelSeparator'
    | 'dataLabelFontColor'
    | 'dataLabelFontSize'
    | 'dataLabelFontBold'
    | 'dataLabelFontItalic'
    | 'dataLabelTextRotation'
    | 'dataLabelTextAlign'
    | 'dataLabelTextVerticalAlign'
    | 'dataLabelTextWrap'
    | 'dataLabelTextAutoFit'
    | 'dataLabelTextAutoFitFontScale'
    | 'dataLabelTextHorizontalOverflow'
    | 'dataLabelTextVerticalOverflow'
    | 'dataLabelTextInsetTop'
    | 'dataLabelTextInsetRight'
    | 'dataLabelTextInsetBottom'
    | 'dataLabelTextInsetLeft'
    | 'dataLabelShowValue'
    | 'dataLabelShowCategoryName'
    | 'dataLabelShowSeriesName'
    | 'dataLabelShowPercent'
    | 'dataLabelShowLeaderLines'
    | 'pieFirstSliceAngle'
    | 'seriesDataLabelPositions'
    | 'seriesDataLabelNumberFormats'
    | 'seriesDataLabelSeparators'
    | 'seriesDataLabelFontColors'
    | 'seriesDataLabelFontSizes'
    | 'seriesDataLabelFontBolds'
    | 'seriesDataLabelFontItalics'
    | 'seriesDataLabelTextRotations'
    | 'seriesDataLabelTextAligns'
    | 'seriesDataLabelTextVerticalAligns'
    | 'seriesDataLabelTextWraps'
    | 'seriesDataLabelTextAutoFits'
    | 'seriesDataLabelTextAutoFitFontScales'
    | 'seriesDataLabelTextHorizontalOverflows'
    | 'seriesDataLabelTextVerticalOverflows'
    | 'seriesDataLabelTextInsetTops'
    | 'seriesDataLabelTextInsetRights'
    | 'seriesDataLabelTextInsetBottoms'
    | 'seriesDataLabelTextInsetLefts'
    | 'seriesDataLabelShowValues'
    | 'seriesDataLabelShowCategoryNames'
    | 'seriesDataLabelShowSeriesNames'
    | 'seriesDataLabelShowPercents'
    | 'seriesDataLabelShowLeaderLines'
>;

type ExcelRangeChartPresentationSettings = Pick<ExcelRangeChartSettings, 'seriesColors'>
    & ExcelRangeChartSeriesStyleSettings
    & Partial<Pick<ExcelRangeChartSettings,
        'chartStyleId'
        | 'chartTitleFontColor'
        | 'chartTitleFontSize'
        | 'chartTitleFontBold'
        | 'chartTitleFontItalic'
        | 'chartTitleTextRotation'
        | 'chartTitleTextAlign'
        | 'chartTitleTextVerticalAlign'
        | 'chartTitleTextWrap'
        | 'chartTitleTextAutoFit'
        | 'chartTitleTextAutoFitFontScale'
        | 'chartTitleTextHorizontalOverflow'
        | 'chartTitleTextVerticalOverflow'
        | 'chartTitleTextInsetTop'
        | 'chartTitleTextInsetRight'
        | 'chartTitleTextInsetBottom'
        | 'chartTitleTextInsetLeft'
        | 'chartRoundedCorners'
        | 'chartAreaFillColor'
        | 'chartAreaFillOpacity'
        | 'chartAreaLineColor'
        | 'chartAreaLineWidth'
        | 'chartAreaFillPattern'
        | 'chartAreaFillForegroundColor'
        | 'chartAreaFillBackgroundColor'
        | 'plotAreaFillColor'
        | 'plotAreaFillOpacity'
        | 'plotAreaLineColor'
        | 'plotAreaLineWidth'
        | 'plotAreaFillPattern'
        | 'plotAreaFillForegroundColor'
        | 'plotAreaFillBackgroundColor'
        | 'categoryAxisKind'
        | 'categoryAxisTitleFontColor'
        | 'categoryAxisTitleFontSize'
        | 'categoryAxisTitleFontBold'
        | 'categoryAxisTitleFontItalic'
        | 'categoryAxisTitleTextRotation'
        | 'categoryAxisTitleTextAlign'
        | 'categoryAxisTitleTextVerticalAlign'
        | 'categoryAxisTitleTextWrap'
        | 'categoryAxisTitleTextAutoFit'
        | 'categoryAxisTitleTextAutoFitFontScale'
        | 'categoryAxisTitleTextHorizontalOverflow'
        | 'categoryAxisTitleTextVerticalOverflow'
        | 'categoryAxisTitleTextInsetTop'
        | 'categoryAxisTitleTextInsetRight'
        | 'categoryAxisTitleTextInsetBottom'
        | 'categoryAxisTitleTextInsetLeft'
        | 'categoryAxisNumberFormat'
        | 'categoryAxisNumberFormatLinkedToSource'
        | 'categoryAxisHasMajorGridlines'
        | 'categoryAxisHasMinorGridlines'
        | 'categoryAxisDeleted'
        | 'showCategoryAxis'
        | 'categoryAxisMajorTickMark'
        | 'categoryAxisMinorTickMark'
        | 'categoryAxisTickLabelPosition'
        | 'valueAxisNumberFormat'
        | 'valueAxisTitleFontColor'
        | 'valueAxisTitleFontSize'
        | 'valueAxisTitleFontBold'
        | 'valueAxisTitleFontItalic'
        | 'valueAxisTitleTextRotation'
        | 'valueAxisTitleTextAlign'
        | 'valueAxisTitleTextVerticalAlign'
        | 'valueAxisTitleTextWrap'
        | 'valueAxisTitleTextAutoFit'
        | 'valueAxisTitleTextAutoFitFontScale'
        | 'valueAxisTitleTextHorizontalOverflow'
        | 'valueAxisTitleTextVerticalOverflow'
        | 'valueAxisTitleTextInsetTop'
        | 'valueAxisTitleTextInsetRight'
        | 'valueAxisTitleTextInsetBottom'
        | 'valueAxisTitleTextInsetLeft'
        | 'valueAxisDisplayUnit'
        | 'valueAxisDisplayUnitCustom'
        | 'valueAxisHasMajorGridlines'
        | 'valueAxisHasMinorGridlines'
        | 'valueAxisDeleted'
        | 'showValueAxis'
        | 'valueAxisMajorTickMark'
        | 'valueAxisMinorTickMark'
        | 'valueAxisTickLabelPosition'
        | 'chartGrouping'
        | 'barGapWidth'
        | 'barOverlap'
        | 'hasSecondaryValueAxis'
        | 'secondaryValueAxisTitle'
        | 'secondaryValueAxisTitleFontColor'
        | 'secondaryValueAxisTitleFontSize'
        | 'secondaryValueAxisTitleFontBold'
        | 'secondaryValueAxisTitleFontItalic'
        | 'secondaryValueAxisTitleTextRotation'
        | 'secondaryValueAxisTitleTextAlign'
        | 'secondaryValueAxisTitleTextVerticalAlign'
        | 'secondaryValueAxisTitleTextWrap'
        | 'secondaryValueAxisTitleTextAutoFit'
        | 'secondaryValueAxisTitleTextAutoFitFontScale'
        | 'secondaryValueAxisTitleTextHorizontalOverflow'
        | 'secondaryValueAxisTitleTextVerticalOverflow'
        | 'secondaryValueAxisTitleTextInsetTop'
        | 'secondaryValueAxisTitleTextInsetRight'
        | 'secondaryValueAxisTitleTextInsetBottom'
        | 'secondaryValueAxisTitleTextInsetLeft'
        | 'secondaryValueAxisNumberFormat'
        | 'secondaryValueAxisDisplayUnit'
        | 'secondaryValueAxisDisplayUnitCustom'
        | 'secondaryValueAxisHasMajorGridlines'
        | 'secondaryValueAxisHasMinorGridlines'
        | 'secondaryValueAxisDeleted'
        | 'secondaryValueAxisMajorTickMark'
        | 'secondaryValueAxisMinorTickMark'
        | 'secondaryValueAxisTickLabelPosition'
        | 'seriesChartTypes'
        | 'chartVaryColors'
        | 'lineSmooth'
    >>
    & Partial<ExcelRangeChartDataLabelSettings>;

const clampDerivedArtifactChartLabelPct = (value: number): number => (
    Math.round(Math.max(4, Math.min(96, value)) * 10) / 10
);

const derivedArtifactChartDataLabelParts = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    point: Pick<DerivedArtifactChartPlotPoint, 'label' | 'value' | 'sharePct'>,
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
    seriesName?: string,
): string[] => {
    const explicitContent = settings?.dataLabelShowValue === true
        || settings?.dataLabelShowCategoryName === true
        || settings?.dataLabelShowSeriesName === true
        || settings?.dataLabelShowPercent === true;
    const parts: string[] = [];
    if (settings?.dataLabelShowSeriesName === true) {
        parts.push(seriesName || (model.series.length === 1 ? model.series[0]?.name || 'Series' : 'Total'));
    }
    if (settings?.dataLabelShowCategoryName === true) {
        parts.push(point.label);
    }
    if (settings?.dataLabelShowValue === true || !explicitContent) {
        parts.push(formatDerivedArtifactChartNumber(point.value, settings?.dataLabelNumberFormat));
    }
    if (settings?.dataLabelShowPercent === true) {
        parts.push(settings?.dataLabelNumberFormat
            ? formatDerivedArtifactChartNumber(point.sharePct / 100, settings.dataLabelNumberFormat)
            : `${point.sharePct}%`);
    }
    return parts.filter((part) => part.trim());
};

const hasExplicitDataLabelSettings = (
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
): boolean => Boolean(
    settings?.dataLabelPosition != null
    || (settings?.dataLabelNumberFormat && settings.dataLabelNumberFormat.trim())
    || (settings?.dataLabelFontColor && settings.dataLabelFontColor.trim())
    || settings?.dataLabelFontSize != null
    || settings?.dataLabelFontBold != null
    || settings?.dataLabelFontItalic != null
    || settings?.dataLabelTextRotation != null
    || settings?.dataLabelTextAlign != null
    || settings?.dataLabelTextVerticalAlign != null
    || settings?.dataLabelTextWrap != null
    || settings?.dataLabelTextAutoFit != null
    || settings?.dataLabelTextAutoFitFontScale != null
    || settings?.dataLabelTextHorizontalOverflow != null
    || settings?.dataLabelTextVerticalOverflow != null
    || settings?.dataLabelTextInsetTop != null
    || settings?.dataLabelTextInsetRight != null
    || settings?.dataLabelTextInsetBottom != null
    || settings?.dataLabelTextInsetLeft != null
    || settings?.dataLabelShowValue != null
    || settings?.dataLabelShowCategoryName != null
    || settings?.dataLabelShowSeriesName != null
    || settings?.dataLabelShowPercent != null
    || settings?.dataLabelShowLeaderLines != null
);

const hasExplicitDataLabelSettingsForSeries = (
    settings: Partial<ExcelRangeChartDataLabelSettings>,
    seriesIndex: number,
): boolean => Boolean(
    settings.seriesDataLabelPositions?.[seriesIndex] != null
    || (settings.seriesDataLabelNumberFormats?.[seriesIndex] && settings.seriesDataLabelNumberFormats[seriesIndex]!.trim())
    || (settings.seriesDataLabelSeparators?.[seriesIndex] && settings.seriesDataLabelSeparators[seriesIndex]!.trim())
    || (settings.seriesDataLabelFontColors?.[seriesIndex] && settings.seriesDataLabelFontColors[seriesIndex]!.trim())
    || settings.seriesDataLabelFontSizes?.[seriesIndex] != null
    || settings.seriesDataLabelFontBolds?.[seriesIndex] != null
    || settings.seriesDataLabelFontItalics?.[seriesIndex] != null
    || settings.seriesDataLabelTextRotations?.[seriesIndex] != null
    || settings.seriesDataLabelTextAligns?.[seriesIndex] != null
    || settings.seriesDataLabelTextVerticalAligns?.[seriesIndex] != null
    || settings.seriesDataLabelTextWraps?.[seriesIndex] != null
    || settings.seriesDataLabelTextAutoFits?.[seriesIndex] != null
    || settings.seriesDataLabelTextAutoFitFontScales?.[seriesIndex] != null
    || settings.seriesDataLabelTextHorizontalOverflows?.[seriesIndex] != null
    || settings.seriesDataLabelTextVerticalOverflows?.[seriesIndex] != null
    || settings.seriesDataLabelTextInsetTops?.[seriesIndex] != null
    || settings.seriesDataLabelTextInsetRights?.[seriesIndex] != null
    || settings.seriesDataLabelTextInsetBottoms?.[seriesIndex] != null
    || settings.seriesDataLabelTextInsetLefts?.[seriesIndex] != null
    || settings.seriesDataLabelShowValues?.[seriesIndex] != null
    || settings.seriesDataLabelShowCategoryNames?.[seriesIndex] != null
    || settings.seriesDataLabelShowSeriesNames?.[seriesIndex] != null
    || settings.seriesDataLabelShowPercents?.[seriesIndex] != null
    || settings.seriesDataLabelShowLeaderLines?.[seriesIndex] != null
);

const hasExplicitSeriesDataLabelSettings = (
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
): boolean => Boolean(
    settings?.seriesDataLabelPositions?.some((position) => position != null)
    || settings?.seriesDataLabelNumberFormats?.some((format) => Boolean(format && format.trim()))
    || settings?.seriesDataLabelSeparators?.some((separator) => Boolean(separator && separator.trim()))
    || settings?.seriesDataLabelFontColors?.some((color) => Boolean(color && color.trim()))
    || settings?.seriesDataLabelFontSizes?.some((size) => size != null)
    || settings?.seriesDataLabelFontBolds?.some((entry) => entry != null)
    || settings?.seriesDataLabelFontItalics?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextRotations?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextAligns?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextVerticalAligns?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextWraps?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextAutoFits?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextAutoFitFontScales?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextHorizontalOverflows?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextVerticalOverflows?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextInsetTops?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextInsetRights?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextInsetBottoms?.some((entry) => entry != null)
    || settings?.seriesDataLabelTextInsetLefts?.some((entry) => entry != null)
    || settings?.seriesDataLabelShowValues?.some((entry) => entry != null)
    || settings?.seriesDataLabelShowCategoryNames?.some((entry) => entry != null)
    || settings?.seriesDataLabelShowSeriesNames?.some((entry) => entry != null)
    || settings?.seriesDataLabelShowPercents?.some((entry) => entry != null)
    || settings?.seriesDataLabelShowLeaderLines?.some((entry) => entry != null)
);

const dataLabelSettingsForSeries = (
    settings: Partial<ExcelRangeChartDataLabelSettings>,
    seriesIndex: number,
): Partial<ExcelRangeChartDataLabelSettings> => ({
    showDataLabels: settings.showDataLabels,
    dataLabelPosition: settings.seriesDataLabelPositions?.[seriesIndex] ?? settings.dataLabelPosition ?? null,
    dataLabelNumberFormat: settings.seriesDataLabelNumberFormats?.[seriesIndex] || settings.dataLabelNumberFormat || null,
    dataLabelSeparator: settings.seriesDataLabelSeparators?.[seriesIndex] || settings.dataLabelSeparator || ', ',
    dataLabelFontColor: settings.seriesDataLabelFontColors?.[seriesIndex] || settings.dataLabelFontColor || '',
    dataLabelFontSize: (settings.seriesDataLabelFontSizes?.[seriesIndex] ?? 0) > 0
        ? settings.seriesDataLabelFontSizes![seriesIndex]!
        : settings.dataLabelFontSize ?? null,
    dataLabelFontBold: settings.seriesDataLabelFontBolds?.[seriesIndex] ?? settings.dataLabelFontBold ?? null,
    dataLabelFontItalic: settings.seriesDataLabelFontItalics?.[seriesIndex] ?? settings.dataLabelFontItalic ?? null,
    dataLabelTextRotation: settings.seriesDataLabelTextRotations?.[seriesIndex] ?? settings.dataLabelTextRotation ?? null,
    dataLabelTextAlign: settings.seriesDataLabelTextAligns?.[seriesIndex] ?? settings.dataLabelTextAlign ?? null,
    dataLabelTextVerticalAlign: settings.seriesDataLabelTextVerticalAligns?.[seriesIndex] ?? settings.dataLabelTextVerticalAlign ?? null,
    dataLabelTextWrap: settings.seriesDataLabelTextWraps?.[seriesIndex] ?? settings.dataLabelTextWrap ?? null,
    dataLabelTextAutoFit: settings.seriesDataLabelTextAutoFits?.[seriesIndex] ?? settings.dataLabelTextAutoFit ?? null,
    dataLabelTextAutoFitFontScale: (settings.seriesDataLabelTextAutoFitFontScales?.[seriesIndex] ?? 0) > 0
        ? settings.seriesDataLabelTextAutoFitFontScales![seriesIndex]!
        : settings.dataLabelTextAutoFitFontScale ?? null,
    dataLabelTextHorizontalOverflow: settings.seriesDataLabelTextHorizontalOverflows?.[seriesIndex] ?? settings.dataLabelTextHorizontalOverflow ?? null,
    dataLabelTextVerticalOverflow: settings.seriesDataLabelTextVerticalOverflows?.[seriesIndex] ?? settings.dataLabelTextVerticalOverflow ?? null,
    dataLabelTextInsetTop: settings.seriesDataLabelTextInsetTops?.[seriesIndex] ?? settings.dataLabelTextInsetTop ?? null,
    dataLabelTextInsetRight: settings.seriesDataLabelTextInsetRights?.[seriesIndex] ?? settings.dataLabelTextInsetRight ?? null,
    dataLabelTextInsetBottom: settings.seriesDataLabelTextInsetBottoms?.[seriesIndex] ?? settings.dataLabelTextInsetBottom ?? null,
    dataLabelTextInsetLeft: settings.seriesDataLabelTextInsetLefts?.[seriesIndex] ?? settings.dataLabelTextInsetLeft ?? null,
    dataLabelShowValue: settings.seriesDataLabelShowValues?.[seriesIndex] ?? settings.dataLabelShowValue ?? null,
    dataLabelShowCategoryName: settings.seriesDataLabelShowCategoryNames?.[seriesIndex] ?? settings.dataLabelShowCategoryName ?? null,
    dataLabelShowSeriesName: settings.seriesDataLabelShowSeriesNames?.[seriesIndex] ?? settings.dataLabelShowSeriesName ?? null,
    dataLabelShowPercent: settings.seriesDataLabelShowPercents?.[seriesIndex] ?? settings.dataLabelShowPercent ?? null,
    dataLabelShowLeaderLines: settings.seriesDataLabelShowLeaderLines?.[seriesIndex] ?? settings.dataLabelShowLeaderLines ?? null,
    pieFirstSliceAngle: settings.pieFirstSliceAngle ?? null,
});

const clampDerivedArtifactChartPct = (value: number): number => (
    Math.round(Math.max(0, Math.min(100, value)) * 10) / 10
);

const clampDerivedArtifactChartPieLabelPct = (value: number): number => (
    Math.round(Math.max(-18, Math.min(118, value)) * 10) / 10
);

const derivedArtifactChartDataLabelPosition = (
    point: Pick<DerivedArtifactChartPlotPoint, 'xPct' | 'yPct'>,
    position?: ExcelRangeChartDataLabelPosition | null,
): Pick<DerivedArtifactChartDataLabelItem, 'xPct' | 'yPct' | 'anchor'> => {
    switch (position) {
        case 'center':
        case 'bestFit':
            return { xPct: clampDerivedArtifactChartLabelPct(point.xPct), yPct: clampDerivedArtifactChartLabelPct(point.yPct), anchor: 'center' };
        case 'insideBase':
        case 'bottom':
            return { xPct: clampDerivedArtifactChartLabelPct(point.xPct), yPct: clampDerivedArtifactChartLabelPct(point.yPct + 8), anchor: 'below' };
        case 'left':
            return { xPct: clampDerivedArtifactChartLabelPct(point.xPct - 8), yPct: clampDerivedArtifactChartLabelPct(point.yPct), anchor: 'left' };
        case 'right':
            return { xPct: clampDerivedArtifactChartLabelPct(point.xPct + 8), yPct: clampDerivedArtifactChartLabelPct(point.yPct), anchor: 'right' };
        case 'insideEnd':
        case 'outsideEnd':
        case 'top':
        default:
            return { xPct: clampDerivedArtifactChartLabelPct(point.xPct), yPct: clampDerivedArtifactChartLabelPct(point.yPct - 8), anchor: 'above' };
    }
};

const derivedArtifactChartPieMidAngle = (
    point: Pick<DerivedArtifactChartPlotPoint, 'shareStartPct' | 'shareEndPct'>,
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
): number => {
    const firstSliceAngle = Number.isFinite(settings?.pieFirstSliceAngle) ? Number(settings?.pieFirstSliceAngle) : 0;
    return firstSliceAngle + (((point.shareStartPct + point.shareEndPct) / 2) * 3.6) - 90;
};

const derivedArtifactChartPieDataLabelRadius = (
    position?: ExcelRangeChartDataLabelPosition | null,
): number => {
    switch (position) {
        case 'center':
        case 'bestFit':
            return 24;
        case 'insideBase':
            return 15;
        case 'insideEnd':
            return 34;
        case 'left':
        case 'right':
        case 'top':
        case 'bottom':
        case 'outsideEnd':
        default:
            return 58;
    }
};

const derivedArtifactChartPieDataLabelUsesOutsideLeaderLine = (
    position?: ExcelRangeChartDataLabelPosition | null,
): boolean => {
    switch (position) {
        case 'center':
        case 'bestFit':
        case 'insideBase':
        case 'insideEnd':
            return false;
        case 'left':
        case 'right':
        case 'top':
        case 'bottom':
        case 'outsideEnd':
        default:
            return true;
    }
};

const derivedArtifactChartPieDataLabelAnchor = (
    angleDeg: number,
    position?: ExcelRangeChartDataLabelPosition | null,
): DerivedArtifactChartDataLabelItem['anchor'] => {
    switch (position) {
        case 'center':
        case 'bestFit':
        case 'insideBase':
        case 'insideEnd':
            return 'center';
        case 'left':
            return 'left';
        case 'right':
            return 'right';
        case 'top':
            return 'above';
        case 'bottom':
            return 'below';
        case 'outsideEnd':
        default: {
            const radians = (angleDeg * Math.PI) / 180;
            const x = Math.cos(radians);
            const y = Math.sin(radians);
            if (x > 0.28) return 'right';
            if (x < -0.28) return 'left';
            return y > 0 ? 'below' : 'above';
        }
    }
};

const derivedArtifactChartPieDataLabelPosition = (
    point: Pick<DerivedArtifactChartPlotPoint, 'shareStartPct' | 'shareEndPct'>,
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
): Pick<DerivedArtifactChartDataLabelItem, 'xPct' | 'yPct' | 'anchor'> => {
    const angle = derivedArtifactChartPieMidAngle(point, settings);
    const radius = derivedArtifactChartPieDataLabelRadius(settings?.dataLabelPosition);
    const labelPoint = pieAnglePoint(angle, radius);
    return {
        xPct: clampDerivedArtifactChartPieLabelPct(labelPoint.x),
        yPct: clampDerivedArtifactChartPieLabelPct(labelPoint.y),
        anchor: derivedArtifactChartPieDataLabelAnchor(angle, settings?.dataLabelPosition),
    };
};

const derivedArtifactChartDataLabelPositionForPoint = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    point: DerivedArtifactChartPlotPoint,
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
): Pick<DerivedArtifactChartDataLabelItem, 'xPct' | 'yPct' | 'anchor'> => (
    model.visualKind === 'pie'
        ? derivedArtifactChartPieDataLabelPosition(point, settings)
        : derivedArtifactChartDataLabelPosition(point, settings?.dataLabelPosition)
);

const derivedArtifactChartDataLabelTextStyle = (
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
): Pick<DerivedArtifactChartDataLabelItem, 'color' | 'fontSize' | 'fontWeight' | 'fontStyle' | 'rotationDeg' | 'textAlign' | 'textVerticalAlign' | 'textWrap' | 'textAutoFit' | 'textAutoFitFontScale' | 'textHorizontalOverflow' | 'textVerticalOverflow' | 'textInsetTop' | 'textInsetRight' | 'textInsetBottom' | 'textInsetLeft'> => {
    const fontSize = finiteNumberFromRecord({ value: settings?.dataLabelFontSize }, 'value');
    const textAutoFit = chartTextAutoFitFromUnknown(settings?.dataLabelTextAutoFit);
    const textAutoFitFontScale = textAutoFit === 'normal'
        ? Math.max(20, Math.min(100, finiteNumberFromRecord({ value: settings?.dataLabelTextAutoFitFontScale }, 'value') ?? 100))
        : null;
    const baseFontSize = fontSize == null ? 9 : Math.max(6, Math.min(16, Math.round(fontSize * 10) / 10));
    return {
        color: chartColorFromUnknown(settings?.dataLabelFontColor) ?? '#334155',
        fontSize: Math.max(6, Math.min(16, Math.round((baseFontSize * ((textAutoFitFontScale ?? 100) / 100)) * 10) / 10)),
        fontWeight: settings?.dataLabelFontBold === false ? 700 : 900,
        fontStyle: settings?.dataLabelFontItalic === true ? 'italic' : 'normal',
        rotationDeg: Math.max(-180, Math.min(180, finiteNumberFromRecord({ value: settings?.dataLabelTextRotation }, 'value') ?? 0)),
        textAlign: chartTextAlignFromUnknown(settings?.dataLabelTextAlign) ?? 'center',
        textVerticalAlign: chartTextVerticalAlignFromUnknown(settings?.dataLabelTextVerticalAlign) ?? 'middle',
        textWrap: settings?.dataLabelTextWrap === true,
        textAutoFit,
        textAutoFitFontScale,
        textHorizontalOverflow: chartTextHorizontalOverflowFromUnknown(settings?.dataLabelTextHorizontalOverflow),
        textVerticalOverflow: chartTextVerticalOverflowFromUnknown(settings?.dataLabelTextVerticalOverflow),
        textInsetTop: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.dataLabelTextInsetTop }, 'value') ?? 0)),
        textInsetRight: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.dataLabelTextInsetRight }, 'value') ?? 0)),
        textInsetBottom: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.dataLabelTextInsetBottom }, 'value') ?? 0)),
        textInsetLeft: Math.max(0, Math.min(32, finiteNumberFromRecord({ value: settings?.dataLabelTextInsetLeft }, 'value') ?? 0)),
    };
};

const derivedArtifactChartDataLabelLeaderLine = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    point: DerivedArtifactChartPlotPoint,
    position: Pick<DerivedArtifactChartDataLabelItem, 'xPct' | 'yPct'>,
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
): DerivedArtifactChartDataLabelItem['leaderLine'] | undefined => {
    if (settings?.dataLabelShowLeaderLines !== true) return undefined;
    if (model.visualKind === 'pie' && !derivedArtifactChartPieDataLabelUsesOutsideLeaderLine(settings?.dataLabelPosition)) return undefined;
    const lineStart = model.visualKind === 'pie'
        ? pieAnglePoint(derivedArtifactChartPieMidAngle(point, settings), 48)
        : { x: point.xPct, y: point.yPct };
    if (Math.abs(position.xPct - lineStart.x) < 0.1 && Math.abs(position.yPct - lineStart.y) < 0.1) return undefined;
    return {
        x1Pct: clampDerivedArtifactChartPct(lineStart.x),
        y1Pct: clampDerivedArtifactChartPct(lineStart.y),
        x2Pct: position.xPct,
        y2Pct: position.yPct,
    };
};

type DerivedArtifactChartDataLabelCandidate = Omit<DerivedArtifactChartDataLabelItem, 'leaderLine'> & {
    point: DerivedArtifactChartPlotPoint;
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null;
};

const derivedArtifactChartResolvePieDataLabelCollisions = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    candidates: DerivedArtifactChartDataLabelCandidate[],
): DerivedArtifactChartDataLabelItem[] => {
    if (model.visualKind !== 'pie' || candidates.length <= 1) {
        return candidates.map(({ point, settings, ...item }) => {
            const leaderLine = derivedArtifactChartDataLabelLeaderLine(model, point, item, settings);
            return { ...item, ...(leaderLine == null ? {} : { leaderLine }) };
        });
    }
    const resolved = candidates.map((candidate) => ({ ...candidate }));
    const minGapPct = 8;
    for (const side of ['left', 'right'] as const) {
        const sideLabels = resolved
            .filter((candidate) => candidate.anchor === side)
            .sort((a, b) => a.yPct - b.yPct);
        if (sideLabels.length <= 1) continue;
        for (let index = 1; index < sideLabels.length; index += 1) {
            const previous = sideLabels[index - 1]!;
            const current = sideLabels[index]!;
            current.yPct = Math.max(current.yPct, previous.yPct + minGapPct);
        }
        const overflow = Math.max(0, (sideLabels[sideLabels.length - 1]?.yPct ?? 0) - 116);
        if (overflow > 0) {
            for (const label of sideLabels) label.yPct -= overflow;
        }
        for (let index = sideLabels.length - 2; index >= 0; index -= 1) {
            const next = sideLabels[index + 1]!;
            const current = sideLabels[index]!;
            current.yPct = Math.min(current.yPct, next.yPct - minGapPct);
        }
        const underflow = Math.max(0, -16 - (sideLabels[0]?.yPct ?? 0));
        if (underflow > 0) {
            for (const label of sideLabels) label.yPct += underflow;
        }
        for (const label of sideLabels) {
            label.yPct = clampDerivedArtifactChartPieLabelPct(label.yPct);
        }
    }
    return resolved.map(({ point, settings, ...item }) => {
        const leaderLine = derivedArtifactChartDataLabelLeaderLine(model, point, item, settings);
        return { ...item, ...(leaderLine == null ? {} : { leaderLine }) };
    });
};

export const derivedArtifactChartDataLabelItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    settings?: Partial<ExcelRangeChartDataLabelSettings> | null,
    maxItems = 4,
): DerivedArtifactChartDataLabelItem[] => {
    if (settings?.showDataLabels !== true || maxItems <= 0) return [];
    const hasGlobalExplicit = hasExplicitDataLabelSettings(settings);
    const hasSeriesExplicit = hasExplicitSeriesDataLabelSettings(settings);
    if (model.series.length > 1 && (hasGlobalExplicit || hasSeriesExplicit)) {
        const items: DerivedArtifactChartDataLabelCandidate[] = [];
        for (const [seriesIndex, series] of model.series.entries()) {
            const seriesSettings = dataLabelSettingsForSeries(settings, seriesIndex);
            if (hasSeriesExplicit && !hasGlobalExplicit && !hasExplicitDataLabelSettingsForSeries(settings, seriesIndex)) continue;
            const separator = seriesSettings.dataLabelSeparator && seriesSettings.dataLabelSeparator.trim()
                ? seriesSettings.dataLabelSeparator
                : ', ';
            for (const point of series.plotPoints) {
                const label = derivedArtifactChartDataLabelParts(model, point, seriesSettings, series.name).join(separator);
                if (!label) continue;
                const position = derivedArtifactChartDataLabelPositionForPoint(model, point, seriesSettings);
                const textStyle = derivedArtifactChartDataLabelTextStyle(seriesSettings);
                items.push({
                    label,
                    title: `${series.name} ${point.label}: ${label}`,
                    ...position,
                    ...textStyle,
                    point,
                    settings: seriesSettings,
                });
                if (items.length >= maxItems) return derivedArtifactChartResolvePieDataLabelCollisions(model, items);
            }
        }
        return derivedArtifactChartResolvePieDataLabelCollisions(model, items);
    }
    const separator = settings.dataLabelSeparator && settings.dataLabelSeparator.trim()
        ? settings.dataLabelSeparator
        : ', ';
    const candidates = model.plotPoints.slice(0, maxItems).flatMap((point): DerivedArtifactChartDataLabelCandidate[] => {
        const label = derivedArtifactChartDataLabelParts(model, point, settings).join(separator);
        if (!label) return [];
        const position = derivedArtifactChartDataLabelPositionForPoint(model, point, settings);
        const textStyle = derivedArtifactChartDataLabelTextStyle(settings);
        return [{
            label,
            title: `${point.label}: ${label}`,
            ...position,
            ...textStyle,
            point,
            settings,
        }];
    });
    return derivedArtifactChartResolvePieDataLabelCollisions(model, candidates);
};

export const derivedArtifactChartSeriesPlotItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    styles?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartSeriesPlotItem[] => derivedArtifactChartSeriesPreviewItems(model, colors, styles)
    .map((series, seriesIndex) => {
        const plotPoints = series.plotPoints.map((point, pointIndex) => ({
            ...point,
            ...derivedArtifactChartPointMarkerStyle(styles, seriesIndex, pointIndex),
        }));
        const renderBarFill = series.componentChartType === 'bar' || series.componentChartType === 'column';
        const renderAreaFill = series.componentChartType === 'area';
        const renderLineStroke = series.componentChartType === 'line'
            || series.componentChartType === 'area'
            || series.componentChartType === 'scatter';
        return {
            ...series,
            plotPoints,
            polyline: plotPoints.map((point) => `${point.xPct},${point.yPct}`).join(' '),
            smoothPath: styles?.lineSmooth === true ? derivedArtifactChartSmoothPath(plotPoints) : '',
            areaPolygon: derivedArtifactAreaPolygonPoints(plotPoints),
            areaFillColor: derivedArtifactChartColorWithOpacity(series.fillBackgroundColor ?? series.color, series.fillOpacity ?? null),
            renderAreaFill,
            renderBarFill,
            renderLineStroke,
        };
    })
    .filter((series) => series.points.length > 0);

export const derivedArtifactChartVisibleSeriesPlotItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    styles?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartSeriesPlotItem[] => {
    if (model.series.length <= 1) return [];
    return derivedArtifactChartSeriesPlotItems(model, colors, styles)
        .filter((series) => series.renderLineStroke || series.renderAreaFill || series.renderBarFill);
};

export const derivedArtifactChartCategoryBarGroups = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    seriesPlotItems: readonly DerivedArtifactChartSeriesPlotItem[],
    settings?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartCategoryBarGroup[] => {
    const barSeries = seriesPlotItems.filter((series) => series.renderBarFill);
    if (barSeries.length === 0) return [];
    const grouping = settings?.chartGrouping === 'stacked' || settings?.chartGrouping === 'percentStacked'
        ? settings.chartGrouping
        : (settings?.chartGrouping ?? 'clustered');
    const barGapWidth = settings?.barGapWidth ?? 150;
    const barOverlap = settings?.barOverlap ?? 0;
    const barGapPx = Math.max(0, Math.min(8, Math.round((barGapWidth / 80) + (Math.max(0, -barOverlap) / 30) - (Math.max(0, barOverlap) / 45))));
    const barHeightPx = Math.max(5, Math.min(12, Math.round(8 - (barGapWidth / 180) + (barOverlap / 25))));
    const maxPointCount = Math.max(0, ...barSeries.map((series) => series.plotPoints.length));
    const groups: DerivedArtifactChartCategoryBarGroup[] = [];
    for (let pointIndex = 0; pointIndex < maxPointCount; pointIndex += 1) {
        const firstPoint = barSeries.find((series) => series.plotPoints[pointIndex])?.plotPoints[pointIndex];
        if (!firstPoint) continue;
        let bars = barSeries.flatMap((series) => {
            const point = series.plotPoints[pointIndex];
            if (!point) return [];
            return [{
                key: `${series.name}:${point.label}:${pointIndex}`,
                seriesName: series.name,
                value: point.value,
                color: series.color,
                displayValue: formatDerivedArtifactChartValueNumber(point.value, settings),
                axisPct: point.axisPct,
                barStartPct: point.barStartPct,
                barWidthPct: point.barWidthPct,
                fillPattern: series.fillPattern,
                fillForegroundColor: series.fillForegroundColor,
                fillBackgroundColor: series.fillBackgroundColor ?? series.color,
            }];
        });
        if (grouping === 'stacked' || grouping === 'percentStacked') {
            const aggregatePoint = model.plotPoints[pointIndex] ?? null;
            const positiveBars = bars.filter((bar) => bar.value >= 0);
            const negativeBars = bars.filter((bar) => bar.value < 0);
            const positiveTotal = positiveBars.reduce((sum, bar) => sum + Math.max(0, bar.value), 0);
            const negativeTotal = negativeBars.reduce((sum, bar) => sum + Math.abs(bar.value), 0);
            let positiveOffset = aggregatePoint?.axisPct ?? firstPoint.axisPct;
            let negativeOffset = aggregatePoint?.axisPct ?? firstPoint.axisPct;
            const positiveWidth = grouping === 'percentStacked'
                ? 100 - (aggregatePoint?.axisPct ?? firstPoint.axisPct)
                : (aggregatePoint?.barWidthPct ?? positiveBars.reduce((sum, bar) => sum + bar.barWidthPct, 0));
            const negativeWidth = grouping === 'percentStacked'
                ? (aggregatePoint?.axisPct ?? firstPoint.axisPct)
                : negativeBars.reduce((sum, bar) => sum + bar.barWidthPct, 0);
            bars = bars.map((bar) => {
                const total = bar.value < 0 ? negativeTotal : positiveTotal;
                const availableWidth = bar.value < 0 ? negativeWidth : positiveWidth;
                const value = Math.abs(bar.value);
                if (value === 0 || total <= 0) return { ...bar, barWidthPct: 0 };
                const barWidthPct = Math.round((value / total) * availableWidth);
                const barStartPct = bar.value < 0 ? negativeOffset - barWidthPct : positiveOffset;
                const stackedBar = {
                    ...bar,
                    axisPct: aggregatePoint?.axisPct ?? bar.axisPct,
                    barStartPct,
                    barWidthPct,
                };
                if (bar.value < 0) negativeOffset -= barWidthPct;
                else positiveOffset += barWidthPct;
                return stackedBar;
            });
        }
        groups.push({
            label: formatDerivedArtifactChartCategoryLabel(firstPoint, settings),
            grouping,
            barGapPx,
            barHeightPx,
            minHeightPx: grouping === 'stacked' || grouping === 'percentStacked'
                ? Math.max(14, barHeightPx + 5)
                : Math.max(14, (bars.length * barHeightPx) + (Math.max(0, bars.length - 1) * barGapPx)),
            bars,
        });
    }
    return groups;
};

export const derivedArtifactChartAggregatePlotItem = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    styles?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartAggregatePlotItem => {
    const explicitSeriesColor = (styles?.seriesColors ?? []).map(chartColorFromUnknown).find((color) => color != null);
    const styledSeriesColor = styles?.chartStyleId != null
        ? (colors[0] ?? DERIVED_ARTIFACT_CHART_PREVIEW_COLORS[0])
        : null;
    const aggregateLineColor = explicitSeriesColor ?? styledSeriesColor;
    return {
        points: model.plotPoints.map((point, pointIndex) => {
        const pointStyle = derivedArtifactChartPointMarkerStyle(styles, 0, pointIndex);
        const variedColor = colors[pointIndex % colors.length] ?? DERIVED_ARTIFACT_CHART_PREVIEW_COLORS[pointIndex % DERIVED_ARTIFACT_CHART_PREVIEW_COLORS.length];
        return {
            ...point,
            ...pointStyle,
            color: pointStyle.markerFillColor
                ?? (styles?.chartVaryColors === true
                    ? variedColor
                    : explicitSeriesColor ?? styledSeriesColor ?? (point.value < 0
                        ? DERIVED_ARTIFACT_CHART_PREVIEW_NEGATIVE_COLOR
                        : DERIVED_ARTIFACT_CHART_PREVIEW_POSITIVE_COLOR)),
            displayLabel: formatDerivedArtifactChartCategoryLabel(point, styles),
            displayValue: formatDerivedArtifactChartValueNumber(point.value, styles),
        };
        }),
        polyline: model.plotPoints.map((point) => `${point.xPct},${point.yPct}`).join(' '),
        smoothPath: styles?.lineSmooth === true ? derivedArtifactChartSmoothPath(model.plotPoints) : '',
        areaPolygon: derivedArtifactAreaPolygonPoints(model.plotPoints),
        lineColor: aggregateLineColor ?? (model.visualKind === 'area'
            ? DERIVED_ARTIFACT_CHART_PREVIEW_AREA_LINE_COLOR
            : DERIVED_ARTIFACT_CHART_PREVIEW_LINE_COLOR),
        areaFillColor: aggregateLineColor
            ? derivedArtifactChartColorWithOpacity(aggregateLineColor, 0.16)
            : DERIVED_ARTIFACT_CHART_PREVIEW_AREA_FILL_COLOR,
        startLabel: model.plotPoints[0] ? formatDerivedArtifactChartCategoryLabel(model.plotPoints[0], styles) : '',
        endLabel: model.plotPoints[model.plotPoints.length - 1] ? formatDerivedArtifactChartCategoryLabel(model.plotPoints[model.plotPoints.length - 1]!, styles) : '',
    };
};

const derivedArtifactChartFixedValueGuidePcts = (
    weight: DerivedArtifactChartAxisGuideLine['weight'],
): number[] => (
    weight === 'major'
        ? [0, 25, 50, 75, 100]
        : [12.5, 37.5, 62.5, 87.5]
);

const derivedArtifactChartCategoryGuidePcts = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    weight: DerivedArtifactChartAxisGuideLine['weight'],
): number[] => {
    const majorPcts = Array.from(new Set(
        model.plotPoints
            .map((point) => Math.round(point.xPct * 10) / 10)
            .filter((pct) => Number.isFinite(pct)),
    )).sort((a, b) => a - b);
    if (weight === 'major') return majorPcts;
    return majorPcts.slice(1).map((pct, index) => (
        Math.round(((majorPcts[index]! + pct) / 2) * 10) / 10
    ));
};

export const derivedArtifactChartAxisGuideLines = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    settings?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartAxisGuideLine[] => {
    if (model.visualKind === 'pie') return [];
    const lines: DerivedArtifactChartAxisGuideLine[] = [];
    const showValueAxis = settings?.showValueAxis !== false && settings?.valueAxisDeleted !== true;
    const showCategoryAxis = settings?.showCategoryAxis !== false && settings?.categoryAxisDeleted !== true;
    if (showValueAxis) {
        if (settings?.valueAxisHasMinorGridlines) {
            lines.push(...derivedArtifactChartFixedValueGuidePcts('minor').map((pct) => ({
                orientation: 'horizontal' as const,
                pct,
                weight: 'minor' as const,
            })));
        }
        if (settings?.valueAxisHasMajorGridlines) {
            lines.push(...derivedArtifactChartFixedValueGuidePcts('major').map((pct) => ({
                orientation: 'horizontal' as const,
                pct,
                weight: 'major' as const,
            })));
        }
    }
    if (showCategoryAxis) {
        if (settings?.categoryAxisHasMinorGridlines) {
            lines.push(...derivedArtifactChartCategoryGuidePcts(model, 'minor').map((pct) => ({
                orientation: 'vertical' as const,
                pct,
                weight: 'minor' as const,
            })));
        }
        if (settings?.categoryAxisHasMajorGridlines) {
            lines.push(...derivedArtifactChartCategoryGuidePcts(model, 'major').map((pct) => ({
                orientation: 'vertical' as const,
                pct,
                weight: 'major' as const,
            })));
        }
    }
    return lines;
};

const derivedArtifactChartAxisTickPcts = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    axis: DerivedArtifactChartAxisTickMarkItem['axis'],
    weight: DerivedArtifactChartAxisTickMarkItem['weight'],
): number[] => (
    axis === 'category'
        ? derivedArtifactChartCategoryGuidePcts(model, weight)
        : derivedArtifactChartFixedValueGuidePcts(weight)
);

const appendDerivedArtifactChartAxisTickMarks = (
    items: DerivedArtifactChartAxisTickMarkItem[],
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    axis: DerivedArtifactChartAxisTickMarkItem['axis'],
    weight: DerivedArtifactChartAxisTickMarkItem['weight'],
    mark: ExcelRangeChartTickMark | null | undefined,
): void => {
    if (!mark || mark === 'none') return;
    items.push(...derivedArtifactChartAxisTickPcts(model, axis, weight).map((pct) => ({
        axis,
        pct,
        weight,
        mark,
    })));
};

export const derivedArtifactChartAxisTickMarks = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    settings?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartAxisTickMarkItem[] => {
    if (model.visualKind === 'pie') return [];
    const items: DerivedArtifactChartAxisTickMarkItem[] = [];
    const showValueAxis = settings?.showValueAxis !== false && settings?.valueAxisDeleted !== true;
    const showSecondaryValueAxis = settings?.hasSecondaryValueAxis === true && settings?.secondaryValueAxisDeleted !== true;
    const showCategoryAxis = settings?.showCategoryAxis !== false && settings?.categoryAxisDeleted !== true;
    if (showValueAxis && settings?.valueAxisTickLabelPosition !== 'none') {
        appendDerivedArtifactChartAxisTickMarks(items, model, 'value', 'minor', settings?.valueAxisMinorTickMark);
        appendDerivedArtifactChartAxisTickMarks(items, model, 'value', 'major', settings?.valueAxisMajorTickMark);
    }
    if (showSecondaryValueAxis && settings?.secondaryValueAxisTickLabelPosition !== 'none') {
        appendDerivedArtifactChartAxisTickMarks(items, model, 'secondaryValue', 'minor', settings?.secondaryValueAxisMinorTickMark);
        appendDerivedArtifactChartAxisTickMarks(items, model, 'secondaryValue', 'major', settings?.secondaryValueAxisMajorTickMark);
    }
    if (showCategoryAxis && settings?.categoryAxisTickLabelPosition !== 'none') {
        appendDerivedArtifactChartAxisTickMarks(items, model, 'category', 'minor', settings?.categoryAxisMinorTickMark);
        appendDerivedArtifactChartAxisTickMarks(items, model, 'category', 'major', settings?.categoryAxisMajorTickMark);
    }
    return items;
};

const derivedArtifactChartCategoryTickLabelPoints = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    maxLabels = 5,
): DerivedArtifactChartAggregatePointItem[] => {
    const plot = derivedArtifactChartAggregatePlotItem(model);
    const points = plot.points;
    if (points.length <= maxLabels) return points;
    const indexes = new Set<number>();
    for (let slot = 0; slot < maxLabels; slot += 1) {
        indexes.add(Math.round((slot / Math.max(1, maxLabels - 1)) * (points.length - 1)));
    }
    return Array.from(indexes)
        .sort((a, b) => a - b)
        .map((index) => points[index]!)
        .filter(Boolean);
};

export const derivedArtifactChartAxisTickLabels = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    settings?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartAxisTickLabelItem[] => {
    if (model.visualKind === 'pie') return [];
    const items: DerivedArtifactChartAxisTickLabelItem[] = [];
    const showCategoryAxis = settings?.showCategoryAxis !== false
        && settings?.categoryAxisDeleted !== true
        && settings?.categoryAxisTickLabelPosition !== 'none';
    const showValueAxis = settings?.showValueAxis !== false
        && settings?.valueAxisDeleted !== true
        && settings?.valueAxisTickLabelPosition !== 'none';
    const showSecondaryValueAxis = settings?.hasSecondaryValueAxis === true
        && settings?.secondaryValueAxisDeleted !== true
        && settings?.secondaryValueAxisTickLabelPosition !== 'none'
        && model.secondaryMinValue != null
        && model.secondaryMaxValue != null;
    if (showCategoryAxis) {
        const placement = settings?.categoryAxisTickLabelPosition === 'high' ? 'high' : settings?.categoryAxisTickLabelPosition === 'low' ? 'low' : 'nextTo';
        for (const point of derivedArtifactChartCategoryTickLabelPoints(model)) {
            items.push({
                axis: 'category',
                pct: point.xPct,
                label: formatDerivedArtifactChartCategoryLabel(point, settings),
                placement,
            });
        }
    }
    if (showValueAxis) {
        const placement = settings?.valueAxisTickLabelPosition === 'high' ? 'high' : settings?.valueAxisTickLabelPosition === 'low' ? 'low' : 'nextTo';
        const valueRange = model.maxValue - model.minValue;
        for (const pct of derivedArtifactChartFixedValueGuidePcts('major')) {
            const value = valueRange === 0
                ? model.maxValue
                : model.maxValue - ((pct / 100) * valueRange);
            items.push({
                axis: 'value',
                pct,
                label: formatDerivedArtifactChartValueNumber(value, settings),
                placement,
            });
        }
    }
    if (showSecondaryValueAxis) {
        const placement = settings?.secondaryValueAxisTickLabelPosition === 'high' ? 'high' : settings?.secondaryValueAxisTickLabelPosition === 'low' ? 'low' : 'nextTo';
        const minValue = model.secondaryMinValue ?? 0;
        const maxValue = model.secondaryMaxValue ?? minValue;
        const valueRange = maxValue - minValue;
        for (const pct of derivedArtifactChartFixedValueGuidePcts('major')) {
            const value = valueRange === 0
                ? maxValue
                : maxValue - ((pct / 100) * valueRange);
            items.push({
                axis: 'secondaryValue',
                pct,
                label: formatDerivedArtifactChartSecondaryValueNumber(value, settings),
                placement,
            });
        }
    }
    return items;
};

export const derivedArtifactPivotMeasureSummaryItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'pivot' }>,
): DerivedArtifactPivotMeasureSummaryItem[] => model.measureSummaries.map((measure) => ({
    ...measure,
    displayTotal: formatDerivedArtifactPreviewNumber(measure.total),
    title: `${measure.header}: total ${formatDerivedArtifactPreviewNumber(measure.total)}, ${measure.count} value${measure.count === 1 ? '' : 's'}, average ${formatDerivedArtifactPreviewNumber(measure.average)}.`,
}));

export const derivedArtifactPieGradientStops = (
    plotPoints: Pick<DerivedArtifactChartPlotPoint, 'shareStartPct' | 'shareEndPct'>[],
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    remainderColor = 'rgba(148, 163, 184, 0.18)',
): string => {
    if (plotPoints.length === 0 || colors.length === 0) return '';
    const stops = plotPoints.flatMap((point, index) => (
        point.shareEndPct > point.shareStartPct
            ? [`${colors[index % colors.length]} ${point.shareStartPct}% ${point.shareEndPct}%`]
            : []
    ));
    if (stops.length === 0) return '';
    const lastSegmentEnd = Math.max(0, ...plotPoints.map((point) => point.shareEndPct));
    if (lastSegmentEnd < 100) {
        stops.push(`${remainderColor} ${lastSegmentEnd}% 100%`);
    }
    return stops.join(', ');
};

export const derivedArtifactPieGradientBackground = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    firstSliceAngle?: number | null,
): string => {
    const stops = derivedArtifactPieGradientStops(model.plotPoints, colors) || DERIVED_ARTIFACT_EMPTY_PIE_GRADIENT_STOP;
    if (firstSliceAngle == null || !Number.isFinite(firstSliceAngle)) return stops;
    const angle = Math.round(((firstSliceAngle % 360) + 360) % 360);
    return `from ${angle}deg, ${stops}`;
};

export const derivedArtifactPieHoleSizePct = (
    settings?: Pick<ExcelRangeChartSettings, 'doughnutHoleSize'> | null,
): number => {
    const holeSize = settings?.doughnutHoleSize;
    if (holeSize == null || !Number.isFinite(holeSize)) return 0;
    return Math.min(90, Math.max(10, Math.round(holeSize)));
};

const pieAnglePoint = (
    angleDeg: number,
    radius: number,
): { x: number; y: number } => {
    const radians = (angleDeg * Math.PI) / 180;
    return {
        x: 50 + (Math.cos(radians) * radius),
        y: 50 + (Math.sin(radians) * radius),
    };
};

const derivedArtifactPieSlicePath = (
    startPct: number,
    endPct: number,
    firstSliceAngle: number,
    holeSizePct: number,
): string => {
    const clampedStart = Math.max(0, Math.min(100, startPct));
    const clampedEnd = Math.max(clampedStart, Math.min(100, endPct));
    if (clampedEnd <= clampedStart) return '';
    const startAngle = firstSliceAngle + (clampedStart * 3.6) - 90;
    const endAngle = firstSliceAngle + (clampedEnd * 3.6) - 90;
    const arcEndAngle = clampedEnd - clampedStart >= 100 ? startAngle + 359.99 : endAngle;
    const largeArc = arcEndAngle - startAngle > 180 ? 1 : 0;
    const outerStart = pieAnglePoint(startAngle, 48);
    const outerEnd = pieAnglePoint(arcEndAngle, 48);
    const innerRadius = Math.max(0, Math.min(43, (holeSizePct / 100) * 48));
    if (innerRadius <= 0) {
        return `M 50 50 L ${outerStart.x.toFixed(3)} ${outerStart.y.toFixed(3)} A 48 48 0 ${largeArc} 1 ${outerEnd.x.toFixed(3)} ${outerEnd.y.toFixed(3)} Z`;
    }
    const innerEnd = pieAnglePoint(arcEndAngle, innerRadius);
    const innerStart = pieAnglePoint(startAngle, innerRadius);
    return [
        `M ${outerStart.x.toFixed(3)} ${outerStart.y.toFixed(3)}`,
        `A 48 48 0 ${largeArc} 1 ${outerEnd.x.toFixed(3)} ${outerEnd.y.toFixed(3)}`,
        `L ${innerEnd.x.toFixed(3)} ${innerEnd.y.toFixed(3)}`,
        `A ${innerRadius.toFixed(3)} ${innerRadius.toFixed(3)} 0 ${largeArc} 0 ${innerStart.x.toFixed(3)} ${innerStart.y.toFixed(3)}`,
        'Z',
    ].join(' ');
};

const derivedArtifactPiePointExplosion = (
    pointIndex: number,
    settings?: Pick<ExcelRangeChartSettings, 'seriesExplosions' | 'seriesPointIndexes' | 'seriesPointExplosions'> | null,
): number => {
    const pointIndexes = settings?.seriesPointIndexes?.[0] ?? [];
    const pointExplosions = settings?.seriesPointExplosions?.[0] ?? [];
    const styleIndex = pointIndexes.findIndex((index) => index === pointIndex);
    const pointExplosion = styleIndex >= 0 ? chartExplosionFromUnknown(pointExplosions[styleIndex]) : null;
    return pointExplosion ?? chartExplosionFromUnknown(settings?.seriesExplosions?.[0]) ?? 0;
};

export const derivedArtifactPieSliceItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    settings?: ExcelRangeChartSeriesStyleSettings | null,
): DerivedArtifactPieSliceItem[] => {
    if (model.plotPoints.length === 0 || colors.length === 0) return [];
    const firstSliceAngle = Number.isFinite(settings?.pieFirstSliceAngle) ? Number(settings?.pieFirstSliceAngle) : 0;
    const holeSizePct = derivedArtifactPieHoleSizePct(settings);
    return model.plotPoints.map((point, index) => {
        const explosion = derivedArtifactPiePointExplosion(index, settings);
        const pointStyle = derivedArtifactChartPointMarkerStyle(settings, 0, index);
        const midAngle = firstSliceAngle + (((point.shareStartPct + point.shareEndPct) / 2) * 3.6) - 90;
        const radians = (midAngle * Math.PI) / 180;
        const offset = Math.round(Math.min(16, Math.max(0, explosion * 0.08)) * 10) / 10;
        return {
            label: point.label,
            color: colors[index % colors.length] ?? DERIVED_ARTIFACT_CHART_PREVIEW_COLORS[index % DERIVED_ARTIFACT_CHART_PREVIEW_COLORS.length],
            path: derivedArtifactPieSlicePath(point.shareStartPct, point.shareEndPct, firstSliceAngle, holeSizePct),
            translateX: Math.round((Math.cos(radians) * offset) * 10) / 10,
            translateY: Math.round((Math.sin(radians) * offset) * 10) / 10,
            exploded: offset > 0,
            ...(pointStyle.markerFillPattern == null ? {} : { fillPattern: pointStyle.markerFillPattern }),
            ...(pointStyle.markerFillForegroundColor == null ? {} : { fillForegroundColor: pointStyle.markerFillForegroundColor }),
            ...(pointStyle.markerFillBackgroundColor == null ? {} : { fillBackgroundColor: pointStyle.markerFillBackgroundColor }),
        };
    }).filter((item) => item.path);
};

export const describeDerivedArtifactPreviewEmptySource = (
    model: DerivedArtifactPreviewModel,
    sourceAddress = model.sourceAddress,
): string => (
    model.kind === 'chart'
        ? `No numeric source values in ${sourceAddress}`
        : `No source rows in ${sourceAddress}`
);

export const describeDerivedArtifactPreviewPlaceholder = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'kind' | 'chart_type'>,
    sourceAddress?: string,
): string => {
    const artifactLabel = artifact.kind === 'pivot' ? 'Pivot artifact' : `${artifact.chart_type ?? 'chart'} artifact`;
    return sourceAddress ? `${artifactLabel} - source ${sourceAddress}` : artifactLabel;
};

export const derivedArtifactPieLegendItems = (
    plotPoints: Pick<DerivedArtifactChartPlotPoint, 'label' | 'sharePct' | 'shareStartPct' | 'shareEndPct'>[],
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
    maxItems = DERIVED_ARTIFACT_PREVIEW_MAX_PIE_LEGEND_ITEMS,
    totalPointCount = plotPoints.length,
    remainderColor = DERIVED_ARTIFACT_PIE_REMAINDER_LEGEND_COLOR,
): DerivedArtifactPieLegendItem[] => {
    const itemLimit = Math.max(0, Math.floor(maxItems));
    if (plotPoints.length === 0 || colors.length === 0 || itemLimit === 0) return [];
    const items: DerivedArtifactPieLegendItem[] = plotPoints.slice(0, itemLimit).map((point, index) => ({
        label: point.label,
        sharePct: point.sharePct,
        shareStartPct: point.shareStartPct,
        shareEndPct: point.shareEndPct,
        color: colors[index % colors.length],
    }));
    const hiddenPointCount = Math.max(0, totalPointCount - plotPoints.length);
    if (hiddenPointCount > 0 && items.length < itemLimit) {
        const remainderStartPct = Math.max(0, ...plotPoints.map((point) => point.shareEndPct));
        const remainderPct = Math.max(0, 100 - remainderStartPct);
        if (remainderPct > 0) {
            items.push({
                label: 'Other',
                sharePct: remainderPct,
                shareStartPct: remainderStartPct,
                shareEndPct: 100,
                color: remainderColor,
                sampledRemainder: true,
                hiddenPointCount,
            });
        }
    }
    return items;
};

export const derivedArtifactChartPieLegendItems = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    maxItems = DERIVED_ARTIFACT_PREVIEW_MAX_PIE_LEGEND_ITEMS,
    colors: readonly string[] = DERIVED_ARTIFACT_CHART_PREVIEW_COLORS,
): DerivedArtifactPieLegendItem[] => derivedArtifactPieLegendItems(model.plotPoints, colors, maxItems, model.totalPointCount);

export const derivedArtifactChartPresentationColors = (
    settings?: Partial<Pick<ExcelRangeChartSettings, 'chartStyleId' | 'seriesColors'>> | null,
): readonly string[] => {
    const colors = (settings?.seriesColors ?? []).map(chartColorFromUnknown);
    if (colors.length === 0 || colors.every((color) => color == null)) {
        const styleId = typeof settings?.chartStyleId === 'number' ? Math.floor(settings.chartStyleId) : null;
        if (styleId != null && styleId > 0) {
            const paletteIndex = Math.min(
                DERIVED_ARTIFACT_CHART_STYLE_PALETTES.length - 1,
                Math.floor((styleId - 1) / 8),
            );
            return DERIVED_ARTIFACT_CHART_STYLE_PALETTES[paletteIndex] ?? DERIVED_ARTIFACT_CHART_PREVIEW_COLORS;
        }
        return DERIVED_ARTIFACT_CHART_PREVIEW_COLORS;
    }
    const colorCount = Math.max(colors.length, DERIVED_ARTIFACT_CHART_PREVIEW_COLORS.length);
    return Array.from({ length: colorCount }, (_, index) => (
        colors[index] ?? DERIVED_ARTIFACT_CHART_PREVIEW_COLORS[index % DERIVED_ARTIFACT_CHART_PREVIEW_COLORS.length]
    ));
};

export const derivedArtifactChartPiePresentationColors = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    settings?: Partial<Pick<ExcelRangeChartSettings, 'chartStyleId' | 'seriesColors' | 'seriesPointIndexes' | 'seriesPointColors' | 'seriesPointFillOpacities' | 'seriesPointFillForegroundColors'>> | null,
): readonly string[] => {
    const fallbackColors = derivedArtifactChartPresentationColors(settings);
    const pointIndexes = settings?.seriesPointIndexes?.[0] ?? [];
    const pointColors = settings?.seriesPointColors?.[0] ?? [];
    const pointFillOpacities = settings?.seriesPointFillOpacities?.[0] ?? [];
    const pointForegroundColors = settings?.seriesPointFillForegroundColors?.[0] ?? [];
    if (pointIndexes.length === 0) return fallbackColors;
    return model.plotPoints.map((_, index) => {
        const fallbackColor = fallbackColors[index % fallbackColors.length] ?? DERIVED_ARTIFACT_CHART_PREVIEW_COLORS[index % DERIVED_ARTIFACT_CHART_PREVIEW_COLORS.length];
        const pointStyleIndex = pointIndexes.findIndex((pointIndex) => pointIndex === index);
        if (pointStyleIndex < 0) return fallbackColor;
        const color = chartColorFromUnknown(pointColors[pointStyleIndex])
            ?? chartColorFromUnknown(pointForegroundColors[pointStyleIndex])
            ?? fallbackColor;
        const opacity = chartOpacityFromUnknown(pointFillOpacities[pointStyleIndex]);
        return opacity == null ? color : derivedArtifactChartColorWithOpacity(color, opacity);
    });
};

export const buildDerivedArtifactChartPreviewPresentation = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'chart' }>,
    sourceAddress = model.sourceAddress,
    settings?: ExcelRangeChartPresentationSettings | null,
): DerivedArtifactChartPreviewPresentation => {
    const colors = derivedArtifactChartPresentationColors(settings);
    const pieColors = derivedArtifactChartPiePresentationColors(model, settings);
    const seriesPlotItems = derivedArtifactChartVisibleSeriesPlotItems(model, colors, settings);
    const renderKind: DerivedArtifactChartPreviewRenderKind = model.visualKind === 'pie'
        ? 'pie'
        : (model.visualKind === 'line' || model.visualKind === 'area' || model.visualKind === 'scatter' ? 'plot' : 'bar');
    const renderAreaFill = model.visualKind === 'area';
    const renderLineStroke = model.visualKind === 'line' || model.visualKind === 'area';
    return {
        hasRenderableContent: model.points.length > 0,
        renderKind,
        chartAreaStyle: derivedArtifactChartShapeStyle(settings, 'chartArea'),
        plotAreaStyle: derivedArtifactChartShapeStyle(settings, 'plotArea'),
        chartTitleStyle: derivedArtifactChartTextStyle(settings, 'chartTitle', { color: '#334155', fontSize: 11, fontWeight: 900 }),
        categoryAxisTitleStyle: derivedArtifactChartTextStyle(settings, 'categoryAxisTitle', { color: '#475569', fontSize: 9, fontWeight: 900 }),
        valueAxisTitleStyle: derivedArtifactChartTextStyle(settings, 'valueAxisTitle', { color: '#475569', fontSize: 9, fontWeight: 900 }),
        secondaryValueAxisTitleStyle: derivedArtifactChartTextStyle(settings, 'secondaryValueAxisTitle', { color: '#475569', fontSize: 9, fontWeight: 900 }),
        renderAreaFill,
        renderLineStroke,
        renderAggregateLine: renderLineStroke,
        seriesPointRadius: model.visualKind === 'scatter' ? 3 : 2.2,
        aggregatePointRadius: model.visualKind === 'scatter' ? 3.2 : 2.4,
        seriesSummaryItems: derivedArtifactChartSeriesSummaryItems(model, DERIVED_ARTIFACT_PREVIEW_MAX_SERIES_SUMMARIES, colors, settings),
        aggregatePlot: derivedArtifactChartAggregatePlotItem(model, colors, settings),
        seriesPlotItems,
        categoryBarGroups: derivedArtifactChartCategoryBarGroups(model, seriesPlotItems, settings),
        axisGuideLines: derivedArtifactChartAxisGuideLines(model, settings),
        axisTickMarks: derivedArtifactChartAxisTickMarks(model, settings),
        axisTickLabels: derivedArtifactChartAxisTickLabels(model, settings),
        dataLabelItems: derivedArtifactChartDataLabelItems(model, settings),
        pieGradient: renderKind === 'pie' ? derivedArtifactPieGradientBackground(model, pieColors, settings?.pieFirstSliceAngle) : '',
        pieHoleSizePct: renderKind === 'pie' ? derivedArtifactPieHoleSizePct(settings) : 0,
        pieSliceItems: renderKind === 'pie' ? derivedArtifactPieSliceItems(model, pieColors, settings) : [],
        pieLegendItems: renderKind === 'pie' ? derivedArtifactChartPieLegendItems(model, DERIVED_ARTIFACT_PREVIEW_MAX_PIE_LEGEND_ITEMS, pieColors) : [],
        emptyLabel: describeDerivedArtifactPreviewEmptySource(model, sourceAddress),
    };
};

export const buildDerivedArtifactPivotPreviewPresentation = (
    model: Extract<DerivedArtifactPreviewModel, { kind: 'pivot' }>,
    sourceAddress = model.sourceAddress,
): DerivedArtifactPivotPreviewPresentation => ({
    hasRenderableContent: model.headers.length > 0,
    measureSummaryItems: derivedArtifactPivotMeasureSummaryItems(model),
    emptyLabel: describeDerivedArtifactPreviewEmptySource(model, sourceAddress),
});

export const buildDerivedArtifactPreviewStatusPresentation = ({
    artifact,
    sourceAddress,
    loading = false,
    error = null,
    chartPresentation = null,
    pivotPresentation = null,
}: {
    artifact: Pick<SpreadsheetDerivedArtifact, 'kind' | 'chart_type'>;
    sourceAddress?: string;
    loading?: boolean;
    error?: string | null;
    chartPresentation?: DerivedArtifactChartPreviewPresentation | null;
    pivotPresentation?: DerivedArtifactPivotPreviewPresentation | null;
}): DerivedArtifactPreviewStatusPresentation => {
    if (loading) {
        return {
            statusKind: 'loading',
            label: 'Loading source preview...',
            title: sourceAddress ? `Loading source preview for ${sourceAddress}.` : 'Loading source preview.',
        };
    }
    if (error) {
        return {
            statusKind: 'error',
            label: 'Preview unavailable',
            title: error,
        };
    }
    if (chartPresentation) {
        return chartPresentation.hasRenderableContent
            ? { statusKind: 'chart', label: '', title: sourceAddress ? `Chart preview from ${sourceAddress}.` : 'Chart preview.' }
            : { statusKind: 'empty', label: chartPresentation.emptyLabel, title: chartPresentation.emptyLabel };
    }
    if (pivotPresentation) {
        return pivotPresentation.hasRenderableContent
            ? { statusKind: 'pivot', label: '', title: sourceAddress ? `Pivot preview from ${sourceAddress}.` : 'Pivot preview.' }
            : { statusKind: 'empty', label: pivotPresentation.emptyLabel, title: pivotPresentation.emptyLabel };
    }
    const placeholder = describeDerivedArtifactPreviewPlaceholder(artifact, sourceAddress);
    return {
        statusKind: 'placeholder',
        label: placeholder,
        title: placeholder,
    };
};

export const derivedArtifactPlacementAddress = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'placement'>,
): string => rangeAddress(artifact.placement);

export const buildDerivedArtifactOverlayChromePresentation = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'kind' | 'chart_type' | 'title' | 'placement'>,
    sourceAddress?: string,
): DerivedArtifactOverlayChromePresentation => {
    const kindLabel = artifact.kind === 'pivot' ? 'PivotTable' : `${artifact.chart_type ?? 'chart'} chart`;
    const title = String(artifact.title ?? kindLabel).trim() || kindLabel;
    const placementAddress = derivedArtifactPlacementAddress(artifact);
    const sourceSuffix = sourceAddress ? `, source ${sourceAddress}` : '';
    const overlayTitle = `${title} ${kindLabel} placement ${placementAddress}${sourceSuffix}. Right-click to edit metadata or drag handles to move or resize.`;
    const sourceLabel = sourceAddress ? `${title} source range ${sourceAddress}` : `${title} source range`;
    const theme: DerivedArtifactOverlayThemePresentation = artifact.kind === 'pivot'
        ? {
            sourceOutlineBorderColor: 'rgba(37, 99, 235, 0.62)',
            sourceOutlineBackground: 'rgba(37, 99, 235, 0.06)',
            sourceOutlineSelectedBackground: 'rgba(37, 99, 235, 0.10)',
            sourceOutlineShadowColor: 'rgba(37, 99, 235, 0.16)',
            sourceHandleBorderColor: 'rgba(37, 99, 235, 0.5)',
            sourceHandleTextColor: '#1d4ed8',
            sourceResizeBorderColor: '#1d4ed8',
            overlayBorderColor: 'rgba(37, 99, 235, 0.78)',
            overlayBackground: 'rgba(239, 246, 255, 0.82)',
            overlaySelectedShadowColor: 'rgba(37, 99, 235, 0.18)',
            headerBackground: 'rgba(37, 99, 235, 0.10)',
            headerBorderColor: 'rgba(37, 99, 235, 0.24)',
            headerTextColor: '#1d4ed8',
            headerAddressTextColor: 'rgba(29, 78, 216, 0.72)',
            previewBorderColor: 'rgba(37, 99, 235, 0.32)',
        }
        : {
            sourceOutlineBorderColor: 'rgba(16, 124, 65, 0.62)',
            sourceOutlineBackground: 'rgba(16, 124, 65, 0.06)',
            sourceOutlineSelectedBackground: 'rgba(16, 124, 65, 0.10)',
            sourceOutlineShadowColor: 'rgba(16, 124, 65, 0.16)',
            sourceHandleBorderColor: 'rgba(16, 124, 65, 0.5)',
            sourceHandleTextColor: 'var(--fg-green)',
            sourceResizeBorderColor: 'var(--fg-green)',
            overlayBorderColor: 'rgba(16, 124, 65, 0.78)',
            overlayBackground: 'rgba(240, 253, 244, 0.82)',
            overlaySelectedShadowColor: 'rgba(16, 124, 65, 0.18)',
            headerBackground: 'rgba(16, 124, 65, 0.10)',
            headerBorderColor: 'rgba(16, 124, 65, 0.24)',
            headerTextColor: 'var(--fg-green)',
            headerAddressTextColor: 'rgba(16, 124, 65, 0.72)',
            previewBorderColor: 'rgba(16, 124, 65, 0.32)',
        };
    return {
        kindLabel,
        iconLabel: artifact.kind === 'pivot' ? '#' : 'C',
        title,
        placementAddress,
        theme,
        overlayTitle,
        overlayAriaLabel: overlayTitle,
        sourceOutlineTitle: sourceLabel,
        sourceOutlineAriaLabel: sourceLabel,
        sourceMoveTitle: sourceAddress
            ? `Drag to move ${title} source range ${sourceAddress} through engine-owned metadata.`
            : `Drag to move ${title} source range through engine-owned metadata.`,
        sourceMoveAriaLabel: `Move ${title} source range`,
        sourceResizeTitle: sourceAddress
            ? `Drag to resize ${title} source range ${sourceAddress} through engine-owned metadata.`
            : `Drag to resize ${title} source range through engine-owned metadata.`,
        sourceResizeAriaLabel: `Resize ${title} source range`,
        sourceChipLabel: sourceAddress ? `Source ${sourceAddress}` : 'Source',
        placementResizeTitle: `Drag to resize ${title} placement through engine-owned metadata.`,
        placementResizeAriaLabel: `Resize ${title} placement`,
    };
};

export const buildDerivedArtifactContextMenuPresentation = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'kind' | 'chart_type' | 'title' | 'metadata' | 'source_range'>,
): DerivedArtifactContextMenuPresentation => {
    const kindTitle = artifact.kind === 'pivot' ? 'PivotTable' : 'Chart';
    const sourceKindLabel = artifact.kind === 'pivot' ? 'Pivot' : 'Chart';
    const title = String(artifact.title ?? '').trim() || kindTitle;
    const chartSettings = artifact.kind === 'chart'
        ? buildExcelRangeChartSettings({ artifact: artifact as SpreadsheetDerivedArtifact })
        : null;
    const checkedForChartMetadataAction = (update: ExcelRangeChartMetadataUpdate): boolean => {
        if (!chartSettings) return false;
        if (update.key === 'seriesOrientation') return chartSettings.seriesOrientation === update.value;
        if (update.key === 'legendPosition') return chartSettings.legendPosition === update.value;
        if (update.key === 'categoryAxisTitle' || update.key === 'valueAxisTitle') return Boolean(chartSettings[update.key]);
        return Boolean(chartSettings[update.key]);
    };
    return {
        promptTitle: 'Artifact title',
        promptDefaultTitle: title,
        renameItem: {
            label: `Rename ${title}`,
            title: `Rename ${title} through engine-owned UpsertDerivedArtifact metadata.`,
            action: 'rename-derived-artifact',
        },
        resizeItem: {
            label: `Resize ${title} to Selection`,
            title: `Move and resize ${title} placement through engine-owned UpsertDerivedArtifact metadata.`,
            action: 'resize-derived-artifact-to-selection',
        },
        setSourceItem: {
            label: `Set ${sourceKindLabel} Source to Selection`,
            title: `Change this ${sourceKindLabel.toLowerCase()} source range through engine-owned UpsertDerivedArtifact metadata.`,
            action: 'set-derived-artifact-source-to-selection',
        },
        selectSourceItem: {
            label: `Select ${sourceKindLabel} Source Range`,
            title: `Select this engine-owned ${sourceKindLabel.toLowerCase()} source range for inspection, movement, or resizing without changing cell truth.`,
            action: 'select-derived-artifact-source-range',
        },
        deleteItem: {
            label: `Delete ${title}`,
            title: `Delete ${title} metadata through the engine-owned DeleteDerivedArtifact operation. Source cells remain in place.`,
            action: 'delete-derived-artifact',
        },
        chartTypeMenu: artifact.kind === 'chart'
            ? {
                label: 'Chart Type',
                title: `Change ${title} chart type through engine-owned UpsertDerivedArtifact metadata.`,
                items: DERIVED_ARTIFACT_CHART_TYPE_MENU_ITEMS.map((item) => ({
                    label: item.label,
                    checked: (artifact.chart_type ?? 'column') === item.chartType,
                    action: item.action,
                    title: `Use ${item.label.toLowerCase()} chart metadata for ${title}.`,
                })),
            }
            : null,
        chartSourceMenu: artifact.kind === 'chart' && chartSettings
            ? {
                label: 'Source Settings',
                title: `Change ${title} source interpretation through engine-owned chart metadata.`,
                items: DERIVED_ARTIFACT_CHART_METADATA_ACTIONS
                    .filter((item) => item.group === 'source')
                    .map((item) => ({
                        label: item.label,
                        checked: checkedForChartMetadataAction(item.update),
                        action: item.action,
                        title: `Use ${item.label.toLowerCase()} for ${title}.`,
                    })),
            }
            : null,
        chartDisplayMenu: artifact.kind === 'chart' && chartSettings
            ? {
                label: 'Chart Display',
                title: `Change ${title} display options through engine-owned chart metadata.`,
                items: DERIVED_ARTIFACT_CHART_METADATA_ACTIONS
                    .filter((item) => item.group === 'display')
                    .map((item) => ({
                        label: item.label,
                        checked: checkedForChartMetadataAction(item.update),
                        action: item.action,
                        title: `Toggle ${item.label.toLowerCase()} for ${title}.`,
                    })),
            }
            : null,
    };
};

export const buildDerivedArtifactChartQuickToolbarPresentation = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'kind' | 'chart_type' | 'title' | 'metadata' | 'source_range'>,
): DerivedArtifactChartQuickToolbarPresentation | null => {
    if (artifact.kind !== 'chart') return null;
    const title = String(artifact.title ?? '').trim() || 'Chart';
    const chartSettings = buildExcelRangeChartSettings({ artifact: artifact as SpreadsheetDerivedArtifact });
    if (!chartSettings) return null;
    const sourceDraft = formatDerivedArtifactSourceRangeDraft(artifact as SpreadsheetDerivedArtifact);
    const activeForChartMetadataAction = (update: ExcelRangeChartMetadataUpdate): boolean => {
        if (update.key === 'seriesOrientation') return chartSettings.seriesOrientation === update.value;
        if (update.key === 'legendPosition') return chartSettings.legendPosition === update.value;
        if (update.key === 'categoryAxisTitle' || update.key === 'valueAxisTitle') return Boolean(chartSettings[update.key]);
        return Boolean(chartSettings[update.key]);
    };
    return {
        title: `Edit ${title} chart settings`,
        editGroup: {
            label: 'Edit',
            title: `Edit ${title} chart title and source range through engine-owned chart metadata.`,
            items: [
                {
                    label: 'Title',
                    active: false,
                    action: 'derived-chart-edit-title',
                    title: `Rename ${title} through engine-owned chart metadata.`,
                    promptTitle: 'Chart title',
                    promptDefault: title,
                },
                {
                    label: 'X Axis',
                    active: Boolean(chartSettings.categoryAxisTitle),
                    action: 'derived-chart-edit-category-axis-title',
                    title: `Set the category axis title for ${title} through engine-owned chart metadata.`,
                    promptTitle: 'Category axis title',
                    promptDefault: chartSettings.categoryAxisTitle,
                },
                {
                    label: 'Y Axis',
                    active: Boolean(chartSettings.valueAxisTitle),
                    action: 'derived-chart-edit-value-axis-title',
                    title: `Set the value axis title for ${title} through engine-owned chart metadata.`,
                    promptTitle: 'Value axis title',
                    promptDefault: chartSettings.valueAxisTitle,
                },
                {
                    label: 'Range',
                    active: false,
                    action: 'derived-chart-edit-source-range',
                    title: `Enter the source range for ${title}.`,
                    promptTitle: 'Chart source range',
                    promptDefault: sourceDraft,
                },
                {
                    label: 'Pick',
                    active: false,
                    action: 'select-derived-artifact-source-range',
                    title: `Select ${title} source range ${sourceDraft} for inspection, movement, or resizing.`,
                },
            ],
        },
        chartTypeGroup: {
            label: 'Type',
            title: `Change ${title} chart type through engine-owned chart metadata.`,
            items: DERIVED_ARTIFACT_CHART_TYPE_MENU_ITEMS.map((item) => ({
                label: item.label,
                active: (artifact.chart_type ?? 'column') === item.chartType,
                action: item.action,
                title: `Use ${item.label.toLowerCase()} chart type for ${title}.`,
            })),
        },
        sourceGroup: {
            label: 'Source',
            title: `Change ${title} source interpretation through engine-owned chart metadata.`,
            items: DERIVED_ARTIFACT_CHART_METADATA_ACTIONS
                .filter((item) => item.group === 'source')
                .map((item) => ({
                    label: item.shortLabel,
                    active: activeForChartMetadataAction(item.update),
                    action: item.action,
                    title: `Use ${item.label.toLowerCase()} for ${title}.`,
                })),
        },
        displayGroup: {
            label: 'Show',
            title: `Change ${title} display options through engine-owned chart metadata.`,
            items: DERIVED_ARTIFACT_CHART_METADATA_ACTIONS
                .filter((item) => item.group === 'display')
                .map((item) => ({
                    label: item.shortLabel,
                    active: activeForChartMetadataAction(item.update),
                    action: item.action,
                    title: `Toggle ${item.label.toLowerCase()} for ${title}.`,
                })),
        },
    };
};

export const buildDerivedArtifactContextMenuEntries = (
    presentation: DerivedArtifactContextMenuPresentation | null | undefined,
): DerivedArtifactContextMenuEntry[] => {
    if (!presentation) {
        return [];
    }
    return [
        { type: 'separator' },
        presentation.renameItem,
        presentation.resizeItem,
        presentation.setSourceItem,
        presentation.selectSourceItem,
        ...(presentation.chartTypeMenu
            ? [{
                label: presentation.chartTypeMenu.label,
                title: presentation.chartTypeMenu.title,
                submenu: presentation.chartTypeMenu.items,
            }]
            : []),
        ...(presentation.chartSourceMenu
            ? [{
                label: presentation.chartSourceMenu.label,
                title: presentation.chartSourceMenu.title,
                submenu: presentation.chartSourceMenu.items,
            }]
            : []),
        ...(presentation.chartDisplayMenu
            ? [{
                label: presentation.chartDisplayMenu.label,
                title: presentation.chartDisplayMenu.title,
                submenu: presentation.chartDisplayMenu.items,
            }]
            : []),
        presentation.deleteItem,
    ];
};

export const derivedArtifactChartTypeForContextMenuAction = (action: string): SpreadsheetChartArtifactType | null => (
    DERIVED_ARTIFACT_CHART_TYPE_MENU_ITEMS.find((item) => item.action === action)?.chartType ?? null
);

export const derivedArtifactChartMetadataUpdateForContextMenuAction = (
    action: string,
): ExcelRangeChartMetadataUpdate | null => (
    DERIVED_ARTIFACT_CHART_METADATA_ACTIONS.find((item) => item.action === action)?.update ?? null
);

const derivedArtifactSelectionProjection = (range: SpreadsheetRangeAddress): DerivedArtifactSelectionProjection => ({
    col: Math.min(range.start_col, range.end_col),
    row: Math.min(range.start_row, range.end_row),
    endCol: Math.max(range.start_col, range.end_col),
    endRow: Math.max(range.start_row, range.end_row),
});

const derivedArtifactNormalizedRange = (range: SpreadsheetRangeAddress): SpreadsheetRangeAddress => ({
    sheet_id: range.sheet_id,
    start_col: Math.min(range.start_col, range.end_col),
    start_row: Math.min(range.start_row, range.end_row),
    end_col: Math.max(range.start_col, range.end_col),
    end_row: Math.max(range.start_row, range.end_row),
});

const derivedArtifactRangesEqual = (
    left: SpreadsheetRangeAddress,
    right: SpreadsheetRangeAddress | null | undefined,
): boolean => {
    if (!right) return false;
    const normalizedLeft = derivedArtifactNormalizedRange(left);
    const normalizedRight = derivedArtifactNormalizedRange(right);
    return normalizedLeft.sheet_id === normalizedRight.sheet_id
        && normalizedLeft.start_col === normalizedRight.start_col
        && normalizedLeft.start_row === normalizedRight.start_row
        && normalizedLeft.end_col === normalizedRight.end_col
        && normalizedLeft.end_row === normalizedRight.end_row;
};

const derivedArtifactRangeIntersectsVisibleBounds = (
    range: SpreadsheetRangeAddress,
    visibleBounds: DerivedArtifactOverlayVisibleBounds,
): boolean => {
    const normalized = derivedArtifactNormalizedRange(range);
    return normalized.end_col >= visibleBounds.startCol
        && normalized.start_col <= visibleBounds.endCol
        && normalized.end_row >= visibleBounds.startRow
        && normalized.start_row <= visibleBounds.endRow;
};

const derivedArtifactRangeWidth = (range: SpreadsheetRangeAddress): number => (
    Math.abs(range.end_col - range.start_col) + 1
);

const derivedArtifactRangeHeight = (range: SpreadsheetRangeAddress): number => (
    Math.abs(range.end_row - range.start_row) + 1
);

export const buildDerivedArtifactOverlayInteractionProjection = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'id' | 'placement' | 'source_range'>,
): DerivedArtifactOverlayInteractionProjection => ({
    placementSelection: derivedArtifactSelectionProjection(artifact.placement),
    sourceSelection: derivedArtifactSelectionProjection(artifact.source_range),
    placementMoveDrag: {
        originCol: artifact.placement.start_col,
        originRow: artifact.placement.start_row,
        width: derivedArtifactRangeWidth(artifact.placement),
        height: derivedArtifactRangeHeight(artifact.placement),
    },
    placementResizeDrag: {
        anchorCol: artifact.placement.start_col,
        anchorRow: artifact.placement.start_row,
    },
    sourceMoveDrag: {
        artifactId: artifact.id,
        width: derivedArtifactRangeWidth(artifact.source_range),
        height: derivedArtifactRangeHeight(artifact.source_range),
    },
    sourceResizeDrag: {
        artifactId: artifact.id,
        anchorCol: artifact.source_range.start_col,
        anchorRow: artifact.source_range.start_row,
    },
});

export const buildDerivedArtifactOverlaySelectionUpdate = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'id' | 'placement' | 'source_range'>,
    target: DerivedArtifactOverlaySelectionTarget,
): DerivedArtifactOverlaySelectionUpdate => {
    const interaction = buildDerivedArtifactOverlayInteractionProjection(artifact);
    const selection = target === 'source' ? interaction.sourceSelection : interaction.placementSelection;
    return {
        artifactId: artifact.id,
        placementCell: {
            col: artifact.placement.start_col,
            row: artifact.placement.start_row,
        },
        selection,
        selectedCell: {
            col: selection.col,
            row: selection.row,
        },
    };
};

export const buildDerivedArtifactOverlayDragStates = (
    interaction: DerivedArtifactOverlayInteractionProjection,
): DerivedArtifactOverlayDragStates => ({
    placementResize: { kind: 'placement-resize', drag: interaction.placementResizeDrag },
    placementMove: { kind: 'placement-move', drag: interaction.placementMoveDrag },
    sourceResize: { kind: 'source-resize', drag: interaction.sourceResizeDrag },
    sourceMove: { kind: 'source-move', drag: interaction.sourceMoveDrag },
});

export const buildDerivedArtifactPlacementResizePreviewSelection = (
    drag: DerivedArtifactOverlayInteractionProjection['placementResizeDrag'],
    target: DerivedArtifactOverlayCellCoordinate,
): DerivedArtifactSelectionProjection => ({
    col: drag.anchorCol,
    row: drag.anchorRow,
    endCol: Math.max(drag.anchorCol, target.col),
    endRow: Math.max(drag.anchorRow, target.row),
});

export const buildDerivedArtifactPlacementMovePreviewSelection = (
    drag: DerivedArtifactOverlayInteractionProjection['placementMoveDrag'],
    target: DerivedArtifactOverlayCellCoordinate,
): DerivedArtifactSelectionProjection => ({
    col: target.col,
    row: target.row,
    endCol: target.col + drag.width - 1,
    endRow: target.row + drag.height - 1,
});

export const buildDerivedArtifactSourceResizePreviewSelection = (
    drag: DerivedArtifactOverlayInteractionProjection['sourceResizeDrag'],
    target: DerivedArtifactOverlayCellCoordinate,
): DerivedArtifactSelectionProjection => ({
    col: drag.anchorCol,
    row: drag.anchorRow,
    endCol: Math.max(drag.anchorCol, target.col),
    endRow: Math.max(drag.anchorRow, target.row),
});

export const buildDerivedArtifactSourceMovePreviewSelection = (
    drag: DerivedArtifactOverlayInteractionProjection['sourceMoveDrag'],
    target: DerivedArtifactOverlayCellCoordinate,
): DerivedArtifactSelectionProjection => ({
    col: target.col,
    row: target.row,
    endCol: target.col + drag.width - 1,
    endRow: target.row + drag.height - 1,
});

export const buildDerivedArtifactOverlayDragPreviewUpdate = (
    dragState: DerivedArtifactOverlayDragState,
    target: DerivedArtifactOverlayCellCoordinate,
): DerivedArtifactOverlayDragPreviewUpdate => {
    switch (dragState.kind) {
        case 'placement-resize': {
            const selection = buildDerivedArtifactPlacementResizePreviewSelection(dragState.drag, target);
            return {
                selection,
                selectedCell: { col: dragState.drag.anchorCol, row: dragState.drag.anchorRow },
                commit: {
                    kind: 'placement-resize',
                    anchorCell: { col: dragState.drag.anchorCol, row: dragState.drag.anchorRow },
                    targetCell: { col: selection.endCol, row: selection.endRow },
                },
            };
        }
        case 'placement-move': {
            const selection = buildDerivedArtifactPlacementMovePreviewSelection(dragState.drag, target);
            return {
                selection,
                selectedCell: { col: selection.col, row: selection.row },
                commit: {
                    kind: 'placement-move',
                    originCell: { col: dragState.drag.originCol, row: dragState.drag.originRow },
                    targetCell: { col: selection.col, row: selection.row },
                },
            };
        }
        case 'source-resize': {
            const selection = buildDerivedArtifactSourceResizePreviewSelection(dragState.drag, target);
            return {
                selection,
                selectedCell: { col: dragState.drag.anchorCol, row: dragState.drag.anchorRow },
                commit: {
                    kind: 'source-resize',
                    artifactId: dragState.drag.artifactId,
                    targetCell: { col: selection.endCol, row: selection.endRow },
                },
            };
        }
        case 'source-move': {
            const selection = buildDerivedArtifactSourceMovePreviewSelection(dragState.drag, target);
            return {
                selection,
                selectedCell: { col: selection.col, row: selection.row },
                commit: {
                    kind: 'source-move',
                    artifactId: dragState.drag.artifactId,
                    targetCell: { col: selection.col, row: selection.row },
                },
            };
        }
    }
};

export const buildDerivedArtifactOverlaySelectionPresentation = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'placement' | 'source_range'>,
    selectedRange: SpreadsheetRangeAddress | null | undefined,
): DerivedArtifactOverlaySelectionPresentation => ({
    placementSelected: derivedArtifactRangesEqual(artifact.placement, selectedRange),
    sourceSelected: derivedArtifactRangesEqual(artifact.source_range, selectedRange),
});

export const buildDerivedArtifactOverlayRangeProjection = (
    artifact: Pick<SpreadsheetDerivedArtifact, 'placement' | 'source_range'>,
    activeSheetId: string,
    visibleBounds: DerivedArtifactOverlayVisibleBounds,
): DerivedArtifactOverlayRangeProjection => {
    const placementRange = derivedArtifactNormalizedRange(artifact.placement);
    const sourceRange = derivedArtifactNormalizedRange(artifact.source_range);
    return {
        placementRange,
        sourceRange,
        placementVisible: placementRange.sheet_id === activeSheetId
            && derivedArtifactRangeIntersectsVisibleBounds(placementRange, visibleBounds),
        sourceVisible: sourceRange.sheet_id === activeSheetId
            && derivedArtifactRangeIntersectsVisibleBounds(sourceRange, visibleBounds),
    };
};

export const buildActiveSheetDerivedArtifactMap = (
    artifacts: SpreadsheetDerivedArtifact[],
    activeSheetId: string,
): Record<string, SpreadsheetDerivedArtifact> => {
    const entries = artifacts
        .filter((artifact) => artifact.sheet_id === activeSheetId)
        .map((artifact) => [artifact.id, artifact] as const);
    return Object.fromEntries(entries);
};

export const clipDerivedArtifactOverlayScreenRect = (
    rect: DerivedArtifactOverlayScreenRect | null | undefined,
    viewport: DerivedArtifactOverlayViewportRect,
): DerivedArtifactOverlayScreenRect | null => {
    if (!rect) return null;
    const left = Math.max(0, rect.x);
    const top = Math.max(0, rect.y);
    const right = Math.min(viewport.width, rect.x + rect.w);
    const bottom = Math.min(viewport.height, rect.y + rect.h);
    if (right <= left || bottom <= top) return null;
    return {
        x: left,
        y: top,
        w: right - left,
        h: bottom - top,
    };
};

const derivedArtifactRangeKey = (range: SpreadsheetRangeAddress | null | undefined): string => {
    if (!range) return 'none';
    return `${range.sheet_id}:${range.start_col},${range.start_row}:${range.end_col},${range.end_row}`;
};

export const derivedArtifactPreviewSourceKey = (
    artifact: SpreadsheetDerivedArtifact,
    previewRange: Pick<DerivedArtifactPreviewRange, 'range'> = derivedArtifactPreviewRange(artifact),
): string => (
    `${derivedArtifactRangeKey(artifact.source_range)}|preview:${derivedArtifactRangeKey(previewRange.range)}`
);

export const buildDerivedArtifactPreviewFetchRequests = (
    artifacts: SpreadsheetDerivedArtifact[],
): DerivedArtifactPreviewFetchRequest[] => (
    artifacts.map((artifact) => {
        const previewRange = derivedArtifactPreviewRange(artifact);
        return {
            artifact,
            artifactId: artifact.id,
            previewRange,
            sourceKey: derivedArtifactPreviewSourceKey(artifact, previewRange),
        };
    })
);

export const buildDerivedArtifactPreviewFetchPlan = (
    artifacts: SpreadsheetDerivedArtifact[],
): DerivedArtifactPreviewFetchPlan => {
    const requests = buildDerivedArtifactPreviewFetchRequests(artifacts);
    return {
        requests,
        rangeReads: requests.map(({ artifactId, sourceKey, previewRange }) => ({
            artifactId,
            sourceKey,
            sheetId: previewRange.range.sheet_id,
            startCol: previewRange.range.start_col,
            startRow: previewRange.range.start_row,
            endCol: previewRange.range.end_col,
            endRow: previewRange.range.end_row,
            mode: 'computed',
        })),
        shouldClearCache: requests.length === 0,
    };
};

export const reconcileDerivedArtifactPreviewCacheStates = (
    current: Record<string, DerivedArtifactOverlayPreviewCacheState>,
    requests: DerivedArtifactPreviewFetchRequest[],
): Record<string, DerivedArtifactOverlayPreviewCacheState> => {
    const next: Record<string, DerivedArtifactOverlayPreviewCacheState> = {};
    for (const request of requests) {
        const existing = current[request.artifactId];
        next[request.artifactId] = existing?.sourceKey === request.sourceKey
            ? existing
            : { sourceKey: request.sourceKey, loading: true, error: null, cells: null };
    }
    return next;
};

export const applyDerivedArtifactPreviewFetchSuccess = (
    current: Record<string, DerivedArtifactOverlayPreviewCacheState>,
    artifactId: string,
    sourceKey: string,
    cells: SpreadsheetRangeCell[][],
): Record<string, DerivedArtifactOverlayPreviewCacheState> => {
    const existing = current[artifactId];
    if (!existing || existing.sourceKey !== sourceKey) return current;
    return {
        ...current,
        [artifactId]: { sourceKey, loading: false, error: null, cells },
    };
};

export const applyDerivedArtifactPreviewFetchError = (
    current: Record<string, DerivedArtifactOverlayPreviewCacheState>,
    artifactId: string,
    sourceKey: string,
    error: unknown,
): Record<string, DerivedArtifactOverlayPreviewCacheState> => {
    const existing = current[artifactId];
    if (!existing || existing.sourceKey !== sourceKey) return current;
    return {
        ...current,
        [artifactId]: {
            sourceKey,
            loading: false,
            error: error instanceof Error ? error.message : String(error),
            cells: null,
        },
    };
};

export const buildDerivedArtifactOverlayProjection = (
    artifact: SpreadsheetDerivedArtifact,
    rangeProjection: DerivedArtifactOverlayRangeProjection,
    placementRect: DerivedArtifactOverlayScreenRect | null | undefined,
    sourceRect: DerivedArtifactOverlayScreenRect | null | undefined,
    viewport: DerivedArtifactOverlayViewportRect,
    selectedRange: SpreadsheetRangeAddress | null | undefined,
    previewState: DerivedArtifactOverlayPreviewCacheState | null | undefined,
): DerivedArtifactOverlayProjection | null => {
    if (!rangeProjection.placementVisible) return null;
    const clippedRect = clipDerivedArtifactOverlayScreenRect(placementRect, viewport);
    if (!clippedRect) return null;
    const clippedSourceRect = rangeProjection.sourceVisible
        ? clipDerivedArtifactOverlayScreenRect(sourceRect, viewport)
        : null;
    const previewPlan = derivedArtifactPreviewRange(artifact);
    const sourceKey = derivedArtifactPreviewSourceKey(artifact, previewPlan);
    const preview = previewState?.sourceKey === sourceKey && previewState.cells
        ? buildDerivedArtifactPreviewModel(artifact, previewState.cells)
        : null;
    const selection = buildDerivedArtifactOverlaySelectionPresentation(artifact, selectedRange);
    const interaction = buildDerivedArtifactOverlayInteractionProjection(artifact);
    return {
        id: artifact.id,
        kind: artifact.kind,
        title: String(artifact.title || (artifact.kind === 'pivot' ? 'PivotTable' : 'Chart')),
        chartType: artifact.chart_type ?? undefined,
        range: rangeProjection.placementRange,
        sourceRange: rangeProjection.sourceRange,
        sourceAddress: previewPlan.sourceAddress,
        placementSelected: selection.placementSelected,
        sourceSelected: selection.sourceSelected,
        preview,
        previewFootnote: preview ? describeDerivedArtifactPreviewFootnote(previewPlan, preview) : '',
        chartSettings: artifact.kind === 'chart' ? buildExcelRangeChartSettings({ artifact }) : null,
        interaction,
        placementSelectionUpdate: buildDerivedArtifactOverlaySelectionUpdate(artifact, 'placement'),
        sourceSelectionUpdate: buildDerivedArtifactOverlaySelectionUpdate(artifact, 'source'),
        dragStates: buildDerivedArtifactOverlayDragStates(interaction),
        previewLoading: previewState?.sourceKey === sourceKey ? previewState.loading : true,
        previewError: previewState?.sourceKey === sourceKey ? previewState.error : null,
        sourceRect: clippedSourceRect,
        rect: clippedRect,
    };
};

export const buildDerivedArtifactOverlayProjections = ({
    artifacts,
    activeSheetId,
    visibleBounds,
    viewport,
    selectedRange,
    previewStates,
    getRangeScreenRect,
}: DerivedArtifactOverlayCollectionProjectionRequest): DerivedArtifactOverlayProjection[] => (
    artifacts
        .map((artifact) => {
            const rangeProjection = buildDerivedArtifactOverlayRangeProjection(artifact, activeSheetId, visibleBounds);
            if (!rangeProjection.placementVisible) return null;
            const placementRect = getRangeScreenRect(rangeProjection.placementRange);
            const sourceRect = rangeProjection.sourceVisible
                ? getRangeScreenRect(rangeProjection.sourceRange)
                : null;
            return buildDerivedArtifactOverlayProjection(
                artifact,
                rangeProjection,
                placementRect,
                sourceRect,
                viewport,
                selectedRange,
                previewStates[artifact.id] ?? null,
            );
        })
        .filter((entry): entry is DerivedArtifactOverlayProjection => Boolean(entry))
);

export const buildDerivedArtifactOverlayRenderPresentation = (
    overlay: Pick<DerivedArtifactOverlayProjection,
        'kind' | 'chartType' | 'title' | 'range' | 'sourceRange' | 'sourceAddress' | 'preview' | 'previewLoading' | 'previewError' | 'chartSettings'>,
): DerivedArtifactOverlayRenderPresentation => {
    const chrome = buildDerivedArtifactOverlayChromePresentation({
        kind: overlay.kind,
        chart_type: overlay.chartType,
        title: overlay.title,
        placement: overlay.range,
    }, overlay.sourceAddress);
    const chartPreview = overlay.preview?.kind === 'chart' ? overlay.preview : null;
    const pivotPreview = overlay.preview?.kind === 'pivot' ? overlay.preview : null;
    const chartPresentation = chartPreview ? buildDerivedArtifactChartPreviewPresentation(chartPreview, overlay.sourceAddress, overlay.chartSettings) : null;
    const pivotPresentation = pivotPreview ? buildDerivedArtifactPivotPreviewPresentation(pivotPreview, overlay.sourceAddress) : null;
    return {
        chrome,
        theme: chrome.theme,
        chartPreview,
        pivotPreview,
        chartPresentation,
        pivotPresentation,
        chartSeriesSummaryItems: chartPresentation?.seriesSummaryItems ?? [],
        chartAggregatePlot: chartPresentation?.aggregatePlot ?? null,
        chartSeriesPlotItems: chartPresentation?.seriesPlotItems ?? [],
        chartCategoryBarGroups: chartPresentation?.categoryBarGroups ?? [],
        chartAxisGuideLines: chartPresentation?.axisGuideLines ?? [],
        chartAxisTickMarks: chartPresentation?.axisTickMarks ?? [],
        chartAxisTickLabels: chartPresentation?.axisTickLabels ?? [],
        chartDataLabelItems: chartPresentation?.dataLabelItems ?? [],
        chartPieGradient: chartPresentation?.pieGradient ?? '',
        chartPieHoleSizePct: chartPresentation?.pieHoleSizePct ?? 0,
        chartPieSliceItems: chartPresentation?.pieSliceItems ?? [],
        chartPieLegendItems: chartPresentation?.pieLegendItems ?? [],
        chartSettings: overlay.chartSettings,
        chartQuickToolbar: buildDerivedArtifactChartQuickToolbarPresentation({
            kind: overlay.kind,
            chart_type: overlay.chartType,
            title: overlay.title,
            metadata: overlay.chartSettings ? { excel_range_chart: overlay.chartSettings } : null,
            source_range: overlay.sourceRange,
        }),
        pivotMeasureSummaryItems: pivotPresentation?.measureSummaryItems ?? [],
        chartRenderKind: chartPresentation?.renderKind ?? 'bar',
        previewStatus: buildDerivedArtifactPreviewStatusPresentation({
            artifact: {
                kind: overlay.kind,
                chart_type: overlay.chartType,
            },
            sourceAddress: overlay.sourceAddress,
            loading: overlay.previewLoading,
            error: overlay.previewError,
            chartPresentation,
            pivotPresentation,
        }),
    };
};

const joinPreviewFootnoteParts = (parts: string[]): string => {
    if (parts.length <= 1) return parts[0] ?? '';
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};

const cellAddress = (cell: SpreadsheetRangeCell): string => `${colToLetter(cell.col)}${cell.row + 1}`;

const cellDisplayLabel = (cell: SpreadsheetRangeCell): string => {
    const displayValue = String(cell.display_value ?? '').trim();
    if (displayValue) return displayValue;
    const rawInput = String(cell.raw_input ?? '').trim();
    if (rawInput && !rawInput.startsWith('=')) return rawInput;
    const value = cell.value;
    switch (value.kind) {
        case 'number':
            return Number.isInteger(value.value) ? String(Math.trunc(value.value)) : String(value.value);
        case 'text':
            return value.value;
        case 'boolean':
            return value.value ? 'TRUE' : 'FALSE';
        case 'datetime':
            return new Date(value.epoch_ms).toISOString().slice(0, 10);
        case 'error':
            return value.code;
        case 'pending_external':
            return 'Pending';
        case 'dynamic_array':
            return 'Array';
        case 'blank':
        default:
            return '';
    }
};

const cellNumber = (value: SpreadsheetCellValue): number | null => {
    if (value.kind === 'number' && Number.isFinite(value.value)) return value.value;
    if (value.kind === 'boolean') return value.value ? 1 : 0;
    return null;
};

const cellDateAxisValue = (cell: SpreadsheetRangeCell | undefined): number | null => {
    if (!cell) return null;
    if (cell.value.kind === 'datetime' && Number.isFinite(cell.value.epoch_ms)) return cell.value.epoch_ms;
    if (cell.value.kind === 'number' && Number.isFinite(cell.value.value)) return cell.value.value;
    const text = String(cell.display_value || cell.raw_input || '').trim();
    if (!/^\d{4}-\d{1,2}-\d{1,2}(?:[T\s].*)?$/.test(text)) return null;
    const parsed = Date.parse(text);
    return Number.isFinite(parsed) ? parsed : null;
};

const chartPreviewVisual = (chartType: SpreadsheetChartArtifactType | null | undefined): DerivedArtifactChartPreviewVisual => {
    switch (chartType) {
        case 'line':
            return 'line';
        case 'area':
            return 'area';
        case 'pie':
            return 'pie';
        case 'scatter':
            return 'scatter';
        case 'bar':
        case 'column':
        default:
            return 'bar';
    }
};

const buildChartPlotPointsWithScale = (
    points: DerivedArtifactChartPreviewPoint[],
    scale: DerivedArtifactChartPlotScale,
): DerivedArtifactChartPlotPoint[] => {
    const valueRange = scale.maxValue - scale.minValue;
    const hasSourceXScale = typeof scale.minXValue === 'number' && typeof scale.maxXValue === 'number'
        && points.every((point) => typeof point.xValue === 'number' && Number.isFinite(point.xValue));
    const xValueRange = hasSourceXScale ? (scale.maxXValue ?? 0) - (scale.minXValue ?? 0) : 0;
    const ordinalCount = Math.max(1, scale.ordinalCount ?? points.length);
    const totalAbs = scale.totalAbsValue;
    const lastOrdinalIndex = Math.max(1, ordinalCount - 1);
    let shareOffsetPct = 0;
    const valuePct = (value: number): number => {
        if (valueRange === 0) return 0.5;
        if (typeof scale.valueAxisLogBase === 'number') {
            if (value <= 0) return 0;
            const clampedValue = Math.max(scale.minValue, Math.min(scale.maxValue, value));
            const logBase = Math.log(scale.valueAxisLogBase);
            const minLog = Math.log(scale.minValue) / logBase;
            const maxLog = Math.log(scale.maxValue) / logBase;
            const valueLog = Math.log(clampedValue) / logBase;
            const logRange = maxLog - minLog;
            return logRange === 0 ? 0.5 : (valueLog - minLog) / logRange;
        }
        return (value - scale.minValue) / valueRange;
    };
    return points.map((point, index) => {
        const barGeometry = buildChartBarGeometry(point.value, scale);
        const pieGeometry = chartPieSegmentBounds(
            point.value,
            index,
            points.length,
            totalAbs,
            shareOffsetPct,
            points.length >= scale.valueCount,
        );
        shareOffsetPct = pieGeometry.nextShareOffsetPct;
        return {
            ...point,
            xPct: hasSourceXScale
                ? (xValueRange === 0 ? 50 : Math.round((((point.xValue ?? scale.minXValue ?? 0) - (scale.minXValue ?? 0)) / xValueRange) * 100))
                : (ordinalCount <= 1 ? 50 : Math.round((index / lastOrdinalIndex) * 100)),
            yPct: valueRange === 0
                ? 50
                : Math.max(0, Math.min(100, Math.round(100 - (valuePct(point.value) * 100)))),
            magnitudePct: Math.max(4, Math.round((Math.abs(point.value) / scale.maxAbsValue) * 100)),
            ...barGeometry,
            sharePct: pieGeometry.sharePct,
            shareStartPct: pieGeometry.shareStartPct,
            shareEndPct: pieGeometry.shareEndPct,
        };
    });
};

export const derivedArtifactSourceAddress = (artifact: SpreadsheetDerivedArtifact): string => {
    const range = artifact.source_range;
    const address = rangeAddress(range);
    return range.sheet_id !== artifact.sheet_id ? `${range.sheet_id}!${address}` : address;
};

function rangeAddress(range: SpreadsheetRangeAddress): string {
    const startRow = Math.min(range.start_row, range.end_row);
    const endRow = Math.max(range.start_row, range.end_row);
    const startCol = Math.min(range.start_col, range.end_col);
    const endCol = Math.max(range.start_col, range.end_col);
    const start = `${colToLetter(startCol)}${startRow + 1}`;
    const end = `${colToLetter(endCol)}${endRow + 1}`;
    return start === end ? start : `${start}:${end}`;
}

export const derivedArtifactPreviewRange = (
    artifact: SpreadsheetDerivedArtifact,
    options: { maxRows?: number; maxCols?: number } = {},
): DerivedArtifactPreviewRange => {
    const source = artifact.source_range;
    const startRow = Math.min(source.start_row, source.end_row);
    const endRow = Math.max(source.start_row, source.end_row);
    const startCol = Math.min(source.start_col, source.end_col);
    const endCol = Math.max(source.start_col, source.end_col);
    const maxRows = Math.max(1, Math.floor(options.maxRows ?? DERIVED_ARTIFACT_PREVIEW_MAX_ROWS));
    const maxCols = Math.max(1, Math.floor(options.maxCols ?? DERIVED_ARTIFACT_PREVIEW_MAX_COLS));
    const previewEndRow = Math.min(endRow, startRow + maxRows - 1);
    const previewEndCol = Math.min(endCol, startCol + maxCols - 1);
    const range: SpreadsheetRangeAddress = {
        sheet_id: source.sheet_id,
        start_row: startRow,
        start_col: startCol,
        end_row: previewEndRow,
        end_col: previewEndCol,
    };
    return {
        range,
        sourceAddress: derivedArtifactSourceAddress({
            ...artifact,
            source_range: { sheet_id: source.sheet_id, start_row: startRow, start_col: startCol, end_row: endRow, end_col: endCol },
        }),
        previewAddress: range.sheet_id !== artifact.sheet_id ? `${range.sheet_id}!${rangeAddress(range)}` : rangeAddress(range),
        sourceRowCount: endRow - startRow + 1,
        sourceColCount: endCol - startCol + 1,
        previewRowCount: previewEndRow - startRow + 1,
        previewColCount: previewEndCol - startCol + 1,
        truncatedRows: previewEndRow < endRow,
        truncatedCols: previewEndCol < endCol,
    };
};

const buildChartPoints = (
    cells: SpreadsheetRangeCell[][],
    maxPoints: number,
    visualKind: DerivedArtifactChartPreviewVisual,
    chartType: SpreadsheetChartArtifactType,
    axisScale?: DerivedArtifactValueAxisScale | null,
): {
    points: DerivedArtifactChartPreviewPoint[];
    series: DerivedArtifactChartPreviewSeries[];
    totalNumericCells: number;
    totalPointCount: number;
    pointScale: DerivedArtifactChartPlotScale;
    seriesScale: DerivedArtifactChartPlotScale;
} => {
    const points: DerivedArtifactChartPreviewPoint[] = [];
    const allPointValues: number[] = [];
    const allPointXValues: number[] = [];
    const allSeriesValues: number[] = [];
    const allSeriesXValues: number[] = [];
    let totalPointCount = 0;
    let totalNumericCells = 0;

    const rowNumericCells = (row: SpreadsheetRangeCell[]) => row
        .map((cell) => ({ cell, value: cellNumber(cell.value) }))
        .filter((entry): entry is { cell: SpreadsheetRangeCell; value: number } => entry.value !== null);

    const firstRowLooksLikeSeriesHeader = (() => {
        const firstRow = cells[0] ?? [];
        if (firstRow.length < 2) return false;
        const textLabelCount = firstRow
            .filter((cell) => cellNumber(cell.value) === null && cellDisplayLabel(cell))
            .length;
        if (textLabelCount === 0) return false;
        const numericHeaderCandidateCount = rowNumericCells(firstRow).length;
        if (textLabelCount < 2 && numericHeaderCandidateCount < 2) return false;
        return cells.slice(1).some((row) => rowNumericCells(row).length > 0);
    })();

    const headerRow = firstRowLooksLikeSeriesHeader ? cells[0] ?? [] : [];
    const chartRows = firstRowLooksLikeSeriesHeader ? cells.slice(1) : cells;
    const sourceWidth = cells.reduce((max, row) => Math.max(max, row.length), 0);
    const firstSourceColumnIndex = Array.from({ length: sourceWidth }, (_, colIndex) => colIndex)
        .find((colIndex) => cells.some((row) => row[colIndex]));
    const valueColumns = Array.from({ length: sourceWidth }, (_, colIndex) => colIndex)
        .filter((colIndex) => chartRows.some((row) => row[colIndex] && cellNumber(row[colIndex].value) !== null));
    const valueColumnSet = new Set(valueColumns);
    const scatterXColumn = visualKind === 'scatter' && valueColumns.length >= 2 ? valueColumns[0] : null;
    const numericCategoryColumn = scatterXColumn === null
        && firstRowLooksLikeSeriesHeader
        && valueColumns.length >= 2
        && valueColumns[0] === firstSourceColumnIndex
        && headerRow[valueColumns[0]]
        && cellNumber(headerRow[valueColumns[0]].value) === null
        ? valueColumns[0]
        : null;
    const dateCategoryColumn = scatterXColumn === null
        && numericCategoryColumn === null
        && axisScale?.categoryAxisKind === 'date'
        && visualKind !== 'pie'
        && firstSourceColumnIndex != null
        && !valueColumnSet.has(firstSourceColumnIndex)
        && chartRows.some((row) => cellDateAxisValue(row[firstSourceColumnIndex]) !== null)
        ? firstSourceColumnIndex
        : null;
    const seriesValueColumns = scatterXColumn !== null || numericCategoryColumn !== null ? valueColumns.slice(1) : valueColumns;
    const seriesValueColumnSet = new Set(seriesValueColumns);
    const columnSeries = new Map<number, DerivedArtifactChartPreviewSeries>();
    const columnSeriesValueCounts = new Map<number, number>();

    for (const colIndex of seriesValueColumns) {
        const headerLabel = headerRow[colIndex] ? cellDisplayLabel(headerRow[colIndex]) : '';
        const sampleCell = chartRows.find((row) => row[colIndex])?.[colIndex];
        columnSeriesValueCounts.set(colIndex, 0);
        columnSeries.set(colIndex, {
            name: headerLabel || (sampleCell ? colToLetter(sampleCell.col) : `Series ${colIndex + 1}`),
            total: 0,
            valueCount: 0,
            componentChartType: chartType,
            valueAxis: 'primary',
            points: [],
            plotPoints: [],
        });
    }

    for (const row of chartRows) {
        const numericCells = valueColumns
            .map((colIndex) => {
                const cell = row[colIndex];
                if (!cell) return null;
                const value = cellNumber(cell.value);
                return value === null ? null : { colIndex, cell, value };
            })
            .filter((entry): entry is { colIndex: number; cell: SpreadsheetRangeCell; value: number } => entry !== null);
        if (numericCells.length === 0) continue;
        totalNumericCells += numericCells.length;
        const shouldSamplePoint = points.length < maxPoints;
        const labelCell = shouldSamplePoint
            ? row.find((cell, colIndex) => !valueColumnSet.has(colIndex) && cellNumber(cell.value) === null && cellDisplayLabel(cell))
            : undefined;
        if (scatterXColumn !== null) {
            const xEntry = numericCells.find((entry) => entry.colIndex === scatterXColumn);
            const yEntries = numericCells.filter((entry) => seriesValueColumnSet.has(entry.colIndex));
            if (!xEntry || yEntries.length === 0) continue;
            const label = labelCell ? cellDisplayLabel(labelCell) : (cellDisplayLabel(xEntry.cell) || cellAddress(xEntry.cell));
            const aggregateValue = yEntries.reduce((sum, entry) => sum + entry.value, 0);
            totalPointCount += 1;
            allPointValues.push(aggregateValue);
            allPointXValues.push(xEntry.value);
            if (shouldSamplePoint) {
                points.push({
                    label,
                    value: aggregateValue,
                    xValue: xEntry.value,
                });
            }
            for (const entry of yEntries) {
                const column = columnSeries.get(entry.colIndex);
                if (!column) continue;
                column.total += entry.value;
                allSeriesValues.push(entry.value);
                allSeriesXValues.push(xEntry.value);
                columnSeriesValueCounts.set(entry.colIndex, (columnSeriesValueCounts.get(entry.colIndex) ?? 0) + 1);
                if (shouldSamplePoint) {
                    column.points.push({ label, value: entry.value, xValue: xEntry.value });
                }
            }
            continue;
        }
        if (numericCategoryColumn !== null) {
            const categoryEntry = numericCells.find((entry) => entry.colIndex === numericCategoryColumn);
            const yEntries = numericCells.filter((entry) => seriesValueColumnSet.has(entry.colIndex));
            if (!categoryEntry || yEntries.length === 0) continue;
            const label = labelCell ? cellDisplayLabel(labelCell) : (cellDisplayLabel(categoryEntry.cell) || cellAddress(categoryEntry.cell));
            const aggregateValue = yEntries.reduce((sum, entry) => sum + entry.value, 0);
            totalPointCount += 1;
            allPointValues.push(aggregateValue);
            if (shouldSamplePoint) {
                points.push({
                    label,
                    value: aggregateValue,
                });
            }
            for (const entry of yEntries) {
                const column = columnSeries.get(entry.colIndex);
                if (!column) continue;
                column.total += entry.value;
                allSeriesValues.push(entry.value);
                columnSeriesValueCounts.set(entry.colIndex, (columnSeriesValueCounts.get(entry.colIndex) ?? 0) + 1);
                if (shouldSamplePoint) {
                    column.points.push({ label, value: entry.value });
                }
            }
            continue;
        }
        if (dateCategoryColumn !== null) {
            const categoryCell = row[dateCategoryColumn];
            const categoryXValue = cellDateAxisValue(categoryCell);
            const yEntries = numericCells.filter((entry) => seriesValueColumnSet.has(entry.colIndex));
            if (categoryXValue === null || yEntries.length === 0) continue;
            const label = categoryCell ? (cellDisplayLabel(categoryCell) || cellAddress(categoryCell)) : cellAddress(yEntries[0]!.cell);
            const aggregateValue = yEntries.reduce((sum, entry) => sum + entry.value, 0);
            totalPointCount += 1;
            allPointValues.push(aggregateValue);
            allPointXValues.push(categoryXValue);
            if (shouldSamplePoint) {
                points.push({
                    label,
                    value: aggregateValue,
                    xValue: categoryXValue,
                });
            }
            for (const entry of yEntries) {
                const column = columnSeries.get(entry.colIndex);
                if (!column) continue;
                column.total += entry.value;
                allSeriesValues.push(entry.value);
                allSeriesXValues.push(categoryXValue);
                columnSeriesValueCounts.set(entry.colIndex, (columnSeriesValueCounts.get(entry.colIndex) ?? 0) + 1);
                if (shouldSamplePoint) {
                    column.points.push({ label, value: entry.value, xValue: categoryXValue });
                }
            }
            continue;
        }
        const label = labelCell ? cellDisplayLabel(labelCell) : cellAddress(numericCells[0].cell);
        const aggregateValue = numericCells.reduce((sum, entry) => sum + entry.value, 0);
        totalPointCount += 1;
        allPointValues.push(aggregateValue);
        if (shouldSamplePoint) {
            points.push({
                label,
                value: aggregateValue,
            });
        }
        for (const entry of numericCells) {
            const column = columnSeries.get(entry.colIndex);
            if (!column) continue;
            column.total += entry.value;
            allSeriesValues.push(entry.value);
            columnSeriesValueCounts.set(entry.colIndex, (columnSeriesValueCounts.get(entry.colIndex) ?? 0) + 1);
            if (shouldSamplePoint) {
                column.points.push({ label, value: entry.value });
            }
        }
    }

    if (points.length > 0) {
        const seriesEntries = Array.from(columnSeries.entries())
            .filter(([colIndex]) => (columnSeriesValueCounts.get(colIndex) ?? 0) > 0);
        return {
            points,
            series: seriesEntries
                .map(([colIndex, entry], seriesIndex) => ({
                    ...entry,
                    componentChartType: derivedArtifactSeriesComponentChartType(axisScale, seriesIndex, chartType),
                    valueCount: columnSeriesValueCounts.get(colIndex) ?? 0,
                    valueAxis: derivedArtifactSeriesValueAxis(axisScale, seriesIndex, seriesEntries.length),
                })),
            totalNumericCells,
            totalPointCount,
            pointScale: buildChartPlotScale(allPointValues, allPointXValues, totalPointCount, axisScale),
            seriesScale: buildChartPlotScale(allSeriesValues, allSeriesXValues, totalPointCount, axisScale),
        };
    }

    const fallbackSeries: DerivedArtifactChartPreviewSeries = { name: 'Values', total: 0, valueCount: 0, componentChartType: chartType, valueAxis: 'primary', points, plotPoints: [] };
    for (const row of cells) {
        for (const cell of row) {
            const value = cellNumber(cell.value);
            if (value === null) continue;
            totalNumericCells += 1;
            fallbackSeries.valueCount += 1;
            const point = { label: cellAddress(cell), value };
            fallbackSeries.total += value;
            totalPointCount += 1;
            allPointValues.push(value);
            allSeriesValues.push(value);
            if (points.length < maxPoints) {
                points.push(point);
                fallbackSeries.points.push(point);
            }
        }
    }

    return {
        points,
        series: fallbackSeries.points.length > 0 ? [fallbackSeries] : [],
        totalNumericCells,
        totalPointCount,
        pointScale: buildChartPlotScale(allPointValues, [], totalPointCount, axisScale),
        seriesScale: buildChartPlotScale(allSeriesValues, [], totalPointCount, axisScale),
    };
};

const buildPivotPreview = (
    cells: SpreadsheetRangeCell[][],
    maxRows: number,
    maxCols: number,
    maxMeasures: number,
) => {
    const width = Math.max(0, Math.min(maxCols, cells.reduce((max, row) => Math.max(max, row.length), 0)));
    const firstRow = cells[0] ?? [];
    const firstRowTextLabelCount = firstRow
        .filter((cell) => cellNumber(cell.value) === null && cellDisplayLabel(cell))
        .length;
    const firstRowNumericCount = firstRow
        .filter((cell) => cellNumber(cell.value) !== null)
        .length;
    const firstRowLooksLikeHeader = firstRowTextLabelCount >= 2 || (firstRowTextLabelCount >= 1 && firstRowNumericCount >= 2);
    const headers = Array.from({ length: width }, (_, index) => {
        const label = firstRowLooksLikeHeader && firstRow[index] ? cellDisplayLabel(firstRow[index]) : '';
        return label || `Column ${index + 1}`;
    });
    const dataRows = firstRowLooksLikeHeader ? cells.slice(1) : cells;
    const rows = dataRows.slice(0, maxRows).map((row) => (
        Array.from({ length: width }, (_, index) => {
            const label = row[index] ? cellDisplayLabel(row[index]) : '';
            return label || '';
        })
    ));
    const measureSummaries: DerivedArtifactPivotMeasureSummary[] = [];
    const sourceWidth = cells.reduce((max, row) => Math.max(max, row.length), 0);
    for (let colIndex = 0; colIndex < sourceWidth; colIndex += 1) {
        const values = dataRows
            .map((row) => row[colIndex] ? cellNumber(row[colIndex].value) : null)
            .filter((value): value is number => value !== null);
        if (values.length === 0) continue;
        const header = firstRow[colIndex] ? cellDisplayLabel(firstRow[colIndex]) : '';
        const total = values.reduce((sum, value) => sum + value, 0);
        measureSummaries.push({
            header: firstRowLooksLikeHeader && header ? header : `Column ${colIndex + 1}`,
            total,
            count: values.length,
            average: total / values.length,
        });
    }
    return { headers, rows, dataRowCount: dataRows.length, measureSummaries: measureSummaries.slice(0, maxMeasures), totalMeasureCount: measureSummaries.length };
};

export const buildDerivedArtifactPreviewModel = (
    artifact: SpreadsheetDerivedArtifact,
    cells: SpreadsheetRangeCell[][],
    options: { maxPoints?: number; maxPivotRows?: number; maxPivotCols?: number; maxPivotMeasures?: number } = {},
): DerivedArtifactPreviewModel => {
    const sourceAddress = derivedArtifactSourceAddress(artifact);
    if (artifact.kind === 'pivot') {
        const { headers, rows, dataRowCount, measureSummaries, totalMeasureCount } = buildPivotPreview(
            cells,
            Math.max(1, options.maxPivotRows ?? DERIVED_ARTIFACT_PREVIEW_MAX_PIVOT_ROWS),
            Math.max(1, options.maxPivotCols ?? DERIVED_ARTIFACT_PREVIEW_MAX_PIVOT_COLS),
            Math.max(1, options.maxPivotMeasures ?? DERIVED_ARTIFACT_PREVIEW_MAX_PIVOT_MEASURES),
        );
        return {
            kind: 'pivot',
            sourceAddress,
            headers,
            rows,
            dataRowCount,
            measureSummaries,
            totalMeasureCount,
            sourceRowCount: cells.length,
            sourceColCount: cells.reduce((max, row) => Math.max(max, row.length), 0),
        };
    }

    const visualKind = chartPreviewVisual(artifact.chart_type);
    const chartSettings = buildExcelRangeChartSettings({ artifact });
    const { points, series: rawSeries, totalNumericCells, totalPointCount, pointScale, seriesScale } = buildChartPoints(
        cells,
        Math.max(1, options.maxPoints ?? DERIVED_ARTIFACT_PREVIEW_MAX_CHART_POINTS),
        visualKind,
        artifact.chart_type ?? 'column',
        chartSettings,
    );
    const plotPoints = buildChartPlotPointsWithScale(points, pointScale);
    const secondaryRawValues = rawSeries
        .filter((entry) => entry.valueAxis === 'secondary')
        .flatMap((entry) => entry.points.map((point) => point.value));
    const secondaryRawXValues = rawSeries
        .filter((entry) => entry.valueAxis === 'secondary')
        .flatMap((entry) => entry.points.map((point) => point.xValue).filter((value): value is number => typeof value === 'number' && Number.isFinite(value)));
    const secondarySeriesScale = secondaryRawValues.length > 0
        ? buildChartPlotScale(secondaryRawValues, secondaryRawXValues, totalPointCount, secondaryValueAxisScaleFromSettings(chartSettings))
        : null;
    const series = rawSeries.map((entry) => ({
        ...entry,
        plotPoints: buildChartPlotPointsWithScale(entry.points, entry.valueAxis === 'secondary' && secondarySeriesScale ? secondarySeriesScale : seriesScale),
    }));
    return {
        kind: 'chart',
        chartType: artifact.chart_type ?? 'column',
        visualKind,
        sourceAddress,
        points,
        totalPointCount,
        series,
        plotPoints,
        maxAbsValue: pointScale.maxAbsValue,
        minValue: pointScale.minValue,
        maxValue: pointScale.maxValue,
        secondaryMinValue: secondarySeriesScale?.minValue ?? null,
        secondaryMaxValue: secondarySeriesScale?.maxValue ?? null,
        totalNumericCells,
    };
};
